"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { useTheme } from "next-themes"
import { translations, Language } from "@/lib/translations"
import { toast } from "@/lib/toast"

// Domain & UI Types
import { Workspace, Account, App, InstalledApp, UserProfile, TabGroup, OpenAppTab, getOldestActiveWorkspace } from "@/types/workspace"
import { NotificationItem } from "@/types/notification"

// Extracted UI Containers
import { DesktopTopBar } from "@/components/desk/DesktopTopBar"
import { DesktopAppGrid } from "@/components/desk/DesktopAppGrid"
import { DesktopChromeTabBar } from "@/components/desk/DesktopChromeTabBar"
import { AppViewportContainer } from "@/components/desk/AppViewportContainer"
import { WorkspaceOverlays } from "@/components/desk/WorkspaceOverlays"
import { FullscreenPromptModal } from "@/components/desk/FullscreenPromptModal"

// Custom Hooks for State & Logic
import { useDashboardHandlers } from "@/hooks/useDashboardHandlers"
import { useCalculatorState } from "@/hooks/useCalculatorState"
import { useHomeScreenState } from "@/hooks/useHomeScreenState"
import { useSecurityHandlers } from "@/hooks/useSecurityHandlers"
import {
  useAppRouterSync,
  resolveIframeUrl,
  getAppSlug,
  isAppMatch,
  getWorkspaceBasePath,
  buildWorkspaceDesktopPath,
  buildWorkspaceAppPath,
  parseWorkspaceRouteFromPathname,
} from "@/hooks/useAppRouterSync"

function extractSubpathFromMessage(data: any): string | null {
  if (!data) return null
  let obj = data
  if (typeof data === "string") {
    const trimmed = data.trim()
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        obj = JSON.parse(trimmed)
      } catch (e) {
        return null
      }
    } else if (trimmed.startsWith("/") || trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed
    } else {
      return null
    }
  }

  if (typeof obj !== "object" || obj === null) return null

  if (typeof obj.payload === "string" && (obj.payload.startsWith("/") || obj.payload.includes("://"))) {
    return obj.payload
  }
  if (obj.payload && typeof obj.payload === "object") {
    const p = obj.payload.path || obj.payload.route || obj.payload.url || obj.payload.pathname
    if (typeof p === "string") return p
  }

  const direct = obj.path || obj.pathname || obj.route || obj.url || obj.subpath || obj.href
  if (typeof direct === "string") return direct

  return null
}

const getAuthToken = (): string | undefined => {
  if (typeof window === "undefined") return undefined
  try {
    const cookieToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (cookieToken) return cookieToken
    return localStorage.getItem("token") || localStorage.getItem("bold_token") || localStorage.getItem("auth_token") || undefined
  } catch (e) {
    return undefined
  }
}

const COLOR_MAP: Record<string, { border: string; bg: string; text: string; rawHex: string }> = {
  blue: { border: "border-blue-500", bg: "bg-blue-100/50 dark:bg-blue-900/20", text: "text-blue-600 dark:text-blue-400", rawHex: "#3b82f6" },
  red: { border: "border-red-500", bg: "bg-red-100/50 dark:bg-red-900/20", text: "text-red-600 dark:text-red-400", rawHex: "#ef4444" },
  green: { border: "border-emerald-500", bg: "bg-emerald-100/50 dark:bg-emerald-900/20", text: "text-emerald-600 dark:text-emerald-400", rawHex: "#10b981" },
  yellow: { border: "border-yellow-500", bg: "bg-yellow-100/50 dark:bg-yellow-900/20", text: "text-yellow-600 dark:text-yellow-400", rawHex: "#eab308" },
  purple: { border: "border-purple-500", bg: "bg-purple-100/50 dark:bg-purple-900/20", text: "text-purple-600 dark:text-purple-400", rawHex: "#a855f7" },
  orange: { border: "border-orange-500", bg: "bg-orange-100/50 dark:bg-orange-900/20", text: "text-orange-600 dark:text-orange-400", rawHex: "#f97316" },
  zinc: { border: "border-zinc-500", bg: "bg-zinc-100/50 dark:bg-zinc-800/40", text: "text-zinc-600 dark:text-zinc-300", rawHex: "#71717a" }
}

export default function DashboardRouterPage({ params }: { params?: any }) {
  const router = useRouter()
  const { theme, setTheme, resolvedTheme } = useTheme()
  const activeTheme = (resolvedTheme || theme || (typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light")) === "dark" ? "dark" : "light"

  const resolvedParams = params ? (params instanceof Promise ? React.use(params) : params) : {}
  const slug = resolvedParams?.slug

  // 1. Core Data States
  const [user, setUser] = React.useState<UserProfile | null>(null)
  const [accounts, setAccounts] = React.useState<Account[]>([])
  const [currentAccount, setCurrentAccount] = React.useState<Account | null>(null)
  const [currentWorkspace, setCurrentWorkspace] = React.useState<Workspace | null>(null)
  const [installedApps, setInstalledApps] = React.useState<InstalledApp[]>([])
  const [globallyInstalledApps, setGloballyInstalledApps] = React.useState<string[]>([])
  const [expandedWorkspaceApps, setExpandedWorkspaceApps] = React.useState<string | null>(null)

  // 2. Navigation & Tabs States (Scoped per workspace and synced with database)
  const [openApps, setOpenApps] = React.useState<OpenAppTab[]>([])
  const [activeAppId, setActiveAppId] = React.useState<string | null>(null)
  const [tabGroups, setTabGroups] = React.useState<TabGroup[]>([])
  const [iframeRefreshKeys, setIframeRefreshKeys] = React.useState<Record<string, number>>({})
  const iframeSubpaths = React.useRef<Record<string, string>>({})

  // 3. Dock States
  const [isDockVisible, setIsDockVisible] = React.useState(false)
  const [dockPosition, setDockPosition] = React.useState<"bottom" | "top" | "left" | "right">(() => {
    if (typeof window !== "undefined") {
      try {
        return (localStorage.getItem("bold_dock_position") as any) || "bottom"
      } catch (e) { }
    }
    return "bottom"
  })
  const [dockOrientation, setDockOrientation] = React.useState<"horizontal" | "vertical">("horizontal")
  const [dockAlignment, setDockAlignment] = React.useState<"left" | "center" | "right">("center")
  const [dockNoMargin, setDockNoMargin] = React.useState<boolean>(false)
  const [dockSize, setDockSize] = React.useState<number>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_dock_size")
        if (saved) return Number(saved)
      } catch (e) { }
    }
    return 48
  })
  const [dockAppIds, setDockAppIds] = React.useState<string[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_dock_app_ids")
        if (saved !== null) return JSON.parse(saved)
      } catch (e) { }
    }
    return []
  })
  const [dockAppIdsInitialized, setDockAppIdsInitialized] = React.useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("bold_dock_app_ids") !== null
    }
    return false
  })
  const [dockWidgetIds, setDockWidgetIds] = React.useState<string[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_dock_widget_ids")
        if (saved !== null) return JSON.parse(saved)
      } catch (e) { }
    }
    return []
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_dock_position", dockPosition)
        localStorage.setItem("bold_dock_size", String(dockSize))
        localStorage.setItem("bold_dock_app_ids", JSON.stringify(dockAppIds))
        localStorage.setItem("bold_dock_widget_ids", JSON.stringify(dockWidgetIds))
      } catch (e) { }
    }
  }, [dockPosition, dockSize, dockAppIds, dockWidgetIds])

  React.useEffect(() => {
    if (installedApps.length > 0 && !dockAppIdsInitialized) {
      const activeAppIds = installedApps.filter((ia) => ia.status !== "inactive").map((ia) => ia.app.id)
      setDockAppIds(activeAppIds)
      setDockAppIdsInitialized(true)
      try {
        localStorage.setItem("bold_dock_app_ids", JSON.stringify(activeAppIds))
      } catch (e) { }
    }
  }, [installedApps, dockAppIdsInitialized])

  // 4. Preferences & Config
  const [lang, setLang] = React.useState<Language>("pt")

  React.useEffect(() => {
    const savedLang = localStorage.getItem("language") as Language
    if (savedLang && (savedLang === "pt" || savedLang === "en" || savedLang === "es")) {
      setLang(savedLang)
    }
  }, [])
  const [topBarConfig, setTopBarConfig] = React.useState(() => {
    const defaultConfig = {
      showClock: true,
      showDate: true,
      showWeather: true,
      timeFormat: "24h" as "12h" | "24h",
      dateFormat: "long" as "long" | "short",
      companyLogoShape: "original" as "circle" | "square" | "rectangle" | "original",
      companyLogoProportion: "1:1" as "1:1" | "4:3" | "16:9" | "2:1"
    }
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_topbar_config")
        if (saved) return { ...defaultConfig, ...JSON.parse(saved) }
      } catch (e) { }
    }
    return defaultConfig
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_topbar_config", JSON.stringify(topBarConfig))
      } catch (e) { }
    }
  }, [topBarConfig])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    const handleTopBarConfig = (e: any) => {
      if (e.detail) {
        setTopBarConfig((prev: any) => ({ ...prev, ...e.detail }))
      }
    }
    window.addEventListener("bold_topbar_config_changed", handleTopBarConfig)
    return () => {
      window.removeEventListener("bold_topbar_config_changed", handleTopBarConfig)
    }
  }, [])

  const [alarms, setAlarms] = React.useState<any[]>([])
  const [timerState, setTimerState] = React.useState({ duration: 300, remaining: 300, isRunning: false })
  const [newAlarmTime, setNewAlarmTime] = React.useState("08:00")
  const [newAlarmLabel, setNewAlarmLabel] = React.useState("")
  const [timerHours, setTimerHours] = React.useState(0)
  const [timerMinutes, setTimerMinutes] = React.useState(5)
  const [timerSeconds, setTimerSeconds] = React.useState(0)
  const [clockTab, setClockTab] = React.useState<"alarms" | "timer">("alarms")

  // 5. Members & Roles
  const [members, setMembers] = React.useState<any[]>([])
  const membersRef = React.useRef(members)
  React.useEffect(() => {
    membersRef.current = members
  }, [members])
  const [profileRole, setProfileRole] = React.useState<string>("member")
  const [workspaceRole, setWorkspaceRole] = React.useState<string>("member")
  const [currentWorkspaceMembers, setCurrentWorkspaceMembers] = React.useState<any[]>([])
  const currentWorkspaceMembersRef = React.useRef(currentWorkspaceMembers)
  React.useEffect(() => {
    currentWorkspaceMembersRef.current = currentWorkspaceMembers
  }, [currentWorkspaceMembers])
  const [userWorkspaceMemberships, setUserWorkspaceMemberships] = React.useState<any[]>([])
  const userWorkspaceMembershipsRef = React.useRef(userWorkspaceMemberships)
  React.useEffect(() => {
    userWorkspaceMembershipsRef.current = userWorkspaceMemberships
  }, [userWorkspaceMemberships])
  const [customRolesList, setCustomRolesList] = React.useState<any[]>([])
  const [allCustomRoles, setAllCustomRoles] = React.useState<any[]>([])
  const [profileTab, setProfileTab] = React.useState<"profile" | "security" | "menu" | "personalization">("profile")
  const [selectedGroupedProfileName, setSelectedGroupedProfileName] = React.useState<string | null>(null)
  const [selectedAppForRoles, setSelectedAppForRoles] = React.useState<any>(null)
  const [userCustomRolesMap, setUserCustomRolesMap] = React.useState<Record<string, string[]>>({})
  const [profileEditingName, setProfileEditingName] = React.useState("")
  const [profileAppPerms, setProfileAppPerms] = React.useState<Record<string, { access: boolean; c: boolean; r: boolean; u: boolean; d: boolean }>>({})
  const [isCreatingNewProfile, setIsCreatingNewProfile] = React.useState(false)

  // 6. Account
  const [isSwitchAccountOpen, setIsSwitchAccountOpen] = React.useState(false)
  const [accountSearchQuery, setAccountSearchQuery] = React.useState("")
  const t = translations[lang]

  // 7. Split Screen & Overlays
  const [splitActiveAppId, setSplitActiveAppId] = React.useState<string | null>(null)
  const [focusedPane, setFocusedPane] = React.useState<"left" | "right">("left")
  const [splitOrientation, setSplitOrientation] = React.useState<"horizontal" | "vertical">("horizontal")
  const [splitProportion, setSplitProportion] = React.useState<number>(50)

  const [revokingApiKeyId, setRevokingApiKeyId] = React.useState<string | null>(null)
  const [revokingAppId, setRevokingAppId] = React.useState<string | null>(null)
  const [showSettingsModal, setShowSettingsModal] = React.useState(false)
  const [showNotificationCenter, setShowNotificationCenter] = React.useState(false)
  const [settingsTab, setSettingsTab] = React.useState<"company" | "workspaces" | "developer" | "widgets" | "users" | "dock" | "menu" | "apps-list-tab" | "widgets-list-tab" | "wallpaper-tab" | "history">("company")
  const [devSubTab, setDevSubTab] = React.useState<string>("api-keys")
  const [authAppToShow, setAuthAppToShow] = React.useState<any>(null)
  const [showAddWidgetToDesktopModal, setShowAddWidgetToDesktopModal] = React.useState(false)
  const [isMac, setIsMac] = React.useState(false)

  // 8. Profile & Company Settings Form States
  const [profileName, setProfileName] = React.useState("")
  const [companyName, setCompanyName] = React.useState("")
  const [profileAvatarUrl, setProfileAvatarUrl] = React.useState<string | null>(null)
  const [profilePhone, setProfilePhone] = React.useState("")
  const [loginMusicUrl, setLoginMusicUrl] = React.useState<string>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("loginMusicUrl")
        if (saved) return saved
      } catch (e) { }
    }
    return "https://cdn.boldworkspace.com.br/storage/v1/object/public/alpha/startup-sound.mp3"
  })
  const [isLoginMusicEnabled, setIsLoginMusicEnabled] = React.useState<boolean>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("loginMusicEnabled")
        if (saved !== null) return saved === "true"
      } catch (e) { }
    }
    return true
  })
  const [fullSizeScreen, setFullSizeScreen] = React.useState<boolean>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_fullscreen_dismissed_permanent")
        if (saved !== null) return saved === "true"
      } catch (e) { }
    }
    return false
  })
  const [lockscreenPin, setLockscreenPin] = React.useState<string>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("lockscreenPin")
        if (saved) return saved
      } catch (e) { }
    }
    return ""
  })
  const [companyLogoUrl, setCompanyLogoUrl] = React.useState<string | null>(null)
  const [companyBannerUrl, setCompanyBannerUrl] = React.useState<string | null>(null)
  const [companyDocument, setCompanyDocument] = React.useState("")
  const [companyBio, setCompanyBio] = React.useState("")
  const [companyCategory, setCompanyCategory] = React.useState("")
  const [companyLocation, setCompanyLocation] = React.useState("")
  const [companyWebsite, setCompanyWebsite] = React.useState("")
  const [companyPrimaryColor, setCompanyPrimaryColor] = React.useState("")
  const [companyCustomDomain, setCompanyCustomDomain] = React.useState("")
  const [companySamlEnabled, setCompanySamlEnabled] = React.useState(false)
  const [companySamlIdpEntityId, setCompanySamlIdpEntityId] = React.useState("")
  const [companySamlSsoUrl, setCompanySamlSsoUrl] = React.useState("")
  const [companySamlCertificate, setCompanySamlCertificate] = React.useState("")
  const [dockAutoHide, setDockAutoHide] = React.useState(false)
  const [usersList, setUsersList] = React.useState<any[]>([])
  const [apiKey, setApiKey] = React.useState(() => {
    if (typeof window !== "undefined") {
      try {
        return localStorage.getItem("bold_api_key") || ""
      } catch (e) { }
    }
    return ""
  })
  const [webhookUrl, setWebhookUrl] = React.useState(() => {
    if (typeof window !== "undefined") {
      try {
        return localStorage.getItem("bold_webhook_url") || ""
      } catch (e) { }
    }
    return ""
  })
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([])
  const [notificationsLoaded, setNotificationsLoaded] = React.useState(false)

  // 9. Terms & Password
  const [isAcceptingTerms, setIsAcceptingTerms] = React.useState(false)
  const [hasCheckedTermsCheckbox, setHasCheckedTermsCheckbox] = React.useState(false)
  const [currentPassword, setCurrentPassword] = React.useState("")
  const [newPassword, setNewPassword] = React.useState("")
  const [confirmPassword, setConfirmPassword] = React.useState("")
  const [isChangingPassword, setIsChangingPassword] = React.useState(false)

  // 10. Security & Developer
  const [apiKeys, setApiKeys] = React.useState<any[]>([])
  const [developerApps, setDeveloperApps] = React.useState<any[]>([])
  const [samlConfig, setSamlConfig] = React.useState<any>(null)
  const [marketplaceApps, setMarketplaceApps] = React.useState<App[]>([])
  const [configInstalledApps, setConfigInstalledApps] = React.useState<InstalledApp[]>([])
  const [configAppIdToEdit, setConfigAppIdToEdit] = React.useState<string | null>(null)

  // 11. UI Core & Time
  const [loading, setLoading] = React.useState(true)
  const [currentTime, setCurrentTime] = React.useState<string>("")
  const [currentDate, setCurrentDate] = React.useState<string>("")
  const [weatherInfo, setWeatherInfo] = React.useState<string>("Compartilhe sua localização")

  // Solicitação de Geolocalização do Navegador e busca de Temperatura em Tempo Real
  const requestUserLocation = React.useCallback(async () => {
    if (typeof window === "undefined" || !("geolocation" in navigator)) {
      setWeatherInfo("Compartilhe sua localização")
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude
        const lon = position.coords.longitude

        let cityName = "Sua Cidade"
        try {
          const geoRes = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=pt`,
            { cache: "no-store" }
          )
          if (geoRes.ok) {
            const geoData = await geoRes.json()
            cityName = geoData.city || geoData.locality || geoData.principalSubdivision || "Sua Cidade"
          }
        } catch (e) { }

        try {
          const weatherRes = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`,
            { cache: "no-store" }
          )
          if (weatherRes.ok) {
            const weatherData = await weatherRes.json()
            if (weatherData.current_weather && typeof weatherData.current_weather.temperature === "number") {
              const temp = Math.round(weatherData.current_weather.temperature)
              const formatted = `${cityName}, ${temp}°C`
              setWeatherInfo(formatted)
              try {
                localStorage.setItem("bold_weather_info", formatted)
              } catch (err) { }
            }
          }
        } catch (e) { }
      },
      (error) => {
        console.warn("Geolocalização não compartilhada pelo usuário:", error)
        setWeatherInfo("Compartilhe sua localização")
      },
      { timeout: 10000, maximumAge: 600000 }
    )
  }, [])

  React.useEffect(() => {
    const handleWeatherUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<any>
      if (customEvent.detail) {
        setWeatherInfo(`${customEvent.detail.cityName}, ${customEvent.detail.currentTemp}°C`)
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("bold_weather_updated", handleWeatherUpdate)
      try {
        const savedWeather = localStorage.getItem("bold_weather_info")
        if (savedWeather && savedWeather.includes("°C")) {
          setWeatherInfo(savedWeather)
        }
      } catch (e) { }

      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
          if (result.state === "granted") {
            requestUserLocation()
          } else if (result.state === "prompt") {
            requestUserLocation()
          } else {
            setWeatherInfo("Compartilhe sua localização")
          }
        }).catch(() => {
          requestUserLocation()
        })
      } else {
        requestUserLocation()
      }
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("bold_weather_updated", handleWeatherUpdate)
      }
    }
  }, [requestUserLocation])
  const [calendarDate, setCalendarDate] = React.useState<Date | undefined>(new Date())
  const [pinnedWidgetSides, setPinnedWidgetSides] = React.useState<Record<string, "left" | "right" | "top">>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_pinned_widget_sides")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return {}
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_pinned_widget_sides", JSON.stringify(pinnedWidgetSides))
      } catch (e) { }
    }
  }, [pinnedWidgetSides])

  const [collapsedSides, setCollapsedSides] = React.useState<Record<"left" | "right" | "top", boolean>>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_collapsed_sides")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return { left: false, right: false, top: false }
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_collapsed_sides", JSON.stringify(collapsedSides))
      } catch (e) { }
    }
  }, [collapsedSides])

  const [sideWidths, setSideWidths] = React.useState<Record<"left" | "right", number>>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_side_widths")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return { left: 320, right: 320 }
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_side_widths", JSON.stringify(sideWidths))
      } catch (e) { }
    }
  }, [sideWidths])

  const [topPanelHeight, setTopPanelHeight] = React.useState<number>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_top_panel_height")
        if (saved) return Number(saved)
      } catch (e) { }
    }
    return 180
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_top_panel_height", String(topPanelHeight))
      } catch (e) { }
    }
  }, [topPanelHeight])

  // 12. Widgets & Floating Windows
  const [widgetsConfig, setWidgetsConfig] = React.useState<any[]>(() => {
    const defaultWidgets = [
      { id: "system-calendar", name: "Calendário", description: "Visualização estilo macOS do calendário e compromissos.", icon: "📅", type: "system", isDefault: true, enabled: true, visible: true, dimension: "small", defaultWidth: 320, defaultHeight: 380 },
      { id: "system-weather", name: "Clima", description: "Previsão do tempo baseada na sua localização.", icon: "☁️", type: "system", isDefault: true, enabled: true, visible: true, dimension: "small", defaultWidth: 320, defaultHeight: 380 },
      { id: "system-calculator", name: "Calculadora", description: "Calculadora flutuante para operações básicas.", icon: "🧮", type: "system", isDefault: true, enabled: true, visible: true, dimension: "small", defaultWidth: 320, defaultHeight: 650 },
      { id: "system-notes", name: "Notas", description: "Anotações rápidas salvas automaticamente.", icon: "📝", type: "system", isDefault: true, enabled: true, visible: true, dimension: "large", defaultWidth: 480, defaultHeight: 430 }
    ]

    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_widgets_config")
        if (saved) {
          const parsed = JSON.parse(saved)
          const standardIds = ["system-calendar", "system-weather", "system-calculator", "system-notes"]
          const updated = parsed.map((w: any) => {
            if (standardIds.includes(w.id)) {
              return { ...w, enabled: true, isDefault: true }
            }
            return w
          })
          // Ensure all 4 default widgets are present
          defaultWidgets.forEach((dw) => {
            if (!updated.some((w: any) => w.id === dw.id)) {
              updated.unshift(dw)
            }
          })
          return updated
        }
      } catch (e) { }
    }
    return defaultWidgets
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_widgets_config", JSON.stringify(widgetsConfig))
      } catch (e) { }
    }
    if (widgetsConfig.length > 0) {
      setDockWidgetIds(widgetsConfig.filter((w) => w.enabled).map((w) => w.id))
      // Remover widgets desativados de activeFloatingWidgets automaticamente
      const disabledIds = widgetsConfig.filter((w) => !w.enabled).map((w) => w.id)
      if (disabledIds.length > 0) {
        setActiveFloatingWidgets((prev) => {
          const next = prev.filter((id) => !disabledIds.includes(id))
          if (typeof window !== "undefined") {
            try {
              localStorage.setItem("bold_active_floating_widgets", JSON.stringify(next))
            } catch (e) { }
          }
          return next
        })
      }
    }

    // Persistir no Banco de Dados via GraphQL (Supabase)
    try {
      const token = getAuthToken()
      if (token) {
        fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              mutation UpdateWidgetsConfig($widgetsConfig: String!) {
                updateUserWidgetsConfig(widgetsConfig: $widgetsConfig)
              }
            `,
            variables: {
              widgetsConfig: JSON.stringify(widgetsConfig)
            }
          })
        }).catch((err) => console.error("Erro ao salvar widgetsConfig no Supabase:", err))
      }
    } catch (e) { }
  }, [widgetsConfig])

  const [activeFloatingWidgets, setActiveFloatingWidgets] = React.useState<string[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_active_floating_widgets")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return []
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_active_floating_widgets", JSON.stringify(activeFloatingWidgets))
      } catch (e) { }
    }
  }, [activeFloatingWidgets])
  const [floatingPositions, setFloatingPositions] = React.useState<Record<string, { x: number; y: number; z: number }>>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_floating_positions")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return {}
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_floating_positions", JSON.stringify(floatingPositions))
      } catch (e) { }
    }
  }, [floatingPositions])

  const [floatingSizes, setFloatingSizes] = React.useState<Record<string, { w: number; h: number }>>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_floating_sizes")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return {}
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_floating_sizes", JSON.stringify(floatingSizes))
      } catch (e) { }
    }
  }, [floatingSizes])

  const [minimizedWidgets, setMinimizedWidgets] = React.useState<Set<string>>(new Set())

  const [pinnedWidgets, setPinnedWidgets] = React.useState<string[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_pinned_widgets")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return []
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_pinned_widgets", JSON.stringify(pinnedWidgets))
      } catch (e) { }
    }
  }, [pinnedWidgets])

  const [maxZIndex, setMaxZIndex] = React.useState(15)
  const [notesContent, setNotesContent] = React.useState("")

  // 13. Desktop & Mobile Home Screen
  const [isMobile, setIsMobile] = React.useState(false)
  const [windowSize, setWindowSize] = React.useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768
  })
  const [renamedAppNames, setRenamedAppNames] = React.useState<Record<string, string>>({})
  const [renamingAppId, setRenamingAppId] = React.useState<string | null>(null)
  const [renamingAppInput, setRenamingAppInput] = React.useState("")
  const [desktopWallpaper, setDesktopWallpaper] = React.useState("bold-light")
  const [mobileWallpaper, setMobileWallpaper] = React.useState("bold-light")
  const [useSeparateMobileWallpaper, setUseSeparateMobileWallpaper] = React.useState(false)
  const [customWallpapers, setCustomWallpapers] = React.useState<string[]>([])
  const [wallpaperTab, setWallpaperTab] = React.useState<"desktop" | "mobile">("desktop")
  const [desktopGridCols, setDesktopGridCols] = React.useState<number>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_desktop_grid_cols")
        if (saved) return Number(saved)
      } catch (e) { }
    }
    return 12
  })
  const [desktopIconSize, setDesktopIconSize] = React.useState<number>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_desktop_icon_size")
        if (saved) return Number(saved)
      } catch (e) { }
    }
    return 48
  })
  const [desktopShowLabels, setDesktopShowLabels] = React.useState<boolean>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_desktop_show_labels")
        if (saved !== null) return saved === "true"
      } catch (e) { }
    }
    return true
  })

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("bold_desktop_grid_cols", String(desktopGridCols))
        localStorage.setItem("bold_desktop_icon_size", String(desktopIconSize))
        localStorage.setItem("bold_desktop_show_labels", String(desktopShowLabels))
      } catch (e) { }
    }
  }, [desktopGridCols, desktopIconSize, desktopShowLabels])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    const handleGridCols = (e: any) => {
      if (typeof e.detail === "number") setDesktopGridCols(e.detail)
    }
    const handleIconSize = (e: any) => {
      if (typeof e.detail === "number") setDesktopIconSize(e.detail)
    }
    const handleShowLabels = (e: any) => {
      if (typeof e.detail === "boolean") setDesktopShowLabels(e.detail)
    }
    window.addEventListener("bold_desktop_grid_cols_changed", handleGridCols)
    window.addEventListener("bold_desktop_icon_size_changed", handleIconSize)
    window.addEventListener("bold_desktop_show_labels_changed", handleShowLabels)
    return () => {
      window.removeEventListener("bold_desktop_grid_cols_changed", handleGridCols)
      window.removeEventListener("bold_desktop_icon_size_changed", handleIconSize)
      window.removeEventListener("bold_desktop_show_labels_changed", handleShowLabels)
    }
  }, [])

  // Sugestão de modo Tela Cheia no login / carregamento do Workspace
  const [showFullscreenPrompt, setShowFullscreenPrompt] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === "undefined") return

    let timer: NodeJS.Timeout | undefined

    const checkFullscreenPreference = () => {
      // Ignora telas touch muito pequenas (mobile)
      if (window.innerWidth < 768) return

      const isPermanentDismissed = fullSizeScreen || (user?.fullSizeScreen === true) || localStorage.getItem("bold_fullscreen_dismissed_permanent") === "true"
      const isSessionDismissed = sessionStorage.getItem("bold_fullscreen_dismissed_session") === "true"
      const isAlreadyFullscreen = Boolean(document.fullscreenElement)

      // Toda vez que recarregar a página deve verificar se este campo está como false, e se a tela está cheia.
      if (!isPermanentDismissed && !isSessionDismissed && !isAlreadyFullscreen) {
        timer = setTimeout(() => {
          if (!document.fullscreenElement) {
            setShowFullscreenPrompt(true)
          }
        }, 1200)
      } else {
        setShowFullscreenPrompt(false)
      }
    }

    checkFullscreenPreference()

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        setShowFullscreenPrompt(false)
      }
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => {
      if (timer) clearTimeout(timer)
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
    }
  }, [fullSizeScreen, user?.fullSizeScreen])

  const handleEnableFullscreen = () => {
    setShowFullscreenPrompt(false)
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => { })
      }
    } catch (e) { }
  }

  const [hiddenAppIds, setHiddenAppIds] = React.useState<string[]>([])

  // Sincronizar hiddenAppIds estritamente por workspace
  React.useEffect(() => {
    if (typeof window === "undefined" || !currentWorkspace?.id) return
    try {
      const saved = localStorage.getItem(`bold_hidden_app_ids:${currentWorkspace.id}`)
      if (saved) {
        setHiddenAppIds(JSON.parse(saved))
      } else {
        setHiddenAppIds([])
      }
    } catch (e) {
      setHiddenAppIds([])
    }
  }, [currentWorkspace?.id])

  React.useEffect(() => {
    if (typeof window !== "undefined" && currentWorkspace?.id) {
      try {
        localStorage.setItem(`bold_hidden_app_ids:${currentWorkspace.id}`, JSON.stringify(hiddenAppIds))
      } catch (e) { }
    }
  }, [hiddenAppIds, currentWorkspace?.id])
  const [contextMenu, setContextMenu] = React.useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false })

  React.useEffect(() => {
    setContextMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev))
  }, [activeAppId, splitActiveAppId])
  const [appPositions, setAppPositions] = React.useState<Record<string, { col: number; row: number }>>({})
  const [desktopWidgets, setDesktopWidgets] = React.useState<any[]>([])
  const [desktopWidgetPositions, setDesktopWidgetPositions] = React.useState<any>({})
  const [mobileHomeOrder, setMobileHomeOrder] = React.useState<string[]>([])
  const [mobileDockAppIds, setMobileDockAppIds] = React.useState<string[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("mobileDockAppIds")
        if (saved) return JSON.parse(saved)
      } catch (e) { }
    }
    return []
  })

  React.useEffect(() => {
    if (installedApps.length > 0 && mobileDockAppIds.length === 0) {
      const initialDock = installedApps.slice(0, 4).map((ia) => ia.app.id)
      setMobileDockAppIds(initialDock)
      try {
        localStorage.setItem("mobileDockAppIds", JSON.stringify(initialDock))
      } catch (e) { }
    }
  }, [installedApps, mobileDockAppIds])

  // 14. Modals States
  const [editMode, setEditMode] = React.useState(false)
  const [showWallpaperModal, setShowWallpaperModal] = React.useState(false)
  const [showAddAppModal, setShowAddAppModal] = React.useState(false)
  const [showWidgetLibraryModal, setShowWidgetLibraryModal] = React.useState(false)
  const [showMobileControlCenter, setShowMobileControlCenter] = React.useState(false)
  const [showMobileNotifications, setShowMobileNotifications] = React.useState(false)
  const [showMobileAppSwitcher, setShowMobileAppSwitcher] = React.useState(false)
  const [switcherOffsets, setSwitcherOffsets] = React.useState<Record<string, number>>({})

  // Audio Handler
  const playNotificationSound = React.useCallback(() => {
    try {
      const audio = new Audio("https://cdn.boldworkspace.com.br/storage/v1/object/public/alpha/notificacao-flashscore.mp3")
      audio.volume = 0.4
      audio.play().catch((e) => console.log("Erro ao reproduzir som:", e))
    } catch (e) { }
  }, [])

  // Play startup sound upon login / initial desktop mount
  const hasPlayedStartupMusic = React.useRef(false)
  React.useEffect(() => {
    if (!hasPlayedStartupMusic.current && isLoginMusicEnabled && loginMusicUrl) {
      hasPlayedStartupMusic.current = true
      const timer = setTimeout(() => {
        try {
          const audio = new Audio(loginMusicUrl)
          audio.volume = 0.35
          audio.play().catch((err) => {
            console.warn("Autoplay block or audio error:", err)
            // Se o navegador bloquear o autoplay antes da primeira interação, dispara no primeiro clique ou tecla
            const playOnFirstInteraction = () => {
              audio.play().catch(() => { })
              window.removeEventListener("click", playOnFirstInteraction)
              window.removeEventListener("keydown", playOnFirstInteraction)
            }
            window.addEventListener("click", playOnFirstInteraction, { once: true })
            window.addEventListener("keydown", playOnFirstInteraction, { once: true })
          })
        } catch (e) {
          console.warn("Audio initialization error:", e)
        }
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [loginMusicUrl, isLoginMusicEnabled])

  // User Workspace Preferences Sync
  const loadedWorkspaceIdRef = React.useRef<string | null>(null)
  const isPreferencesLoadedRef = React.useRef<boolean>(false)
  const pendingPrefUpdatesRef = React.useRef<Record<string, any>>({})
  const prefSaveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const saveWorkspacePreferences = React.useCallback(
    (updates: Record<string, any>, targetWorkspaceId?: string) => {
      const wsId = targetWorkspaceId || currentWorkspace?.id
      const token = getAuthToken()
      if (!wsId || !token) return

      pendingPrefUpdatesRef.current = {
        ...pendingPrefUpdatesRef.current,
        ...updates
      }

      if (prefSaveTimeoutRef.current) {
        clearTimeout(prefSaveTimeoutRef.current)
      }

      prefSaveTimeoutRef.current = setTimeout(async () => {
        const payload = { ...pendingPrefUpdatesRef.current }
        pendingPrefUpdatesRef.current = {}

        const input: Record<string, any> = {}
        for (const [k, v] of Object.entries(payload)) {
          if (v === undefined) continue
          if (
            [
              "appPositions",
              "topBarConfig",
              "floatingPositions",
              "floatingSizes",
              "desktopWidgets",
              "desktopWidgetPositions",
              "alarms",
              "extraSettings"
            ].includes(k)
          ) {
            input[k] = typeof v === "string" ? v : JSON.stringify(v)
          } else {
            input[k] = v
          }
        }

        try {
          await fetch("/api/graphql", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              query: `
                mutation SaveWorkspacePreferences($workspaceId: ID!, $input: UpdateUserWorkspacePreferencesInput!) {
                  updateMyWorkspacePreferences(workspaceId: $workspaceId, input: $input) {
                    id
                    workspaceId
                    updatedAt
                  }
                }
              `,
              variables: {
                workspaceId: wsId,
                input
              }
            })
          })
        } catch (err) {
          console.error("Erro ao salvar preferências do workspace:", err)
        }
      }, 350)
    },
    [currentWorkspace?.id]
  )

  const loadWorkspacePreferences = React.useCallback(
    async (workspaceId: string, tokenOverride?: string) => {
      const token = tokenOverride || getAuthToken()
      if (!token || !workspaceId) return

      try {
        const res = await fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              query GetWorkspacePreferences($workspaceId: ID!) {
                myWorkspacePreferences(workspaceId: $workspaceId) {
                  id
                  desktopWallpaper
                  mobileWallpaper
                  useSeparateMobileWallpaper
                  desktopIconSize
                  desktopGridCols
                  desktopShowLabels
                  hiddenAppIds
                  appPositions
                  dockPosition
                  dockOrientation
                  dockAlignment
                  dockSize
                  dockNoMargin
                  dockAppIds
                  dockWidgetIds
                  topBarConfig
                  topPanelHeight
                  activeFloatingWidgets
                  floatingPositions
                  floatingSizes
                  pinnedWidgets
                  desktopWidgets
                  desktopWidgetPositions
                  customWallpapers
                  alarms
                  extraSettings
                }
              }
            `,
            variables: { workspaceId }
          })
        })

        const json = await res.json()
        const pref = json?.data?.myWorkspacePreferences

        if (pref) {
          if (pref.desktopWallpaper) setDesktopWallpaper(pref.desktopWallpaper)
          if (pref.mobileWallpaper) setMobileWallpaper(pref.mobileWallpaper)
          if (pref.useSeparateMobileWallpaper !== null && pref.useSeparateMobileWallpaper !== undefined) {
            setUseSeparateMobileWallpaper(pref.useSeparateMobileWallpaper)
          }
          if (pref.desktopGridCols) setDesktopGridCols(pref.desktopGridCols)
          if (pref.desktopIconSize) setDesktopIconSize(pref.desktopIconSize)
          if (pref.desktopShowLabels !== null && pref.desktopShowLabels !== undefined) {
            setDesktopShowLabels(pref.desktopShowLabels)
          }
          if (pref.hiddenAppIds) setHiddenAppIds(pref.hiddenAppIds)

          if (pref.appPositions) {
            try {
              const parsed = typeof pref.appPositions === "string" ? JSON.parse(pref.appPositions) : pref.appPositions
              setAppPositions(parsed || {})
            } catch { }
          } else {
            setAppPositions({})
          }

          if (pref.dockPosition) setDockPosition(pref.dockPosition)
          if (pref.dockOrientation) setDockOrientation(pref.dockOrientation)
          if (pref.dockAlignment) setDockAlignment(pref.dockAlignment)
          if (pref.dockSize) setDockSize(pref.dockSize)
          if (pref.dockNoMargin !== null && pref.dockNoMargin !== undefined) {
            setDockNoMargin(pref.dockNoMargin)
          }
          if (pref.dockAppIds && pref.dockAppIds.length > 0) {
            setDockAppIds(pref.dockAppIds)
            setDockAppIdsInitialized(true)
          }
          if (pref.dockWidgetIds) setDockWidgetIds(pref.dockWidgetIds)

          if (pref.topBarConfig) {
            try {
              const parsed = typeof pref.topBarConfig === "string" ? JSON.parse(pref.topBarConfig) : pref.topBarConfig
              if (parsed && typeof parsed === "object") {
                setTopBarConfig((prev: any) => ({ ...prev, ...parsed }))
              }
            } catch { }
          }

          if (pref.activeFloatingWidgets) setActiveFloatingWidgets(pref.activeFloatingWidgets)
          if (pref.floatingPositions) {
            try {
              const parsed = typeof pref.floatingPositions === "string" ? JSON.parse(pref.floatingPositions) : pref.floatingPositions
              setFloatingPositions(parsed || {})
            } catch { }
          }
          if (pref.floatingSizes) {
            try {
              const parsed = typeof pref.floatingSizes === "string" ? JSON.parse(pref.floatingSizes) : pref.floatingSizes
              setFloatingSizes(parsed || {})
            } catch { }
          }
          if (pref.pinnedWidgets) setPinnedWidgets(pref.pinnedWidgets)
          if (pref.desktopWidgets) {
            try {
              const parsed = typeof pref.desktopWidgets === "string" ? JSON.parse(pref.desktopWidgets) : pref.desktopWidgets
              setDesktopWidgets(parsed || [])
            } catch { }
          } else {
            setDesktopWidgets([])
          }
          if (pref.desktopWidgetPositions) {
            try {
              const parsed = typeof pref.desktopWidgetPositions === "string" ? JSON.parse(pref.desktopWidgetPositions) : pref.desktopWidgetPositions
              setDesktopWidgetPositions(parsed || {})
            } catch { }
          } else {
            setDesktopWidgetPositions({})
          }
          if (pref.customWallpapers) setCustomWallpapers(pref.customWallpapers)
          if (pref.alarms) {
            try {
              const parsed = typeof pref.alarms === "string" ? JSON.parse(pref.alarms) : pref.alarms
              setAlarms(parsed || [])
            } catch { }
          }

          let restoredApps: OpenAppTab[] = []
          let restoredPaths: Record<string, string> = {}

          if (pref.extraSettings) {
            try {
              const extra = typeof pref.extraSettings === "string" ? JSON.parse(pref.extraSettings) : pref.extraSettings
              if (Array.isArray(extra?.openApps)) {
                restoredApps = extra.openApps
              }
              if (extra?.appSubpaths && typeof extra.appSubpaths === "object") {
                restoredPaths = extra.appSubpaths
              }
            } catch { }
          }

          if (restoredApps.length === 0 && typeof window !== "undefined") {
            try {
              const localSaved = localStorage.getItem(`bold_open_apps_${workspaceId}`)
              if (localSaved) restoredApps = JSON.parse(localSaved) || []
            } catch { }
          }

          if (typeof window !== "undefined") {
            try {
              const localPaths = localStorage.getItem(`bold_app_subpaths_${workspaceId}`)
              if (localPaths) {
                const parsed = JSON.parse(localPaths)
                if (parsed && typeof parsed === "object") {
                  restoredPaths = { ...parsed, ...restoredPaths }
                }
              }
            } catch { }
          }

          Object.assign(iframeSubpaths.current, restoredPaths)
          restoredApps.forEach((t) => {
            const sub = t.subpath || restoredPaths[t.instanceId] || (t.app?.id ? restoredPaths[t.app.id] : "") || (t.app?.slug ? restoredPaths[t.app.slug] : "") || ""
            if (sub) {
              iframeSubpaths.current[t.instanceId] = sub
              t.subpath = sub
              if (t.app && t.app.iframeUrl) {
                t.app.iframeUrl = resolveIframeUrl(t.app.iframeUrl, sub)
              }
              if (typeof window !== "undefined" && workspaceId) {
                const sSlug = getAppSlug(t.app)
                if (sSlug) {
                  iframeSubpaths.current[sSlug] = sub
                  localStorage.setItem(`bold_subpath_${workspaceId}_${sSlug}`, sub)
                }
                if (t.app?.id) {
                  iframeSubpaths.current[t.app.id] = sub
                  localStorage.setItem(`bold_subpath_${workspaceId}_${t.app.id}`, sub)
                }
              }
            }
          })

          setOpenApps((prev) => {
            const systemTabs = prev.filter((t) => ["settings", "profile", "marketplace"].includes(t.app.id))
            if (systemTabs.length === 0) return restoredApps
            const merged = [...restoredApps]
            for (const st of systemTabs) {
              if (!merged.some((t) => t.instanceId === st.instanceId)) {
                merged.push(st)
              }
            }
            return merged
          })
        } else {
          let restoredApps: OpenAppTab[] = []
          if (typeof window !== "undefined") {
            try {
              const localSaved = localStorage.getItem(`bold_open_apps_${workspaceId}`)
              if (localSaved) restoredApps = JSON.parse(localSaved) || []
            } catch { }
          }
          setOpenApps((prev) => {
            const systemTabs = prev.filter((t) => ["settings", "profile", "marketplace"].includes(t.app.id))
            if (systemTabs.length === 0) return restoredApps
            const merged = [...restoredApps]
            for (const st of systemTabs) {
              if (!merged.some((t) => t.instanceId === st.instanceId)) {
                merged.push(st)
              }
            }
            return merged
          })
        }
        loadedWorkspaceIdRef.current = workspaceId
        isPreferencesLoadedRef.current = true
      } catch (e) {
        console.error("Erro ao carregar preferências do workspace:", e)
      }
    },
    [resolveIframeUrl]
  )

  // Salvar estado das abas abertas no localStorage e banco por workspace
  React.useEffect(() => {
    if (typeof window !== "undefined" && currentWorkspace?.id) {
      try {
        localStorage.setItem(`bold_open_apps_${currentWorkspace.id}`, JSON.stringify(openApps))
        localStorage.setItem(`bold_active_app_${currentWorkspace.id}`, activeAppId || "")
        localStorage.setItem(`bold_app_subpaths_${currentWorkspace.id}`, JSON.stringify(iframeSubpaths.current))
      } catch (e) { }

      if (isPreferencesLoadedRef.current) {
        saveWorkspacePreferences(
          {
            extraSettings: {
              openApps,
              activeAppId,
              appSubpaths: iframeSubpaths.current
            }
          },
          currentWorkspace.id
        )
      }
    }
  }, [openApps, activeAppId, currentWorkspace?.id, saveWorkspacePreferences, iframeSubpaths])

  // Obter e sincronizar o papel do usuário no Workspace atual e os membros do Workspace
  React.useEffect(() => {
    if (!currentWorkspace?.id) {
      setWorkspaceRole(profileRole === "owner" || profileRole === "admin" ? "admin" : "member")
      setCurrentWorkspaceMembers([])
      currentWorkspaceMembersRef.current = []
      return
    }

    if (profileRole === "owner" || profileRole === "admin") {
      setWorkspaceRole("admin")
    }

    const token = getAuthToken()
    if (!token) return

    fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          query GetWorkspaceContextData($userId: ID!, $workspaceId: ID!) {
            userWorkspaceMemberships(userId: $userId) {
              workspaceId
              role
            }
            workspaceMembers(workspaceId: $workspaceId) {
              id
              userId
              role
            }
          }
        `,
        variables: { userId: user?.id || "", workspaceId: currentWorkspace.id }
      })
    })
      .then((res) => res.json())
      .then((res) => {
        const memberships = res?.data?.userWorkspaceMemberships || []
        setUserWorkspaceMemberships(memberships)
        userWorkspaceMembershipsRef.current = memberships
        const currentMembership = memberships.find((m: any) => m.workspaceId === currentWorkspace.id)
        if (profileRole === "owner" || profileRole === "admin") {
          setWorkspaceRole("admin")
        } else if (currentMembership?.role) {
          setWorkspaceRole(currentMembership.role)
        } else {
          setWorkspaceRole("member")
        }

        const wsMembers = res?.data?.workspaceMembers || []
        setCurrentWorkspaceMembers(wsMembers)
        currentWorkspaceMembersRef.current = wsMembers
      })
      .catch((e) => {
        console.error("Erro ao buscar dados do workspace:", e)
        if (profileRole === "owner" || profileRole === "admin") {
          setWorkspaceRole("admin")
        } else {
          setWorkspaceRole("member")
        }
      })
  }, [currentWorkspace?.id, user?.id, profileRole])

  // Hook 1: Core GraphQL Handlers
  const {
    showToast,
    loadDashboardData,
    loadInstalledApps,
    fetchMarketplaceApps,
    fetchMembers,
    fetchAllCustomRoles,
    fetchCustomRoles,
    fetchUserCustomRoles,
    handleCreateWorkspace,
    handleUpdateWorkspace,
    handleArchiveWorkspace,
    handleReactivateWorkspace,
    handleDeleteWorkspace,
    handleInviteUser,
    handleUpdateAccountMember,
    handleRemoveUser,
    handleSaveCompany
  } = useDashboardHandlers({
    user,
    setUser,
    accounts,
    setAccounts,
    currentAccount,
    setCurrentAccount,
    currentWorkspace,
    setCurrentWorkspace,
    installedApps,
    setInstalledApps,
    setMembers,
    setProfileRole,
    setAllCustomRoles,
    setSelectedGroupedProfileName,
    setIsCreatingNewProfile,
    setProfileEditingName,
    setProfileAppPerms,
    setCustomRolesList,
    setUserCustomRolesMap,
    setApiKeys,
    setDeveloperApps,
    setSamlConfig,
    setMarketplaceApps,
    setConfigInstalledApps,
    setConfigAppIdToEdit,
    setDesktopWallpaper,
    setMobileWallpaper,
    setUseSeparateMobileWallpaper,
    setLang,
    setTopBarConfig,
    setCustomWallpapers,
    setAlarms,
    setAppPositions,
    setDesktopWidgets,
    setDesktopWidgetPositions,
    setWidgetsConfig,
    loadWorkspacePreferences,
    setProfileName,
    setProfileAvatarUrl,
    setProfilePhone,
    setLoginMusicUrl,
    setIsLoginMusicEnabled,
    setFullSizeScreen,
    setLockscreenPin,
    setCompanyName,
    setCompanyLogoUrl,
    setCompanyBannerUrl,
    setCompanyDocument,
    setCompanyBio,
    setCompanyCategory,
    setCompanyLocation,
    setCompanyWebsite,
    setLoading,
    lang,
    router,
    playNotificationSound,
    t
  })

  // Auto-sync User Workspace Preferences to DB
  React.useEffect(() => {
    if (isPreferencesLoadedRef.current && currentWorkspace?.id && loadedWorkspaceIdRef.current === currentWorkspace.id) {
      saveWorkspacePreferences({
        activeFloatingWidgets,
        floatingPositions,
        floatingSizes,
        pinnedWidgets
      })
    }
  }, [activeFloatingWidgets, floatingPositions, floatingSizes, pinnedWidgets, currentWorkspace?.id, saveWorkspacePreferences])

  React.useEffect(() => {
    if (isPreferencesLoadedRef.current && currentWorkspace?.id && loadedWorkspaceIdRef.current === currentWorkspace.id) {
      saveWorkspacePreferences({
        dockPosition,
        dockOrientation,
        dockAlignment,
        dockSize,
        dockNoMargin,
        dockAppIds,
        dockWidgetIds
      })
    }
  }, [dockPosition, dockOrientation, dockAlignment, dockSize, dockNoMargin, dockAppIds, dockWidgetIds, currentWorkspace?.id, saveWorkspacePreferences])

  React.useEffect(() => {
    if (isPreferencesLoadedRef.current && currentWorkspace?.id && loadedWorkspaceIdRef.current === currentWorkspace.id) {
      saveWorkspacePreferences({
        desktopGridCols,
        desktopIconSize,
        desktopShowLabels
      })
    }
  }, [desktopGridCols, desktopIconSize, desktopShowLabels, currentWorkspace?.id, saveWorkspacePreferences])

  React.useEffect(() => {
    if (isPreferencesLoadedRef.current && currentWorkspace?.id && loadedWorkspaceIdRef.current === currentWorkspace.id) {
      saveWorkspacePreferences({
        hiddenAppIds
      })
    }
  }, [hiddenAppIds, currentWorkspace?.id, saveWorkspacePreferences])

  React.useEffect(() => {
    if (isPreferencesLoadedRef.current && currentWorkspace?.id && loadedWorkspaceIdRef.current === currentWorkspace.id) {
      saveWorkspacePreferences({
        topBarConfig
      })
    }
  }, [topBarConfig, currentWorkspace?.id, saveWorkspacePreferences])

  // Trigger fetchMarketplaceApps whenever Settings or Marketplace tab opens
  React.useEffect(() => {
    const isSettingsOpen = activeAppId === "settings" || splitActiveAppId === "settings"
    const isMarketplaceOpen = activeAppId === "marketplace" || splitActiveAppId === "marketplace"
    if (isSettingsOpen || isMarketplaceOpen) {
      fetchMarketplaceApps()
    }
  }, [activeAppId, splitActiveAppId, settingsTab, fetchMarketplaceApps])

  // Inicialização e sincronização contínua de globallyInstalledApps da conta
  React.useEffect(() => {
    if (!currentAccount) return

    const allInstalledAppIds = new Set<string>()

    // 1. Dados diretos de installedAppIds da conta (resolvidos pelo backend a partir do banco de dados)
    if (Array.isArray((currentAccount as any).installedAppIds)) {
      (currentAccount as any).installedAppIds.forEach((id: string) => {
        if (id) allInstalledAppIds.add(id)
      })
    }

    // 2. Coletar de todos os workspaces da conta atual
    currentAccount.workspaces?.forEach((ws: any) => {
      if (ws.installedApps) {
        ws.installedApps.forEach((ia: any) => {
          if (ia.status !== "inactive") {
            if (ia.app && ia.app.id) allInstalledAppIds.add(ia.app.id)
            if (ia.app && ia.app.slug) allInstalledAppIds.add(ia.app.slug)
            if (ia.appId) allInstalledAppIds.add(ia.appId)
          }
        })
      }
    })

    // 3. Coletar de installedApps ativos do workspace atual
    if (installedApps && installedApps.length > 0) {
      installedApps.forEach((ia) => {
        if (ia.status !== "inactive") {
          if (ia.app && ia.app.id) allInstalledAppIds.add(ia.app.id)
          if (ia.app && ia.app.slug) allInstalledAppIds.add(ia.app.slug)
        }
      })
    }

    // 4. Cache local por conta (apenas como fallback complementar)
    if (typeof window !== "undefined") {
      try {
        const savedAccountApps = localStorage.getItem(`globallyInstalledApps:${currentAccount.id}`)
        if (savedAccountApps) {
          const parsed = JSON.parse(savedAccountApps)
          if (Array.isArray(parsed)) {
            parsed.forEach((id: string) => allInstalledAppIds.add(id))
          }
        }
      } catch (e) { }
    }

    const merged = Array.from(allInstalledAppIds)
    setGloballyInstalledApps(merged)

    if (typeof window !== "undefined" && merged.length > 0) {
      try {
        localStorage.setItem(`globallyInstalledApps:${currentAccount.id}`, JSON.stringify(merged))
      } catch (e) { }
    }
  }, [currentAccount, installedApps])

  // Carrega notificações do banco de dados quando o usuário autenticar
  React.useEffect(() => {
    if (!user || notificationsLoaded) return
    const token = getAuthToken()
    if (!token) return
    setNotificationsLoaded(true)
    fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          query MyNotifications {
            myNotifications(limit: 50) {
              id
              appName
              type
              title
              text
              avatarUrl
              appIcon
              action
              read
              createdAt
            }
          }
        `
      })
    })
      .then((res) => res.json())
      .then((data) => {
        const dbNotifs = data?.data?.myNotifications
        if (Array.isArray(dbNotifs) && dbNotifs.length > 0) {
          const mapped: NotificationItem[] = dbNotifs.map((n: any) => ({
            id: n.id,
            type: n.type,
            title: n.title,
            text: n.text,
            time: n.createdAt ? new Date(n.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "agora",
            avatarUrl: n.avatarUrl || null,
            appIcon: n.appIcon || null,
            appName: n.appName || "Bold Workspace",
            action: n.action && n.action !== "{}" ? JSON.parse(n.action) : undefined
          }))
          setNotifications(mapped)
        }
      })
      .catch(() => { })
  }, [user, notificationsLoaded])

  // Hook 2: Calculator State
  const {
    calcDisplay,
    setCalcDisplay,
    calcFormula,
    setCalcFormula,
    calcResetOnNext,
    setCalcResetOnNext,
    calcHistory,
    setCalcHistory,
    showCalcHistory,
    setShowCalcHistory,
    calcFocusedWidgetId,
    setCalcFocusedWidgetId,
    handleCalcPress
  } = useCalculatorState()

  // Hook 3: Home Screen Grid & Reorder State
  const {
    mobileSearch,
    setMobileSearch,
    draggedAppId,
    setDraggedAppId,
    finalMobileHomeOrder,
    moveHomeItem,
    handleHomeDragStart,
    handleHomeDragOver,
    handleHomeDrop,
    desktopWallpaperStyle,
    mobileWallpaperStyle
  } = useHomeScreenState({
    mobileHomeOrder,
    setMobileHomeOrder,
    widgetsConfig,
    installedApps,
    hiddenAppIds,
    theme,
    desktopWallpaper,
    mobileWallpaper,
    useSeparateMobileWallpaper
  })

  // Hook 4: Security & 2FA
  const {
    setupData,
    setSetupData,
    totpCode,
    setTotpCode,
    totpDisableCode,
    setTotpDisableCode,
    totpError,
    setTotpError,
    handleEnable2FA,
    handleDisable2FA
  } = useSecurityHandlers({ user, setUser, showToast })

  // Hook 5: Navigation & App Router Sync
  const {
    updateAppSubpath,
    handleOpenSettingsTab,
    handleOpenProfileTab,
    handleOpenMarketplaceTab,
    handleAddNewTab,
    handleOpenApp,
    handleOpenExternalUrl,
    handleOpenWidgetAsApp,
    handleCloseApp,
    handleCloseAllApps
  } = useAppRouterSync({
    activeAppId,
    setActiveAppId,
    splitActiveAppId,
    setSplitActiveAppId,
    focusedPane,
    openApps,
    setOpenApps,
    installedApps,
    setWidgetsConfig,
    setNotifications,
    playNotificationSound,
    isMobile,
    setSettingsTab,
    currentWorkspace,
    currentAccount,
    iframeSubpaths
  })

  // Bi-directional Deep Link URL Routing (/account/:accountId/workspaces/:workspaceId/... <-> UI State)
  const initialSlugProcessedRef = React.useRef(false)
  React.useEffect(() => {
    if (!slug || initialSlugProcessedRef.current) return
    if (loading || !currentAccount || !currentAccount.workspaces) return

    const rawSegments = Array.isArray(slug)
      ? slug.map((s: string) => decodeURIComponent(s))
      : [decodeURIComponent(slug)]
    if (rawSegments.length === 0) return

    const parsed =
      typeof window !== "undefined"
        ? parseWorkspaceRouteFromPathname(
            window.location.pathname,
            window.location.search,
            window.location.hash
          )
        : parseWorkspaceRouteFromPathname("/" + rawSegments.join("/"))

    // Resolve Workspace if specified in URL
    let activeWs = currentWorkspace || currentAccount.workspaces?.[0] || null
    if (currentAccount?.workspaces && currentAccount.workspaces.length > 0 && parsed.workspaceIdentifier) {
      const matchedWs = currentAccount.workspaces.find(
        (w) =>
          w.id?.toLowerCase() === parsed.workspaceIdentifier ||
          w.slug?.toLowerCase() === parsed.workspaceIdentifier
      )
      if (matchedWs) {
        activeWs = matchedWs
        if (!currentWorkspace || currentWorkspace.id !== matchedWs.id) {
          setCurrentWorkspace(matchedWs)
          const token = getAuthToken()
          if (token && loadWorkspacePreferences) {
            loadWorkspacePreferences(matchedWs.id, token)
          }
          if (token) {
            loadInstalledApps(token, matchedWs.id)
          }
        }
      }
    }

    const primary = parsed.primary
    const secondary = parsed.secondary
    const tertiary = parsed.tertiary
    const appTargetId = parsed.appIdentifier || primary

    if (primary === "desk" || primary === "desktop") {
      initialSlugProcessedRef.current = true
      setActiveAppId(null)
      setSplitActiveAppId(null)
      setShowSettingsModal(false)
    } else if (!primary) {
      initialSlugProcessedRef.current = true
      const savedActiveId = typeof window !== "undefined" && activeWs?.id
        ? localStorage.getItem(`bold_active_app_${activeWs.id}`)
        : null
      if (savedActiveId && openApps.some((t) => t.instanceId === savedActiveId)) {
        setActiveAppId(savedActiveId)
      } else {
        setActiveAppId(null)
        setSplitActiveAppId(null)
        setShowSettingsModal(false)
      }
    } else if (primary === "new-tab") {
      initialSlugProcessedRef.current = true
      const existingNewTab = openApps.find((t) => t.app?.id === "new-tab" || t.app?.slug === "new-tab")
      if (existingNewTab) {
        setActiveAppId(existingNewTab.instanceId)
        setShowSettingsModal(false)
      } else {
        handleAddNewTab()
      }
    } else if (primary === "dock") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      setSettingsTab("dock")
    } else if (primary === "bar" || primary === "topbar") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      setSettingsTab("company")
    } else if (primary === "widgets") {
      initialSlugProcessedRef.current = true
      setShowWidgetLibraryModal(true)
    } else if (primary === "users") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      setSettingsTab("users")
    } else if (primary === "workspaces") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      setSettingsTab("workspaces")
    } else if (primary === "marketplace") {
      initialSlugProcessedRef.current = true
      handleOpenMarketplaceTab()
    } else if (primary === "profile") {
      initialSlugProcessedRef.current = true
      handleOpenProfileTab()
    } else if (primary === "sso") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      setSettingsTab("developer")
      setDevSubTab("saml-sso")
    } else if (primary === "webhooks") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      setSettingsTab("developer")
      setDevSubTab("webhooks")
    } else if (primary === "api" || primary === "api-keys") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      setSettingsTab("developer")
      setDevSubTab("api-keys")
    } else if (primary === "settings") {
      initialSlugProcessedRef.current = true
      handleOpenSettingsTab()
      if (!secondary || secondary === "company") setSettingsTab("company")
      else if (secondary === "workspaces") setSettingsTab("workspaces")
      else if (secondary === "users") setSettingsTab("users")
      else if (secondary === "history" || secondary === "registros") setSettingsTab("history")
      else if (secondary === "dock") setSettingsTab("dock")
      else if (secondary === "marketplace") handleOpenMarketplaceTab()
      else if (secondary === "wallpaper") setSettingsTab("wallpaper-tab")
      else if (secondary === "developer" || secondary === "developers") {
        setSettingsTab("developer")
        if (tertiary === "api" || tertiary === "api-keys") setDevSubTab("api-keys")
        else if (tertiary === "webhooks") setDevSubTab("webhooks")
        else if (tertiary === "sso" || tertiary === "saml" || tertiary === "saml-sso") setDevSubTab("saml-sso")
      }
    } else {
      const targetStr = appTargetId || ""
      const matchedInstalledApp = installedApps.find((ia) => isAppMatch(ia, targetStr))
      const matchedOpenTab = openApps.find((t) => isAppMatch({ app: t.app }, targetStr))
      const matchedApp = matchedInstalledApp?.app || matchedOpenTab?.app

      if (matchedApp) {
        initialSlugProcessedRef.current = true
        if (!matchedInstalledApp || matchedInstalledApp.status === "active") {
          let appSubpath = parsed.appSubpath || ""
          if (appSubpath.startsWith("/")) appSubpath = appSubpath.slice(1)
          handleOpenApp(matchedApp, appSubpath)
        } else {
          setActiveAppId(null)
          setSplitActiveAppId(null)
          setShowSettingsModal(false)
          const desktopPath = buildWorkspaceDesktopPath(currentAccount, activeWs)
          window.history.replaceState({ path: desktopPath }, "", desktopPath)
        }
      } else if (!loading && installedApps.length > 0) {
        initialSlugProcessedRef.current = true
        setActiveAppId(null)
        setSplitActiveAppId(null)
        setShowSettingsModal(false)
        const desktopPath = buildWorkspaceDesktopPath(currentAccount, activeWs)
        window.history.replaceState({ path: desktopPath }, "", desktopPath)
      }
    }
  }, [slug, currentAccount, currentWorkspace, installedApps, loading, handleOpenApp, handleOpenSettingsTab, handleOpenMarketplaceTab, handleOpenProfileTab, handleAddNewTab, loadWorkspacePreferences, loadInstalledApps, openApps])

  // Sync Active UI State to Browser URL Path (/account/[accountId]/workspaces/[workspaceId]/...)
  React.useEffect(() => {
    if (typeof window === "undefined") return
    if (!currentAccount || !currentWorkspace) return
    if (window.location.pathname === "/" || window.location.pathname === "") return
    if (!initialSlugProcessedRef.current || loading) return

    const wsBasePath = getWorkspaceBasePath(currentAccount, currentWorkspace)
    let fullPath = `${wsBasePath}/desktop`

    const isSettingsActive =
      activeAppId === "settings" ||
      openApps.find((t) => t.instanceId === activeAppId)?.app?.id === "settings" ||
      showSettingsModal

    if (isSettingsActive) {
      let subPath = "settings/company"
      if (settingsTab === "developer") {
        if (devSubTab === "webhooks") subPath = "settings/developers/webhooks"
        else if (devSubTab === "saml-sso" || devSubTab === "sso") subPath = "settings/developers/sso"
        else if (devSubTab === "mcp") subPath = "settings/developers/mcp"
        else subPath = "settings/developers/api"
      } else if (settingsTab === "company" || (settingsTab as string) === "system") subPath = "settings/company"
      else if (settingsTab === "workspaces") subPath = "settings/workspaces"
      else if (settingsTab === "users") subPath = "settings/users"
      else if (settingsTab === "history") subPath = "settings/history"
      else if (settingsTab === "dock") subPath = "settings/dock"
      else if (settingsTab === "wallpaper-tab") subPath = "settings/wallpaper"
      else subPath = "settings/company"
      fullPath = `${wsBasePath}/${subPath}`
    } else if (activeAppId === "profile" || openApps.find((t) => t.instanceId === activeAppId)?.app?.id === "profile") {
      fullPath = `${wsBasePath}/profile`
    } else if (activeAppId === "marketplace" || openApps.find((t) => t.instanceId === activeAppId)?.app?.id === "marketplace") {
      fullPath = `${wsBasePath}/marketplace`
    } else if (activeAppId) {
      const activeTab = openApps.find((t) => t.instanceId === activeAppId) || openApps.find((t) => t.app?.id === activeAppId)
      if (activeTab) {
        const appSlug = getAppSlug(activeTab.app)
        let appSubpath = iframeSubpaths.current[activeTab.instanceId] || activeTab.subpath || ""
        if (!appSubpath && currentWorkspace?.id) {
          appSubpath =
            localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${appSlug}`) ||
            localStorage.getItem(`bold_subpath_${currentWorkspace.id}_${activeTab.app.id}`) ||
            iframeSubpaths.current[activeTab.app.id] ||
            iframeSubpaths.current[appSlug] ||
            ""
        }
        fullPath = buildWorkspaceAppPath(currentAccount, currentWorkspace, activeTab.app, appSubpath)
      } else {
        // App is currently transitioning or opening, avoid resetting URL to desktop
        return
      }
    }

    if (window.location.pathname + window.location.search + window.location.hash !== fullPath) {
      window.history.replaceState({ path: fullPath }, "", fullPath)
    }
  }, [currentWorkspace, currentAccount, activeAppId, showSettingsModal, settingsTab, devSubTab, openApps, iframeSubpaths, loading])

  // Continuous Two-way iframe sub-path synchronization to parent browser address bar
  React.useEffect(() => {
    if (typeof window === "undefined") return
    if (!activeAppId) return

    const activeTab = openApps.find((t) => t.instanceId === activeAppId)
    if (!activeTab || activeTab.app.id === "settings" || activeTab.app.id === "profile" || activeTab.app.id === "marketplace") return

    const syncUrlFromIframe = () => {
      try {
        const iframe = document.querySelector(`iframe[data-instance-id="${activeTab.instanceId}"]`) as HTMLIFrameElement
        if (iframe && iframe.contentWindow) {
          const loc = iframe.contentWindow.location
          let pathname = loc.pathname || ""
          const search = loc.search || ""
          const hash = loc.hash || ""

          if (pathname && pathname !== "blank" && !loc.href.startsWith("about:")) {
            let sub = pathname
            if (!sub.startsWith("/")) sub = "/" + sub
            if (sub === "/" || sub === "/blank") sub = ""

            const fullSub = `${sub}${search}${hash}`
            if (iframeSubpaths.current[activeTab.instanceId] !== fullSub) {
              updateAppSubpath(activeTab.instanceId, fullSub)
            }
          }
        }
      } catch (e) {
        // Cross-origin fallback handled via postMessage
      }
    }

    syncUrlFromIframe()
    const interval = setInterval(syncUrlFromIframe, 350)
    return () => clearInterval(interval)
  }, [activeAppId, openApps, currentAccount, currentWorkspace, installedApps, iframeSubpaths, updateAppSubpath])

  // Listen for Browser & Mouse Back Button Navigation (popstate & Mouse 4)
  React.useEffect(() => {
    if (typeof window === "undefined") return

    const handlePopState = () => {
      const parsed = parseWorkspaceRouteFromPathname(
        window.location.pathname,
        window.location.search,
        window.location.hash
      )

      const primary = parsed.primary
      const secondary = parsed.secondary
      const tertiary = parsed.tertiary
      const appTargetId = parsed.appIdentifier || primary
      const appSubpath = parsed.appSubpath

      if (!primary || primary === "home" || primary === "desk" || primary === "desktop") {
        setActiveAppId(null)
        setShowSettingsModal(false)
        return
      }

      if (primary === "new-tab") {
        const existingNewTab = openApps.find((t) => t.app?.id === "new-tab" || t.app?.slug === "new-tab")
        if (existingNewTab) {
          setActiveAppId(existingNewTab.instanceId)
          setShowSettingsModal(false)
        } else {
          handleAddNewTab()
        }
      } else if (primary === "settings") {
        setShowSettingsModal(true)
        if (secondary === "company") setSettingsTab("company")
        else if (secondary === "workspaces") setSettingsTab("workspaces")
        else if (secondary === "users") setSettingsTab("users")
        else if (secondary === "history" || secondary === "registros") setSettingsTab("history")
        else if (secondary === "dock") setSettingsTab("dock")
        else if (secondary === "marketplace") handleOpenMarketplaceTab()
        else if (secondary === "wallpaper") setSettingsTab("wallpaper-tab")
        else if (secondary === "developers" || secondary === "developer") {
          setSettingsTab("developer")
          if (tertiary === "webhooks") setDevSubTab("webhooks")
          else if (tertiary === "sso" || tertiary === "saml") setDevSubTab("saml-sso")
          else setDevSubTab("api-keys")
        }
      } else if (primary === "profile") {
        setActiveAppId("profile")
      } else if (primary === "marketplace") {
        setActiveAppId("marketplace")
      } else {
        const matchedInstalledApp = installedApps.find(
          (ia) => ia.app.slug?.toLowerCase() === appTargetId || ia.app.id?.toLowerCase() === appTargetId
        )
        const matchedOpenTab = openApps.find(
          (t) => t.app?.slug?.toLowerCase() === appTargetId || t.app?.id?.toLowerCase() === appTargetId
        )
        const matchedApp = matchedInstalledApp?.app || matchedOpenTab?.app

        if (matchedApp && (!matchedInstalledApp || matchedInstalledApp.status === "active")) {
          handleOpenApp(matchedApp, appSubpath)
        } else {
          setActiveAppId(null)
          setShowSettingsModal(false)
          const desktopPath = buildWorkspaceDesktopPath(currentAccount, currentWorkspace)
          window.history.replaceState({ path: desktopPath }, "", desktopPath)
        }
      }
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button === 3) {
        e.preventDefault()
        window.history.back()
      }
    }

    window.addEventListener("popstate", handlePopState)
    window.addEventListener("mouseup", handleMouseUp)
    return () => {
      window.removeEventListener("popstate", handlePopState)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [installedApps, handleOpenApp, currentAccount, currentWorkspace])


  // Sync Theme and Language across multiple browser tabs / window focus
  React.useEffect(() => {
    const syncFromStorage = () => {
      try {
        const savedLang = localStorage.getItem("language") as Language
        if (savedLang && (savedLang === "pt" || savedLang === "en" || savedLang === "es") && savedLang !== lang) {
          setLang(savedLang)
        }
        const savedTheme = localStorage.getItem("theme")
        if (savedTheme && (savedTheme === "dark" || savedTheme === "light") && savedTheme !== theme) {
          setTheme(savedTheme)
        }
      } catch (e) { }
    }

    const handleStorage = (e: StorageEvent) => {
      if (e.key === "language" && e.newValue && (e.newValue === "pt" || e.newValue === "en" || e.newValue === "es")) {
        setLang(e.newValue as Language)
      }
      if (e.key === "theme" && e.newValue && (e.newValue === "dark" || e.newValue === "light")) {
        setTheme(e.newValue)
      }
    }

    const handleFocus = () => {
      syncFromStorage()
    }

    window.addEventListener("storage", handleStorage)
    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleFocus)

    return () => {
      window.removeEventListener("storage", handleStorage)
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleFocus)
    }
  }, [lang, theme, setTheme])

  // Obter lista de usuários da empresa enriquecida com o workspaceRole referente ao Workspace ativo
  const getAccountUsersList = React.useCallback((overrideWsMembers?: any[], overrideMembers?: any[]) => {
    const currentMembers = overrideMembers || membersRef.current || members || []
    const wsMembers = overrideWsMembers || currentWorkspaceMembersRef.current || currentWorkspaceMembers || []

    const getMemberWorkspaceRole = (userId: string, companyRole: string): string | null => {
      // 1. Se for o usuário logado, utiliza seu workspaceRole atual
      if (userId === user?.id && workspaceRole) {
        return workspaceRole
      }
      // 2. Busca o papel explicitamente atribuído a este usuário no workspace atual
      const matchedWsMember = wsMembers.find((wm: any) => wm.userId === userId || wm.user?.id === userId || wm.id === userId)
      if (matchedWsMember?.role) {
        return matchedWsMember.role
      }
      // 3. Se for owner ou admin da empresa (account), tem privilégio de admin em todos os workspaces
      if (companyRole === "owner" || companyRole === "admin") {
        return "admin"
      }
      // 4. Se esse usuário não tiver acesso àquele Workspace, deixa null
      return null
    }

    if (currentMembers.length > 0) {
      return currentMembers
        .map((m: any) => {
          const uId = m.user?.id || m.userId || m.id
          const cRole = m.role || "member"
          const wRole = getMemberWorkspaceRole(uId, cRole)
          return {
            id: uId,
            userId: uId,
            name: m.user?.name || m.name || "Usuário",
            email: m.user?.email || m.email || "",
            avatarUrl: m.user?.avatarUrl || m.avatarUrl || null,
            status: m.status || "active",
            workspaceRole: wRole,
            accountRole: cRole,
            companyRole: cRole,
            role: cRole,
            userCustomRole: m.userCustomRole || null,
          }
        })
    }
    if (user) {
      const cRole = profileRole || "member"
      return [{
        id: user.id,
        userId: user.id,
        name: user.name || "Usuário",
        email: user.email || "",
        avatarUrl: user.avatarUrl ?? null,
        status: (user as any).status || "active",
        workspaceRole: workspaceRole || (cRole === "owner" || cRole === "admin" ? "admin" : "member"),
        accountRole: cRole,
        companyRole: cRole,
        role: cRole,
        userCustomRole: (user as any).userCustomRole || null,
      }]
    }
    return []
  }, [members, currentWorkspaceMembers, user, workspaceRole, profileRole])

  // Formatar lista de usuários estritamente conforme a interface do SDK (sem duplicar role/companyRole)
  const formatUsersForSdk = React.useCallback((list: any[]) => {
    return (list || []).map((u: any) => ({
      id: u.id || u.userId,
      name: u.name,
      email: u.email,
      avatarUrl: u.avatarUrl ?? null,
      status: u.status || "active",
      workspaceRole: u.workspaceRole ?? null,
      accountRole: u.accountRole || u.companyRole || u.role || "member",
    }))
  }, [])

  // Obter lista de workspaces acessíveis pelo usuário atual
  const getUserAccessibleWorkspaces = React.useCallback((overrideMemberships?: any[]) => {
    const rawWorkspaces = (currentAccount?.workspaces || []).filter((w: any) => w.status !== false)
    const memberships = overrideMemberships || userWorkspaceMembershipsRef.current || userWorkspaceMemberships || []
    const isAccountAdmin = profileRole === "owner" || profileRole === "admin"

    const accessible = rawWorkspaces
      .filter((w: any) => {
        if (isAccountAdmin) return true
        if (currentWorkspace?.id === w.id) return true
        return memberships.some((m: any) => m.workspaceId === w.id)
      })
      .map((w: any) => {
        let roleInWs = "member"
        if (isAccountAdmin) {
          roleInWs = "admin"
        } else if (currentWorkspace?.id === w.id && workspaceRole) {
          roleInWs = workspaceRole
        } else {
          const matched = memberships.find((m: any) => m.workspaceId === w.id)
          if (matched?.role) {
            roleInWs = matched.role
          }
        }
        return {
          id: w.id,
          name: w.name,
          slug: w.slug,
          role: roleInWs,
        }
      })

    if (accessible.length === 0 && currentWorkspace) {
      return [{
        id: currentWorkspace.id,
        name: currentWorkspace.name,
        slug: currentWorkspace.slug,
        role: workspaceRole || (isAccountAdmin ? "admin" : "member"),
      }]
    }

    return accessible
  }, [currentAccount, currentWorkspace, profileRole, workspaceRole, userWorkspaceMemberships])

  const getUserWorkspacesList = getUserAccessibleWorkspaces

  // Broadcast Theme, Language, Context, Users and Workspaces via PostMessage to all open Iframes
  const broadcastUsersAndContext = React.useCallback((overrideUsers?: any[], overrideWsRole?: string, overrideMemberships?: any[]) => {
    if (typeof document === "undefined") return
    const currentTheme = (resolvedTheme || theme || (document.documentElement.classList.contains("dark") ? "dark" : "light")) === "dark" ? "dark" : "light"
    const currentLang = (lang || (typeof localStorage !== "undefined" ? localStorage.getItem("language") : null) || "pt").split("-")[0]
    const iframes = document.querySelectorAll("iframe")
    const activeUsersList = overrideUsers || getAccountUsersList()
    const sdkUsersList = formatUsersForSdk(activeUsersList)
    const activeAccountWorkspaces = getUserAccessibleWorkspaces(overrideMemberships)
    const effectiveWsRole = overrideWsRole || workspaceRole || (profileRole === "owner" || profileRole === "admin" ? "admin" : "member")
    const effectiveAccountRole = profileRole || "member"

    const contextPayload = {
      theme: currentTheme,
      language: currentLang,
      user: user ? {
        id: user.id,
        name: user.name,
        email: user.email,
        avatarUrl: user.avatarUrl ?? null,
        status: (user as any).status || "active",
      } : null,
      workspace: currentWorkspace ? {
        id: currentWorkspace.id,
        name: currentWorkspace.name,
        slug: currentWorkspace.slug,
        role: effectiveWsRole,
      } : null,
      account: currentAccount ? {
        id: currentAccount.id,
        name: currentAccount.name,
        slug: currentAccount.slug,
        role: effectiveAccountRole,
        workspaces: activeAccountWorkspaces,
      } : null,
      users: sdkUsersList,
    }

    iframes.forEach((iframe) => {
      try {
        const win = iframe.contentWindow
        if (win) {
          win.postMessage({ type: "BOLD_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
          win.postMessage({ type: "BOLD_WIDGET_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
          win.postMessage({ type: "BOLD_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
          win.postMessage({ type: "BOLD_WIDGET_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
          win.postMessage({ type: "BOLD_CONTEXT_CHANGED", payload: contextPayload }, "*")
          win.postMessage({ type: "BOLD_WIDGET_CONTEXT_CHANGED", payload: contextPayload }, "*")
          win.postMessage({ type: "BOLD_CONTEXT_UPDATE", payload: contextPayload }, "*")
          win.postMessage({ type: "BOLD_WIDGET_CONTEXT_UPDATE", payload: contextPayload }, "*")
          if (activeAccountWorkspaces.length > 0) {
            win.postMessage({ type: "BOLD_WORKSPACES_CHANGED", payload: activeAccountWorkspaces, workspaces: activeAccountWorkspaces }, "*")
            win.postMessage({ type: "BOLD_WORKSPACE_WORKSPACES", payload: activeAccountWorkspaces, workspaces: activeAccountWorkspaces }, "*")
          }
          if (sdkUsersList.length > 0) {
            win.postMessage({ type: "BOLD_USERS_CHANGED", payload: sdkUsersList, users: sdkUsersList }, "*")
            win.postMessage({ type: "BOLD_WORKSPACE_USERS", payload: sdkUsersList, users: sdkUsersList }, "*")
            win.postMessage({ type: "BOLD_WIDGET_USERS_CHANGED", payload: sdkUsersList, users: sdkUsersList }, "*")
          }
        }
      } catch (e) { }
    })
  }, [resolvedTheme, theme, lang, getAccountUsersList, formatUsersForSdk, getUserAccessibleWorkspaces, workspaceRole, profileRole, user, currentWorkspace, currentAccount])

  // Sincronização e Revalidação em tempo real de membros e permissões
  const isRefreshingUsersRef = React.useRef(false)
  const refreshUsersAndPermissions = React.useCallback(async (): Promise<{ users: any[]; workspaces: any[]; workspaceRole: string; memberships: any[] }> => {
    const token = getAuthToken()
    const activeWorkspaceId = currentWorkspace?.id
    const activeUserId = user?.id

    if (!token) {
      return {
        users: getAccountUsersList(),
        workspaces: getUserAccessibleWorkspaces(),
        workspaceRole: workspaceRole || (profileRole === "owner" || profileRole === "admin" ? "admin" : "member"),
        memberships: userWorkspaceMembershipsRef.current || [],
      }
    }

    try {
      isRefreshingUsersRef.current = true
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        body: JSON.stringify({
          query: `
            query RefreshUsersAndContextData($userId: ID!, $workspaceId: ID!) {
              accountProfiles {
                id
                userId
                role
                status
                user {
                  id
                  name
                  email
                  avatarUrl
                }
              }
              userWorkspaceMemberships(userId: $userId) {
                workspaceId
                role
              }
              workspaceMembers(workspaceId: $workspaceId) {
                id
                userId
                role
              }
            }
          `,
          variables: {
            userId: activeUserId || "",
            workspaceId: activeWorkspaceId || "",
          },
        }),
      })

      const json = await res.json()
      const fetchedProfiles = json?.data?.accountProfiles || []
      const fetchedMemberships = json?.data?.userWorkspaceMemberships || []
      const fetchedWsMembers = json?.data?.workspaceMembers || []

      if (fetchedProfiles.length > 0) {
        setMembers(fetchedProfiles)
        membersRef.current = fetchedProfiles
      }
      if (Array.isArray(json?.data?.workspaceMembers)) {
        setCurrentWorkspaceMembers(fetchedWsMembers)
        currentWorkspaceMembersRef.current = fetchedWsMembers
      }
      if (Array.isArray(json?.data?.userWorkspaceMemberships)) {
        setUserWorkspaceMemberships(fetchedMemberships)
        userWorkspaceMembershipsRef.current = fetchedMemberships
      }

      let effectiveWsRole = "member"
      if (profileRole === "owner" || profileRole === "admin") {
        effectiveWsRole = "admin"
      } else {
        const currentMembership = fetchedMemberships.find((m: any) => m.workspaceId === activeWorkspaceId)
        const matchedWsMember = fetchedWsMembers.find((wm: any) => wm.userId === activeUserId)
        if (currentMembership?.role) {
          effectiveWsRole = currentMembership.role
        } else if (matchedWsMember?.role) {
          effectiveWsRole = matchedWsMember.role
        } else {
          effectiveWsRole = "member"
        }
      }
      setWorkspaceRole(effectiveWsRole)

      const freshUsers = getAccountUsersList(fetchedWsMembers, fetchedProfiles)
      const freshWorkspaces = getUserAccessibleWorkspaces(fetchedMemberships)

      // Dispara broadcast imediato para todos os iframes ativos
      broadcastUsersAndContext(freshUsers, effectiveWsRole, fetchedMemberships)

      return {
        users: freshUsers,
        workspaces: freshWorkspaces,
        workspaceRole: effectiveWsRole,
        memberships: fetchedMemberships,
      }
    } catch (err) {
      console.error("[RefreshUsersAndPermissions] Erro ao sincronizar permissões:", err)
      return {
        users: getAccountUsersList(),
        workspaces: getUserAccessibleWorkspaces(),
        workspaceRole: workspaceRole || (profileRole === "owner" || profileRole === "admin" ? "admin" : "member"),
        memberships: userWorkspaceMembershipsRef.current || [],
      }
    } finally {
      isRefreshingUsersRef.current = false
    }
  }, [currentWorkspace?.id, user?.id, profileRole, workspaceRole, getAccountUsersList, getUserAccessibleWorkspaces, broadcastUsersAndContext])

  // Carregar permissões do workspace atual e memberships de todos os workspaces ao montar ou trocar de workspace
  React.useEffect(() => {
    if (!user?.id || !currentWorkspace?.id) return
    refreshUsersAndPermissions().catch(() => {})
  }, [user?.id, currentWorkspace?.id, currentAccount?.id])

  const isCurrentWorkspaceAdmin = React.useMemo(() => {
    if (profileRole === "owner" || profileRole === "admin") return true
    if (workspaceRole === "admin") return true
    if (currentWorkspace?.id && userWorkspaceMemberships.some((m: any) => m.workspaceId === currentWorkspace.id && m.role === "admin")) {
      return true
    }
    return false
  }, [profileRole, workspaceRole, currentWorkspace?.id, userWorkspaceMemberships])

  const adminWorkspaces = React.useMemo(() => {
    const allWs = (currentAccount?.workspaces || []).filter((w: any) => w.status !== false)
    if (profileRole === "owner" || profileRole === "admin") {
      return allWs
    }
    return allWs.filter((ws: any) => {
      if (ws.id === currentWorkspace?.id && workspaceRole === "admin") return true
      if (ws.role === "admin") return true
      return userWorkspaceMemberships.some((m: any) => m.workspaceId === ws.id && m.role === "admin")
    })
  }, [currentAccount?.workspaces, profileRole, currentWorkspace?.id, workspaceRole, userWorkspaceMemberships])

  // Se o usuário não for Administrador do Workspace atual, fechar/bloquear acesso à Loja de Aplicativos
  React.useEffect(() => {
    if (loading || !currentWorkspace?.id) return
    if (!isCurrentWorkspaceAdmin) {
      const hasMarketplaceOpen =
        activeAppId === "marketplace" ||
        openApps.some((t) => t.app?.id === "marketplace" || t.instanceId === "marketplace")
      if (hasMarketplaceOpen) {
        setOpenApps((prev) => prev.filter((t) => t.app?.id !== "marketplace" && t.instanceId !== "marketplace"))
        if (
          activeAppId === "marketplace" ||
          openApps.find((t) => t.instanceId === activeAppId)?.app?.id === "marketplace"
        ) {
          setActiveAppId(null)
        }
      }
    }
  }, [isCurrentWorkspaceAdmin, activeAppId, openApps, loading, currentWorkspace?.id])

  // Ouvinte de invalidação em tempo real (disparado ao alterar membros, papéis ou workspaces)
  React.useEffect(() => {
    const handleInvalidation = () => {
      refreshUsersAndPermissions().catch(() => {})
    }

    window.addEventListener("bold_users_invalidated", handleInvalidation)
    window.addEventListener("bold_members_updated", handleInvalidation)

    return () => {
      window.removeEventListener("bold_users_invalidated", handleInvalidation)
      window.removeEventListener("bold_members_updated", handleInvalidation)
    }
  }, [refreshUsersAndPermissions])

  // Broadcast contínuo de tema, idioma e estado nas abas abertas
  React.useEffect(() => {
    broadcastUsersAndContext()
    const t1 = setTimeout(broadcastUsersAndContext, 50)
    const t2 = setTimeout(broadcastUsersAndContext, 150)
    const t3 = setTimeout(broadcastUsersAndContext, 300)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [theme, resolvedTheme, lang, activeAppId, splitActiveAppId, focusedPane, openApps, broadcastUsersAndContext])

  // Listen to BoldBridge and BoldWidgetBridge message events from SDK in Iframes
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data) return

      const currentTheme = document.documentElement.classList.contains("dark") ? "dark" : "light"

      if (typeof event.data === "object" && typeof event.data.type === "string" && event.data.type.startsWith("BOLD_")) {
        const msg = event.data as { type: string; requestId?: string; payload?: any }
        const sourceWindow = event.source as Window | null

        const sendResponse = (type: string, payload: unknown, error?: string) => {
          sourceWindow?.postMessage({ type, requestId: msg.requestId, payload, error }, event.origin || "*")
        }

        switch (msg.type) {
          case "BOLD_GET_CONTEXT": {
            if (!user || !currentWorkspace || !currentAccount) {
              sendResponse("BOLD_ERROR", null, "Context not yet available.")
            } else {
              const forceRefresh = Boolean(msg.payload?.forceRefresh)
              const currentLang = (lang || (typeof localStorage !== "undefined" ? localStorage.getItem("language") : null) || "pt").split("-")[0]

              const buildContextResponse = (
                uList = getAccountUsersList(),
                wsRole = workspaceRole || (profileRole === "owner" || profileRole === "admin" ? "admin" : "member"),
                accWorkspaces = getUserAccessibleWorkspaces()
              ) => ({
                theme: currentTheme,
                language: currentLang,
                user: {
                  id: user.id,
                  name: user.name,
                  email: user.email,
                  avatarUrl: user.avatarUrl ?? null,
                  status: (user as any).status || "active",
                },
                workspace: {
                  id: currentWorkspace.id,
                  name: currentWorkspace.name,
                  slug: currentWorkspace.slug,
                  role: wsRole,
                },
                account: {
                  id: currentAccount.id,
                  name: currentAccount.name,
                  slug: currentAccount.slug,
                  role: profileRole || "member",
                  workspaces: accWorkspaces,
                },
                users: formatUsersForSdk(uList),
              })

              if (!forceRefresh) {
                // Stale-While-Revalidate: responde instantâneo com a memória e revalida em segundo plano
                sendResponse("BOLD_CONTEXT_RESPONSE", buildContextResponse())
                refreshUsersAndPermissions().catch(() => {})
              } else {
                // Força atualização em tempo real aguardando resposta fresca do banco
                refreshUsersAndPermissions()
                  .then((res) => {
                    const freshWorkspaces = getUserAccessibleWorkspaces(res.memberships)
                    sendResponse("BOLD_CONTEXT_RESPONSE", buildContextResponse(res.users, res.workspaceRole, freshWorkspaces))
                  })
                  .catch(() => {
                    sendResponse("BOLD_CONTEXT_RESPONSE", buildContextResponse())
                  })
              }
            }
            break
          }

          case "BOLD_GET_USERS":
          case "BOLD_GET_ACCOUNT_USERS":
          case "REQUEST_USERS":
          case "GET_USERS":
          case "BOLD_WIDGET_GET_USERS": {
            const isWidget = msg.type === "BOLD_WIDGET_GET_USERS"
            const responseType = isWidget ? "BOLD_WIDGET_USERS_RESPONSE" : "BOLD_USERS_RESPONSE"
            const forceRefresh = Boolean(msg.payload?.forceRefresh)
            const available = formatUsersForSdk(getAccountUsersList())

            if (!forceRefresh && available.length > 0) {
              // Stale-While-Revalidate: responde imediatamente ao iframe e revalida em segundo plano
              sendResponse(responseType, available)
              if (sourceWindow) {
                try {
                  sourceWindow.postMessage({
                    type: "BOLD_WORKSPACE_USERS",
                    payload: available,
                    users: available,
                    requestId: msg.requestId
                  }, "*")
                } catch (e) { }
              }
              refreshUsersAndPermissions().catch(() => {})
            } else {
              // Force refresh ou dados vazios em memória: busca via GraphQL fresco
              refreshUsersAndPermissions()
                .then((res) => {
                  const fresh = res.users && res.users.length > 0 ? formatUsersForSdk(res.users) : available
                  sendResponse(responseType, fresh)
                  if (sourceWindow) {
                    try {
                      sourceWindow.postMessage({
                        type: "BOLD_WORKSPACE_USERS",
                        payload: fresh,
                        users: fresh,
                        requestId: msg.requestId
                      }, "*")
                    } catch (e) { }
                  }
                })
                .catch((err) => {
                  if (available.length > 0) {
                    sendResponse(responseType, available)
                  } else {
                    sendResponse("BOLD_ERROR", null, String(err))
                  }
                })
            }
            break
          }

          case "BOLD_GET_WORKSPACES":
          case "BOLD_GET_ACCOUNT_WORKSPACES":
          case "REQUEST_WORKSPACES":
          case "GET_WORKSPACES":
          case "BOLD_WIDGET_GET_WORKSPACES": {
            const isWidget = msg.type === "BOLD_WIDGET_GET_WORKSPACES"
            const responseType = isWidget ? "BOLD_WIDGET_WORKSPACES_RESPONSE" : "BOLD_WORKSPACES_RESPONSE"
            const forceRefresh = Boolean(msg.payload?.forceRefresh)
            let available = getUserWorkspacesList()

            if (!forceRefresh && available.length > 0) {
              sendResponse(responseType, available)
              if (sourceWindow) {
                try {
                  sourceWindow.postMessage({
                    type: "BOLD_WORKSPACE_WORKSPACES",
                    payload: available,
                    workspaces: available,
                    requestId: msg.requestId
                  }, "*")
                } catch (e) { }
              }
              refreshUsersAndPermissions().catch(() => {})
            } else {
              // Se solicitado forceRefresh ou a lista de workspaces ainda não estiver em memória, busca via GraphQL
              const token = getAuthToken()
              if (token) {
                fetch("/api/graphql", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Cache-Control": "no-cache, no-store, must-revalidate",
                    Pragma: "no-cache",
                  },
                  body: JSON.stringify({
                    query: `
                      query GetAvailableWorkspaces {
                        myAccounts {
                          id
                          name
                          slug
                          workspaces {
                            id
                            name
                            slug
                            status
                            sortOrder
                          }
                        }
                      }
                    `
                  })
                })
                  .then((res) => res.json())
                  .then((data) => {
                    const fetchedAccounts = data?.data?.myAccounts || []
                    let availableWorkspaces: any[] = []
                    if (fetchedAccounts.length > 0) {
                      const matchedAcc = currentAccount
                        ? fetchedAccounts.find((a: any) => a.id === currentAccount.id) || fetchedAccounts[0]
                        : fetchedAccounts[0]
                      if (matchedAcc && matchedAcc.workspaces) {
                        availableWorkspaces = matchedAcc.workspaces
                          .filter((w: any) => w.status !== false)
                          .map((w: any) => ({
                            id: w.id,
                            name: w.name,
                            slug: w.slug,
                            accountId: matchedAcc.id,
                            accountName: matchedAcc.name,
                            accountSlug: matchedAcc.slug,
                            status: "active",
                            sortOrder: w.sortOrder || 0,
                            isCurrent: currentWorkspace?.id === w.id,
                            role: (currentWorkspace?.id === w.id ? workspaceRole : null) || profileRole || "member",
                            workspaceRole: (currentWorkspace?.id === w.id ? workspaceRole : null) || profileRole || "member",
                          }))
                      }
                    }
                    if (availableWorkspaces.length === 0 && currentWorkspace) {
                      availableWorkspaces = [{
                        id: currentWorkspace.id,
                        name: currentWorkspace.name,
                        slug: currentWorkspace.slug,
                        accountId: currentWorkspace.accountId || currentAccount?.id,
                        accountName: currentAccount?.name,
                        accountSlug: currentAccount?.slug,
                        status: "active",
                        sortOrder: 0,
                        isCurrent: true,
                        role: workspaceRole || profileRole || "member",
                        workspaceRole: workspaceRole || profileRole || "member",
                      }]
                    }
                    sendResponse(responseType, availableWorkspaces)
                    if (sourceWindow) {
                      try {
                        sourceWindow.postMessage({
                          type: "BOLD_WORKSPACE_WORKSPACES",
                          payload: availableWorkspaces,
                          workspaces: availableWorkspaces,
                          requestId: msg.requestId
                        }, "*")
                      } catch (e) { }
                    }
                  })
                  .catch((err) => {
                    if (available.length > 0) {
                      sendResponse(responseType, available)
                    } else {
                      sendResponse("BOLD_ERROR", null, String(err))
                    }
                  })
              } else {
                sendResponse(responseType, available)
              }
            }
            break
          }

          case "BOLD_GET_SSO_TOKEN":
          case "REQUEST_SSO_TOKEN":
          case "BOLD_WORKSPACE_READY": {
            if (!user || !currentWorkspace) {
              sendResponse("BOLD_ERROR", null, "Context not available for SSO token.")
              break
            }
            const activeTabForSso =
              openApps.find((t) => {
                const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
                return iframe && iframe.contentWindow === sourceWindow
              }) || openApps.find((t) => t.instanceId === activeAppId)

            const targetAppId = activeTabForSso?.app?.id || activeAppId || "app"
            const rawCookie = document.cookie
            const jwtMatch = rawCookie.split("; ").find((c) => c.startsWith("token="))
            const jwt = jwtMatch ? jwtMatch.split("=")[1] : ""

            fetch("/api/v1/sso/token", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(jwt && { Authorization: `Bearer ${jwt}` })
              },
              body: JSON.stringify({
                workspaceId: currentWorkspace.id,
                userId: user.id,
                appId: targetAppId
              }),
              credentials: "include"
            })
              .then((res) => res.json())
              .then((data) => {
                if (data.token) {
                  // Responde em ambos os protocolos para compatibilidade total (SDK + Bold Agents / Custom Apps)
                  sendResponse("BOLD_SSO_TOKEN_RESPONSE", data.token)
                  if (sourceWindow) {
                    try {
                      sourceWindow.postMessage(
                        {
                          type: "BOLD_WORKSPACE_SSO",
                          ssoToken: data.token,
                          token: data.token,
                          payload: data.token
                        },
                        "*"
                      )
                      sourceWindow.postMessage(
                        {
                          type: "BOLD_SSO_TOKEN_RESPONSE",
                          payload: data.token
                        },
                        "*"
                      )
                    } catch (e) { }
                  }
                } else {
                  sendResponse("BOLD_ERROR", null, data.message || "SSO token generation failed.")
                }
              })
              .catch((err) => {
                sendResponse("BOLD_ERROR", null, String(err))
              })
            break
          }

          case "BOLD_SET_TITLE": {
            const payload = msg.payload as { title?: string } | undefined
            if (payload?.title) {
              const tabForTitle = openApps.find((t) => t.instanceId === activeAppId)
              if (tabForTitle) {
                setOpenApps((prev) =>
                  prev.map((t) =>
                    t.instanceId === tabForTitle.instanceId
                      ? { ...t, app: { ...t.app, name: payload.title! } }
                      : t
                  )
                )
              }
            }
            break
          }

          case "BOLD_SEND_NOTIFICATION":
          case "BOLD_WIDGET_SEND_NOTIFICATION":
          case "BOLD_TOAST_SHOW":
          case "BOLD_NOTIFY":
          case "SHOW_TOAST": {
            const payload = msg.payload as {
              title?: string
              text?: string
              message?: string
              description?: string
              type?: string
              variant?: string
              toastType?: string
              action?: { label: string; url?: string; appId?: string; target?: string }
              avatarUrl?: string
              iconUrl?: string
              sound?: boolean
              silent?: boolean
              playSound?: boolean
              hasSound?: boolean
              temporary?: boolean
              isTemporary?: boolean
              persist?: boolean
              saveToDb?: boolean
              duration?: number
            } | undefined

            const title = payload?.title || payload?.message || "Notificação"
            const description = payload?.description || payload?.text || ""
            const variant = (payload?.variant || payload?.type || payload?.toastType || "info").toLowerCase()
            const action = payload?.action

            const isSilent = payload?.silent === true || payload?.sound === false || payload?.playSound === false || payload?.hasSound === false
            const shouldPlaySound = !isSilent

            const isTemporary = payload?.temporary === true || payload?.isTemporary === true || payload?.persist === false || payload?.saveToDb === false

            const sendingApp =
              openApps.find((t) => {
                const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
                return iframe && iframe.contentWindow === sourceWindow
              }) || openApps.find((t) => t.instanceId === activeAppId)

            const appName = sendingApp?.app?.name || "App"
            const appIcon = payload?.avatarUrl || payload?.iconUrl || sendingApp?.app?.iconUrl || null

            // 1. Registra no Painel de Notificações com Suporte a Call To Action (CTA) & Redirecionamento
            const newNotif: NotificationItem = {
              id: Date.now(),
              type: variant as any,
              title: title,
              text: description || title,
              time: "agora",
              avatarUrl: appIcon,
              appIcon: appIcon,
              appName: appName,
              appId: sendingApp?.instanceId || sendingApp?.app?.id,
              action: action ? { label: action.label, url: action.url, appId: action.appId, target: action.target } : undefined
            }
            setNotifications((prev) => [newNotif, ...prev])

            // Persiste no banco de dados via GraphQL (apenas se NÃO for temporário)
            if (!isTemporary) {
              const authToken = getAuthToken()
              if (authToken) {
                fetch("/api/graphql", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`
                  },
                  body: JSON.stringify({
                    query: `
                      mutation CreateNotification($input: CreateNotificationInput!) {
                        createNotification(input: $input) { id }
                      }
                    `,
                    variables: {
                      input: {
                        workspaceId: null,
                        appName: appName,
                        type: variant,
                        title: title,
                        text: description || title,
                        avatarUrl: appIcon || null,
                        appIcon: appIcon || null,
                        action: action ? { label: action.label, url: action.url || null, appId: action.appId || null, target: action.target || null } : null
                      }
                    }
                  })
                }).catch(() => { })
              }
            }

            // Opções do Toast com suporte a botão de Ação (CTA), Duração e Modos de Redirecionamento (same_tab, new_tab, app_tab)
            const toastOptions: any = {
              description,
              ...(payload?.duration && { duration: payload.duration }),
              ...(action?.label && {
                action: {
                  label: action.label,
                  onClick: () => {
                    if (!action) return
                    const targetMode = (action.target || 'app_tab').toLowerCase()
                    const url = action.url

                    if (targetMode === 'same_tab' || targetMode === '_self' || targetMode === 'self') {
                      if (url) window.location.href = url
                    } else if (targetMode === 'new_tab' || targetMode === '_blank' || targetMode === 'blank') {
                      if (url) handleOpenExternalUrl(url)
                    } else {
                      // 'app_tab' / 'app': Ativa a guia do app e navega o iframe sem recarregar a página principal
                      const targetAppId = action.appId || sendingApp?.instanceId || sendingApp?.app?.id
                      if (targetAppId) {
                        setActiveAppId(targetAppId)
                      }
                      if (url) {
                        setTimeout(() => {
                          const iframe = (
                            (targetAppId ? document.querySelector(`iframe[data-instance-id="${targetAppId}"], iframe[data-app-id="${targetAppId}"]`) : null) ||
                            document.querySelector(`iframe`)
                          ) as HTMLIFrameElement | null

                          if (iframe) {
                            if (iframe.contentWindow) {
                              try {
                                iframe.contentWindow.postMessage({ type: 'BOLD_NAVIGATE', payload: { path: url } }, '*')
                              } catch (err) { }
                            }
                            if (iframe.src !== url) {
                              iframe.src = url
                            }
                          }
                        }, 50)
                      }
                    }
                  }
                }
              })
            }

            // 2. Dispara o Toast Flutuante com a variante correta (success, info, warning, error, loading)
            if (variant === "success") {
              toast.success(`${appName}: ${title}`, toastOptions)
            } else if (variant === "warning") {
              toast.warning(`${appName}: ${title}`, toastOptions)
            } else if (variant === "error") {
              toast.error(`${appName}: ${title}`, toastOptions)
            } else if (variant === "loading") {
              toast.loading(`${appName}: ${title}`, toastOptions)
            } else {
              toast.info(`${appName}: ${title}`, toastOptions)
            }

            if (shouldPlaySound) {
              playNotificationSound()
            }
            sendResponse("BOLD_NOTIFICATION_RESPONSE", { success: true, notifId: newNotif.id })
            break
          }

          case "BOLD_SHOW_LOADING": {
            const payload = msg.payload as { show: boolean } | undefined
            setLoading(payload?.show ?? true)
            break
          }

          case "BOLD_OPEN_TAB":
          case "BOLD_OPEN_NEW_TAB":
          case "OPEN_NEW_TAB":
          case "BOLD_OPEN_EXTERNAL_URL":
          case "BOLD_OPEN_EXTERNAL":
          case "BOLD_OPEN_URL":
          case "OPEN_URL":
          case "OPEN_EXTERNAL_LINK": {
            const url = (msg.payload as any)?.url || (msg.payload as any)?.path || (typeof msg.payload === "string" ? msg.payload : "")
            if (url && typeof url === "string") {
              const sendingTab =
                openApps.find((t) => {
                  const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
                  return iframe && iframe.contentWindow === sourceWindow
                }) || openApps.find((t) => t.instanceId === activeAppId)
              const sourceBaseUrl = sendingTab?.baseIframeUrl || sendingTab?.app?.iframeUrl || (event.origin && event.origin !== "null" ? event.origin : undefined)
              handleOpenExternalUrl(url, {
                forceNewTab: true,
                inBackground: Boolean((msg.payload as any)?.inBackground),
                openInSplit: Boolean((msg.payload as any)?.openInSplit),
                sourceBaseUrl
              })
            }
            break
          }

          case "BOLD_LINK_CONTEXT_MENU": {
            const payload = msg.payload as any
            const url = payload?.url || payload?.href
            if (url && typeof url === "string") {
              const sendingTab =
                openApps.find((t) => {
                  const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
                  return iframe && iframe.contentWindow === sourceWindow
                }) || openApps.find((t) => t.instanceId === activeAppId)
              const iframeEl = sendingTab
                ? (document.querySelector(`iframe[data-instance-id="${sendingTab.instanceId}"]`) as HTMLIFrameElement | null)
                : null
              const rect = iframeEl?.getBoundingClientRect()
              const sourceBaseUrl = sendingTab?.baseIframeUrl || sendingTab?.app?.iframeUrl || (event.origin && event.origin !== "null" ? event.origin : undefined)
              window.dispatchEvent(
                new CustomEvent("bold:link-context-menu", {
                  detail: {
                    url,
                    text: payload?.text || "",
                    x: (rect?.left || 0) + (typeof payload?.clientX === "number" ? payload.clientX : typeof payload?.x === "number" ? payload.x : 200),
                    y: (rect?.top || 0) + (typeof payload?.clientY === "number" ? payload.clientY : typeof payload?.y === "number" ? payload.y : 200),
                    sourceBaseUrl
                  }
                })
              )
            }
            break
          }

          case "BOLD_ROUTE_CHANGED":
          case "BOLD_PATH_CHANGED":
          case "BOLD_NAVIGATE":
          case "BOLD_SET_ROUTE":
          case "ROUTE_CHANGED":
          case "ROUTE_CHANGE":
          case "NAVIGATE":
          case "NAVIGATION":
          case "PATH_CHANGED":
          case "LOCATION_CHANGE": {
            const potentialUrl = (msg.payload as any)?.url || (msg.payload as any)?.path || (msg as any).path
            const isExternal = (msg.payload as any)?.external === true || (typeof potentialUrl === "string" && (potentialUrl.startsWith("http://") || potentialUrl.startsWith("https://") || potentialUrl.startsWith("//")))
            if (isExternal && potentialUrl && typeof potentialUrl === "string") {
              const sendingTab =
                openApps.find((t) => {
                  const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
                  return iframe && iframe.contentWindow === sourceWindow
                }) || openApps.find((t) => t.instanceId === activeAppId)
              const sourceBaseUrl = sendingTab?.baseIframeUrl || sendingTab?.app?.iframeUrl || (event.origin && event.origin !== "null" ? event.origin : undefined)
              handleOpenExternalUrl(potentialUrl, { forceNewTab: true, sourceBaseUrl })
              break
            }

            const rawPath = extractSubpathFromMessage(msg)
            if (rawPath) {
              const targetTab =
                openApps.find((t) => {
                  const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
                  return iframe && iframe.contentWindow === sourceWindow
                }) || openApps.find((t) => t.instanceId === activeAppId)

              if (targetTab) {
                updateAppSubpath(targetTab.instanceId, rawPath)
              }
            }
            break
          }

          case "BOLD_GET_ROUTE":
          case "BOLD_REQUEST_ROUTE": {
            const targetTab =
              openApps.find((t) => {
                const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
                return iframe && iframe.contentWindow === sourceWindow
              }) || openApps.find((t) => t.instanceId === activeAppId)

            if (targetTab) {
              const currentSub = iframeSubpaths.current[targetTab.instanceId] || targetTab.subpath || ""
              sendResponse("BOLD_ROUTE_RESPONSE", { path: currentSub, subpath: currentSub })
            }
            break
          }

          default:
            break
        }
      } else if (event.data) {
        const d = event.data as any
        if (typeof d === "object" && (d.type === "GET_USERS" || d.type === "REQUEST_USERS" || d.type === "BOLD_GET_USERS" || d.type === "BOLD_GET_ACCOUNT_USERS")) {
          const sourceWindow = event.source as Window | null
          const available = getAccountUsersList()
          sourceWindow?.postMessage({
            type: "BOLD_USERS_RESPONSE",
            requestId: d.requestId,
            payload: available,
            users: available
          }, "*")
          return
        }

        if (typeof d === "object" && (d.type === "GET_WORKSPACES" || d.type === "REQUEST_WORKSPACES" || d.type === "BOLD_GET_WORKSPACES" || d.type === "BOLD_GET_ACCOUNT_WORKSPACES")) {
          const sourceWindow = event.source as Window | null
          const available = getUserWorkspacesList()
          sourceWindow?.postMessage({
            type: "BOLD_WORKSPACES_RESPONSE",
            requestId: d.requestId,
            payload: available,
            workspaces: available
          }, "*")
          return
        }

        const rawPath = extractSubpathFromMessage(event.data)
        if (rawPath) {
          const sourceWindow = event.source as Window | null
          const targetTab =
            openApps.find((t) => {
              const iframe = document.querySelector(`iframe[data-instance-id="${t.instanceId}"]`) as HTMLIFrameElement
              return iframe && iframe.contentWindow === sourceWindow
            }) || openApps.find((t) => t.instanceId === activeAppId)

          if (targetTab) {
            updateAppSubpath(targetTab.instanceId, rawPath)
          }
        }
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [user, currentWorkspace, currentAccount, accounts, lang, openApps, activeAppId, playNotificationSound, iframeSubpaths, updateAppSubpath, handleOpenExternalUrl])

  const [linkContextMenu, setLinkContextMenu] = React.useState<{
    x: number
    y: number
    url: string
    text?: string
    sourceBaseUrl?: string
  } | null>(null)

  // Intercepta cliques, clique do meio (auxclick), botão direito (contextmenu) em links e window.open em qualquer tela do Workspace
  React.useEffect(() => {
    const resolveAnchorUrl = (anchor: HTMLAnchorElement, rawHref: string) => {
      try {
        return anchor.href || new URL(rawHref, window.location.href).href
      } catch {
        return rawHref
      }
    }

    const handleGlobalClick = (e: MouseEvent) => {
      const targetEl = e.target as HTMLElement | null
      if (targetEl && !targetEl.closest("#bold-link-context-menu")) {
        setLinkContextMenu(null)
      }

      const anchor = targetEl?.closest("a") as HTMLAnchorElement | null
      if (!anchor) return

      const rawHref = anchor.getAttribute("href")
      if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) return

      const fullHref = resolveAnchorUrl(anchor, rawHref)
      const targetAttr = (anchor.getAttribute("target") || "").toLowerCase()
      const isNewTarget = targetAttr === "_blank" || targetAttr === "_top" || targetAttr === "_parent"
      const hasModifier = e.ctrlKey || e.metaKey || e.shiftKey
      const isExternalUrl = rawHref.startsWith("http://") || rawHref.startsWith("https://") || rawHref.startsWith("//")
      const isCurrentOrigin = typeof window !== "undefined" && fullHref.startsWith(window.location.origin)

      if (isNewTarget || hasModifier || (isExternalUrl && !isCurrentOrigin)) {
        e.preventDefault()
        e.stopPropagation()
        handleOpenExternalUrl(fullHref, { forceNewTab: true })
      }
    }

    const handleGlobalMouseDown = (e: MouseEvent) => {
      if (e.button !== 1) return
      const anchor = (e.target as HTMLElement)?.closest("a") as HTMLAnchorElement | null
      if (!anchor) return
      const rawHref = anchor.getAttribute("href")
      if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#")) return
      e.preventDefault()
    }

    const handleGlobalAuxClick = (e: MouseEvent) => {
      if (e.button !== 1) return
      const anchor = (e.target as HTMLElement)?.closest("a") as HTMLAnchorElement | null
      if (!anchor) return
      const rawHref = anchor.getAttribute("href")
      if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) return

      const fullHref = resolveAnchorUrl(anchor, rawHref)
      e.preventDefault()
      e.stopPropagation()
      handleOpenExternalUrl(fullHref, { forceNewTab: true })
    }

    const handleGlobalContextMenu = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest("a") as HTMLAnchorElement | null
      if (!anchor) {
        setLinkContextMenu(null)
        return
      }
      const rawHref = anchor.getAttribute("href")
      if (!rawHref || rawHref.startsWith("javascript:") || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) return

      const fullHref = resolveAnchorUrl(anchor, rawHref)
      e.preventDefault()
      e.stopPropagation()
      setLinkContextMenu({
        x: e.clientX,
        y: e.clientY,
        url: fullHref,
        text: (anchor.textContent || "").trim()
      })
    }

    const handleCustomLinkMenu = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail?.url) return
      setLinkContextMenu({
        x: detail.x,
        y: detail.y,
        url: detail.url,
        text: detail.text || "",
        sourceBaseUrl: detail.sourceBaseUrl
      })
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setLinkContextMenu(null)
      }
    }

    document.addEventListener("click", handleGlobalClick, true)
    document.addEventListener("mousedown", handleGlobalMouseDown, true)
    document.addEventListener("auxclick", handleGlobalAuxClick, true)
    document.addEventListener("contextmenu", handleGlobalContextMenu, true)
    window.addEventListener("bold:link-context-menu", handleCustomLinkMenu)
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("click", handleGlobalClick, true)
      document.removeEventListener("mousedown", handleGlobalMouseDown, true)
      document.removeEventListener("auxclick", handleGlobalAuxClick, true)
      document.removeEventListener("contextmenu", handleGlobalContextMenu, true)
      window.removeEventListener("bold:link-context-menu", handleCustomLinkMenu)
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleOpenExternalUrl])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    const origOpen = window.open
    const createWindowShim = () => {
      const locProxy = {
        _href: "",
        get href() {
          return this._href
        },
        set href(val: string) {
          this._href = val
          if (val && val !== "about:blank") {
            handleOpenExternalUrl(String(val), { forceNewTab: true })
          }
        },
        assign(val: string) {
          if (val && val !== "about:blank") {
            handleOpenExternalUrl(String(val), { forceNewTab: true })
          }
        },
        replace(val: string) {
          if (val && val !== "about:blank") {
            handleOpenExternalUrl(String(val), { forceNewTab: true })
          }
        }
      }
      const shim = {
        closed: false,
        focus() {},
        close() { shim.closed = true },
        postMessage() {},
        location: locProxy
      }
      return shim as unknown as Window
    }

    window.open = function (url?: string | URL, _target?: string, _features?: string) {
      const strUrl = url != null ? String(url).trim() : ""
      if (strUrl && strUrl !== "about:blank" && !strUrl.startsWith("javascript:")) {
        handleOpenExternalUrl(strUrl, { forceNewTab: true })
      }
      return createWindowShim()
    }
    return () => {
      window.open = origOpen
    }
  }, [handleOpenExternalUrl])

  // Handoff entre abas do navegador: caso uma nova guia do navegador seja aberta a partir do Bold Workspace
  // (ex: "Abrir link em uma nova guia" nativo do navegador), transfere a URL para a instância principal ativa do Bold Workspace
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return

    const channel = new BroadcastChannel("bold_workspace_tab_handoff")
    const winId = Math.random().toString(36).substring(2, 10)
    const isAlreadyEstablished = sessionStorage.getItem("bold_tab_established") === "1"

    const updatePrimaryHeartbeat = () => {
      try {
        localStorage.setItem(
          "bold_workspace_active_window",
          JSON.stringify({ id: winId, ts: Date.now() })
        )
      } catch {}
    }

    // Se esta guia acabou de ser aberta (não estabelecida na sessão) e há uma janela principal ativa recentemente
    // com window.opener ou interação recente, transfere a abertura para uma nova guia dentro do Bold Workspace principal
    if (!isAlreadyEstablished) {
      try {
        const rawActive = localStorage.getItem("bold_workspace_active_window")
        const parsedActive = rawActive ? JSON.parse(rawActive) : null
        const hasRecentPrimary = parsedActive && parsedActive.id !== winId && Date.now() - parsedActive.ts < 6000
        const hasOpenerOrReferrer = Boolean(window.opener) || (Boolean(document.referrer) && document.referrer.startsWith(window.location.origin))

        if (hasRecentPrimary && hasOpenerOrReferrer && window.history.length <= 2) {
          const currentFullUrl = window.location.href
          const onAck = (ev: MessageEvent) => {
            if (ev.data?.type === "BOLD_HANDOFF_ACK" && ev.data?.senderId === winId) {
              channel.removeEventListener("message", onAck)
              try {
                window.close()
              } catch {}
            }
          }
          channel.addEventListener("message", onAck)
          channel.postMessage({
            type: "BOLD_HANDOFF_NEW_TAB",
            senderId: winId,
            url: currentFullUrl
          })
        }
      } catch {}
      sessionStorage.setItem("bold_tab_established", "1")
    }

    updatePrimaryHeartbeat()
    const heartbeatInterval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        updatePrimaryHeartbeat()
      }
    }, 2500)

    window.addEventListener("mousedown", updatePrimaryHeartbeat, true)
    window.addEventListener("contextmenu", updatePrimaryHeartbeat, true)
    window.addEventListener("focus", updatePrimaryHeartbeat)
    window.addEventListener("blur", updatePrimaryHeartbeat)

    const handleChannelMessage = (ev: MessageEvent) => {
      const data = ev.data
      if (!data || data.senderId === winId) return
      if (data.type === "BOLD_HANDOFF_NEW_TAB" && typeof data.url === "string") {
        channel.postMessage({
          type: "BOLD_HANDOFF_ACK",
          senderId: data.senderId
        })
        handleOpenExternalUrl(data.url, { forceNewTab: true })
        try {
          window.focus()
        } catch {}
      }
    }

    channel.addEventListener("message", handleChannelMessage)

    return () => {
      window.clearInterval(heartbeatInterval)
      window.removeEventListener("mousedown", updatePrimaryHeartbeat, true)
      window.removeEventListener("contextmenu", updatePrimaryHeartbeat, true)
      window.removeEventListener("focus", updatePrimaryHeartbeat)
      window.removeEventListener("blur", updatePrimaryHeartbeat)
      channel.removeEventListener("message", handleChannelMessage)
      channel.close()
    }
  }, [handleOpenExternalUrl])

  // System Setup & Initial Fetch Effects
  React.useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]

    if (!token) {
      router.push("/login")
      return
    }

    loadDashboardData(token)
  }, [loadDashboardData, router])

  React.useEffect(() => {
    setIsMac(/Mac|iPhone|iPod|iPad/i.test(navigator.userAgent || navigator.platform || ""))
  }, [])

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  React.useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      const langLocale = lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR"

      const is12h = topBarConfig.timeFormat === "12h"
      setCurrentTime(
        now.toLocaleTimeString(langLocale, {
          hour: is12h ? "numeric" : "2-digit",
          minute: "2-digit",
          hour12: is12h
        })
      )

      if (topBarConfig.dateFormat === "short") {
        setCurrentDate(
          now.toLocaleDateString(langLocale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          })
        )
      } else {
        setCurrentDate(
          now.toLocaleDateString(langLocale, {
            weekday: "long",
            day: "numeric",
            month: "long"
          })
        )
      }
    }
    updateTime()
    const timer = setInterval(updateTime, 1000)
    return () => clearInterval(timer)
  }, [lang, topBarConfig.timeFormat, topBarConfig.dateFormat])

  // Alarme e Temporizador Runner
  React.useEffect(() => {
    const playTimerDoneSound = () => {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
        if (!AudioContextClass) return
        const audioCtx = new AudioContextClass()
        if (audioCtx.state === "suspended") {
          audioCtx.resume().catch(() => {})
        }
        const notes = [523.25, 659.25, 783.99] // C5, E5, G5
        notes.forEach((freq, idx) => {
          const osc = audioCtx.createOscillator()
          const gain = audioCtx.createGain()
          osc.type = "sine"
          osc.frequency.setValueAtTime(freq, audioCtx.currentTime + idx * 0.18)
          gain.gain.setValueAtTime(0.35, audioCtx.currentTime + idx * 0.18)
          gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + idx * 0.18 + 0.4)
          osc.connect(gain)
          gain.connect(audioCtx.destination)
          osc.start(audioCtx.currentTime + idx * 0.18)
          osc.stop(audioCtx.currentTime + idx * 0.18 + 0.4)
        })
      } catch (e) {}
    }

    const playAlarmSound = () => {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
        if (!AudioContextClass) return
        const audioCtx = new AudioContextClass()
        if (audioCtx.state === "suspended") {
          audioCtx.resume().catch(() => {})
        }
        const osc = audioCtx.createOscillator()
        osc.type = "sine"
        osc.frequency.setValueAtTime(440, audioCtx.currentTime)
        osc.connect(audioCtx.destination)
        osc.start()
        osc.stop(audioCtx.currentTime + 1)
      } catch (e) {}
    }

    const interval = setInterval(() => {
      const now = new Date()
      const nowStr = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })

      // 1. Verificar Alarmes
      alarms.forEach((alarm) => {
        if (alarm.enabled && alarm.time === nowStr) {
          const alarmKey = `fired:${alarm.id}:${now.getMinutes()}`
          try {
            if (!sessionStorage.getItem(alarmKey)) {
              sessionStorage.setItem(alarmKey, "true")
              toast.success(t?.alarmTriggered || "Alarme Disparado!", {
                description: alarm.label || "Despertador",
                duration: 10000,
              })
              playAlarmSound()
            }
          } catch (e) {}
        }
      })

      // 2. Controlar Temporizador
      setTimerState((prev) => {
        if (prev.isRunning && prev.remaining > 0) {
          const nextRemaining = prev.remaining - 1
          if (nextRemaining === 0) {
            toast.success(t?.timerTriggered || "Temporizador Concluído!", {
              description: t?.timerTriggered || "Tempo esgotado!",
              duration: 8000,
            })
            playTimerDoneSound()
            return { ...prev, isRunning: false, remaining: 0 }
          }
          return { ...prev, remaining: nextRemaining }
        }
        return prev
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [alarms, t])

  // Floating Widgets Handlers
  const handleToggleFloatingWidget = (widgetId: string) => {
    const isFloating = activeFloatingWidgets.includes(widgetId)
    const isPinned = pinnedWidgets.includes(widgetId)
    if (isFloating || isPinned) {
      setActiveFloatingWidgets((prev) => prev.filter((id) => id !== widgetId))
      setPinnedWidgets((prev) => prev.filter((id) => id !== widgetId))
      setMinimizedWidgets((prev) => {
        const n = new Set(prev)
        n.delete(widgetId)
        return n
      })
      if (calcFocusedWidgetId === widgetId) setCalcFocusedWidgetId(null)
    } else {
      if (activeFloatingWidgets.length >= 4) {
        toast.error("Limite máximo de 4 widgets flutuantes atingido!")
        return
      }
      const offset = activeFloatingWidgets.length * 25
      const initialX = Math.max(50, 300 + offset)
      const initialY = Math.max(106, 180 + offset)

      setActiveFloatingWidgets((prev) => [...prev, widgetId])
      setFloatingPositions((prev) => {
        let highestZ = 15
        for (const key of Object.keys(prev)) {
          const valZ = prev[key]?.z
          if (typeof valZ === "number" && valZ > highestZ && valZ <= 35) {
            highestZ = valZ
          }
        }
        return {
          ...prev,
          [widgetId]: { x: initialX, y: initialY, z: highestZ + 1 }
        }
      })
    }
  }

  const handleFocusFloatingWidget = (widgetId: string) => {
    setFloatingPositions((prev) => {
      const current = { ...prev }
      let highestZ = 15
      for (const key of Object.keys(current)) {
        const valZ = current[key]?.z
        if (typeof valZ === "number" && valZ > highestZ && valZ <= 35) {
          highestZ = valZ
        }
      }

      const currentWidgetZ = current[widgetId]?.z || 15
      // Se já é o topo e mais de 1 widget existe com z inferior, mantém
      if (currentWidgetZ === highestZ && Object.keys(current).length > 1) {
        return prev
      }

      if (highestZ >= 32) {
        const keys = Object.keys(current).filter((k) => k !== widgetId)
        keys.sort((a, b) => (current[a]?.z || 15) - (current[b]?.z || 15))
        const reindexed: Record<string, { x: number; y: number; z: number }> = {}
        keys.forEach((k, idx) => {
          reindexed[k] = { ...current[k], z: 15 + idx }
        })
        reindexed[widgetId] = {
          ...(current[widgetId] || { x: 200, y: 150 }),
          z: 15 + keys.length
        }
        return reindexed
      }

      return {
        ...current,
        [widgetId]: {
          ...(current[widgetId] || { x: 200, y: 150 }),
          z: highestZ + 1
        }
      }
    })
  }

  const handleDragWidget = (widgetId: string, x: number, y: number) => {
    setFloatingPositions((prev) => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], x, y }
    }))
  }

  const handleMinimizeWidget = (widgetId: string) => {
    setMinimizedWidgets((prev) => {
      const next = new Set(prev)
      if (next.has(widgetId)) {
        next.delete(widgetId)
      } else {
        next.add(widgetId)
      }
      return next
    })
  }

  const handlePinWidget = React.useCallback((widgetId: string) => {
    setPinnedWidgets((prev) => {
      const isPinned = prev.includes(widgetId)
      if (isPinned) {
        // Desfixar do painel -> retornar para janela flutuante
        setActiveFloatingWidgets((floating) => (floating.includes(widgetId) ? floating : [...floating, widgetId]))
        return prev.filter((id) => id !== widgetId)
      } else {
        // Fixar no painel -> remover de janelas flutuantes
        setActiveFloatingWidgets((floating) => floating.filter((id) => id !== widgetId))
        return [...prev, widgetId]
      }
    })
  }, [])

  // Responsive Desktop Grid Helpers
  const getCellSize = React.useCallback(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1280
    const h = typeof window !== "undefined" ? window.innerHeight : 800
    const isCompact = w < 1024
    const maxCols = isCompact ? Math.min(6, desktopGridCols || 12) : (desktopGridCols || 12)
    const maxRows = 5
    const availW = Math.max(320, w - 24)
    const availH = Math.max(320, h - 160)
    const cellW = Math.floor(availW / maxCols)
    const cellH = Math.floor(availH / maxRows)
    return Math.max(70, Math.min(140, Math.min(cellW, cellH)))
  }, [desktopGridCols])

  const getDesktopIconConfig = React.useCallback(() => {
    const size = getCellSize()
    return {
      cellSize: size,
      cardClass: "w-24 h-24 p-2 rounded-2xl",
      iconSize: size < 90 ? ("medium" as const) : ("large" as const),
      iconScaleClass: size < 90 ? "scale-90" : "",
      textClass: "text-[10px] mt-1"
    }
  }, [getCellSize])

  // Drag & Drop Desktop Icons & Layout Persistence
  const handleSaveDesktopLayoutToDb = async (
    newPositions: Record<string, { col: number; row: number }>,
    newWidgets?: any[],
    newWidgetPositions?: any
  ) => {
    saveWorkspacePreferences({
      appPositions: newPositions,
      desktopWidgets: newWidgets || desktopWidgets,
      desktopWidgetPositions: newWidgetPositions || desktopWidgetPositions
    })
  }

  const handleAppIconDragStart = (e: React.DragEvent, appId: string) => {
    e.dataTransfer.setData("text/plain", appId)
    const rect = e.currentTarget.getBoundingClientRect()
    const offsetX = e.clientX - rect.left
    const offsetY = e.clientY - rect.top
    e.dataTransfer.setData("application/json", JSON.stringify({ offsetX, offsetY }))
    e.dataTransfer.effectAllowed = "move"
    if (e.dataTransfer.setDragImage) {
      e.dataTransfer.setDragImage(e.currentTarget as HTMLElement, offsetX, offsetY)
    }
    setDraggedAppId(appId)
  }

  const handleDesktopDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const dataId = e.dataTransfer.getData("text/plain")
    const metadataStr = e.dataTransfer.getData("application/json")
    if (!dataId) return

    let offsetX = 20
    let offsetY = 20
    if (metadataStr) {
      try {
        const parsed = JSON.parse(metadataStr)
        if (parsed.offsetX !== undefined) offsetX = parsed.offsetX
        if (parsed.offsetY !== undefined) offsetY = parsed.offsetY
      } catch { }
    }

    const isCompact = windowSize.width < 1024
    const maxCols = isCompact ? Math.min(6, desktopGridCols || 12) : (desktopGridCols || 12)
    const maxRows = 5

    const gridOffsetX = 12
    const gridOffsetY = 24
    const availW = windowSize.width - 2 * gridOffsetX
    const cellW = availW / maxCols
    const availH = windowSize.height - 84 - 76 - gridOffsetY - 16
    const cellH = Math.floor(availH / maxRows)

    const containerRect = e.currentTarget.getBoundingClientRect()
    const relativeX = e.clientX - containerRect.left - gridOffsetX - offsetX
    const relativeY = e.clientY - containerRect.top - gridOffsetY - offsetY

    // Determinar colSpan e rowSpan do widget ou app
    let colSpan = 1
    let rowSpan = 1

    if (dataId.startsWith("widget:")) {
      const wid = dataId.replace("widget:", "")
      const wObj = widgetsConfig.find((w) => w.id === wid)
      const dim = wObj?.dimension || "small"
      if (dim === "medium") {
        colSpan = 2
        rowSpan = 2
      } else if (dim === "large") {
        colSpan = 4
        rowSpan = 3
      }
    }

    let col = Math.max(0, Math.round(relativeX / cellW))
    let row = Math.max(0, Math.round(relativeY / cellH))

    // Garantir que o widget/app nao vaze as bordas direita e inferior da tela
    col = Math.min(Math.max(0, maxCols - colSpan), col)
    row = Math.min(Math.max(0, maxRows - rowSpan), row)

    const newPositions = {
      ...appPositions,
      [dataId]: { col, row }
    }

    setAppPositions(newPositions)

    if (user && currentWorkspace) {
      localStorage.setItem(
        `appPositions:${user.id}:${currentWorkspace.id}`,
        JSON.stringify(newPositions)
      )
    }
    handleSaveDesktopLayoutToDb(newPositions)
    setDraggedAppId(null)
  }

  // Simple Action Handlers
  const handleDockAppClick = (appInput: any) => {
    const targetApp = appInput?.app ? appInput.app : appInput
    const appId = targetApp?.id || (typeof targetApp === "string" ? targetApp : "")

    if (appId === "settings" || targetApp === "settings") {
      handleOpenSettingsTab()
      return
    }
    if (appId === "profile" || targetApp === "profile") {
      handleOpenProfileTab()
      return
    }
    if (appId === "marketplace" || targetApp === "marketplace") {
      handleOpenMarketplaceTab()
      return
    }

    if (typeof targetApp === "string") {
      const found = installedApps.find((ia) => ia.app.id === targetApp || ia.app.slug === targetApp)?.app
      if (found) {
        handleOpenApp(found)
      } else {
        handleOpenApp({ id: targetApp, name: targetApp, slug: targetApp, description: "", iconUrl: "", iframeUrl: "" })
      }
    } else if (targetApp && typeof targetApp === "object") {
      handleOpenApp(targetApp)
    }
  }
  const handleDockDragStart = (e: React.DragEvent, id: string) => { }
  const handleDesktopContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, visible: true })
  }
  const handleRenameAppClick = (id: string) => setRenamingAppId(id)
  const handleSaveRenameApp = () => setRenamingAppId(null)

  const handleRenameApp = async (appId: string, newName: string) => {
    if (!newName.trim()) return
    const trimmed = newName.trim()

    // 1. Atualizar installedApps no estado
    setInstalledApps((prev: any[]) =>
      prev.map((item: any) =>
        item.app.id === appId
          ? { ...item, app: { ...item.app, name: trimmed } }
          : item
      )
    )

    // 2. Atualizar openApps se estiver aberto
    setOpenApps((prev: any[]) =>
      prev.map((tab: any) =>
        tab.app.id === appId
          ? { ...tab, app: { ...tab.app, name: trimmed } }
          : tab
      )
    )

    // 3. Atualizar mapa local de renomeações
    setRenamedAppNames((prev) => {
      const next = { ...prev, [appId]: trimmed }
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(`renamedAppNames:${user?.id || "default"}`, JSON.stringify(next))
          localStorage.setItem("bold_renamed_apps", JSON.stringify(next))
        } catch (e) { }
      }
      return next
    })

    // 4. Persistir no banco de dados via GraphQL para que todos os membros do workspace vejam
    try {
      const token = getAuthToken()
      if (token && currentWorkspace?.id) {
        const targetInstalled = installedApps.find((ia: any) => ia.app.id === appId)
        let currentConfigObj: Record<string, any> = {}
        if (targetInstalled?.configuration) {
          try {
            currentConfigObj =
              typeof targetInstalled.configuration === "string"
                ? JSON.parse(targetInstalled.configuration)
                : targetInstalled.configuration
          } catch (e) { }
        }
        currentConfigObj.customName = trimmed

        await fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              mutation UpdateInstalledAppConfiguration($workspaceId: ID!, $appId: ID!, $configuration: String!) {
                updateInstalledAppConfiguration(workspaceId: $workspaceId, appId: $appId, configuration: $configuration) {
                  id
                  configuration
                }
              }
            `,
            variables: {
              workspaceId: currentWorkspace.id,
              appId,
              configuration: JSON.stringify(currentConfigObj)
            }
          })
        })
      }
    } catch (err) {
      console.error("Erro ao salvar configuração de renomeação do aplicativo no workspace:", err)
    }

    toast.success("Nome do aplicativo atualizado", {
      description: `O aplicativo agora será exibido como "${trimmed}" para todos os membros deste workspace.`
    })
  }
  const handleSaveWallpaperToDb = async (desktop?: string, mobile?: string, separate?: boolean) => {
    const desktopUrl = desktop ?? desktopWallpaper
    const mobileUrl = mobile ?? mobileWallpaper
    const sepVal = separate ?? useSeparateMobileWallpaper

    if (desktop) setDesktopWallpaper(desktop)
    if (mobile) setMobileWallpaper(mobile)
    if (separate !== undefined) setUseSeparateMobileWallpaper(separate)

    saveWorkspacePreferences({
      desktopWallpaper: desktopUrl,
      mobileWallpaper: mobileUrl,
      useSeparateMobileWallpaper: sepVal
    })

    if (typeof window !== "undefined") {
      try {
        if (desktopUrl) {
          localStorage.setItem("desktopWallpaper", desktopUrl)
          if (user) localStorage.setItem(`desktopWallpaper:${user.id}`, desktopUrl)
        }
        if (mobileUrl) {
          localStorage.setItem("mobileWallpaper", mobileUrl)
          if (user) localStorage.setItem(`mobileWallpaper:${user.id}`, mobileUrl)
        }
        localStorage.setItem("useSeparateMobileWallpaper", String(sepVal))
      } catch (e) { }
    }
  }
  const handleToggleAppStatus = (appId: string) => {
    let newStatus = "active"
    setInstalledApps((prev) => {
      const next = prev.map((item) => {
        if (item.app.id === appId) {
          newStatus = item.status === "active" ? "inactive" : "active"
          return { ...item, status: newStatus }
        }
        return item
      })
      if (typeof window !== "undefined") {
        try {
          const statuses = next.map((ia) => ({ id: ia.app.id, status: ia.status }))
          if (currentWorkspace?.id) {
            localStorage.setItem(`bold_installed_apps_status:${currentWorkspace.id}`, JSON.stringify(statuses))
          }
          localStorage.setItem("bold_installed_apps_status", JSON.stringify(statuses))
        } catch (e) { }
      }
      return next
    })

    if (newStatus === "inactive") {
      setOpenApps((prev) => prev.filter((tab) => tab.app.id !== appId))
      setDockAppIds((prev) => prev.filter((id) => id !== appId))
      toast.success("Aplicativo desativado do workspace!")
    } else {
      setHiddenAppIds((prev) => prev.filter((id) => id !== appId && id !== `installed-${appId}`))
      toast.success("Aplicativo ativado com sucesso!")
    }

    // Persistir alteração no Banco de Dados via GraphQL (Supabase) apenas para o workspace atual
    try {
      const token = getAuthToken()
      if (token && currentWorkspace?.id) {
        fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              mutation UpdateAppStatus($workspaceId: ID!, $appId: ID!, $status: String!) {
                updateInstalledAppStatus(workspaceId: $workspaceId, appId: $appId, status: $status)
              }
            `,
            variables: {
              workspaceId: currentWorkspace.id,
              appId,
              status: newStatus
            }
          })
        }).catch((err) => console.error("Erro ao salvar status do app no Supabase:", err))
      }
    } catch (e) { }
  }
  const handleInstallApp = (appObj: any) => {
    const formattedApp = {
      id: appObj.id || `app-${Date.now()}`,
      name: appObj.name,
      slug: appObj.slug || appObj.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      iconUrl: appObj.iconUrl,
      iframeUrl: appObj.iframeUrl || "",
      category: appObj.category || "Utilitários",
      isCustom: true
    }

    setInstalledApps((prev) => {
      const exists = prev.some((ia) => ia.app.id === formattedApp.id)
      if (exists) {
        return prev.map((ia) => (ia.app.id === formattedApp.id ? { ...ia, status: "active" as const } : ia))
      }
      return [...prev, { id: formattedApp.id, app: formattedApp as any, status: "active", configuration: "{}", sortOrder: 0 }]
    })

    setDeveloperApps((prev) => {
      if (prev.some((a) => a.id === formattedApp.id)) return prev
      return [...prev, formattedApp]
    })

    setHiddenAppIds((prev) => prev.filter((id) => id !== formattedApp.id))

    try {
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("token="))
        ?.split("=")[1]
      if (token) {
        fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              mutation CreateApp($name: String!, $slug: String!, $iframeUrl: String!, $iconUrl: String) {
                createApp(name: $name, slug: $slug, iframeUrl: $iframeUrl, iconUrl: $iconUrl, scopes: [], dependencies: []) {
                  id
                  name
                  slug
                }
              }
            `,
            variables: {
              name: formattedApp.name,
              slug: formattedApp.slug,
              iframeUrl: formattedApp.iframeUrl,
              iconUrl: formattedApp.iconUrl
            }
          })
        }).catch(() => { })
      }
    } catch (e) { }

    toast.success(`Aplicativo ${formattedApp.name} instalado e disponível no workspace!`)
  }

  const handleAddCustomApp = React.useCallback(async (appData: App, isEdit?: boolean) => {
    // 1. Atualiza imediatamente o estado de marketplaceApps e developerApps
    setMarketplaceApps((prev) => {
      const exists = prev.some((a) => a.id === appData.id || a.slug === appData.slug)
      if (exists) {
        return prev.map((a) => {
          if (a.id === appData.id || a.slug === appData.slug) {
            const mergedAccounts = Array.from(
              new Set([...(Array.isArray(a.accounts) ? a.accounts : []), ...(Array.isArray(appData.accounts) ? appData.accounts : [])])
            )
            return {
              ...a,
              ...appData,
              iframeUrl: isEdit ? a.iframeUrl || appData.iframeUrl : appData.iframeUrl,
              accounts: mergedAccounts,
            }
          }
          return a
        })
      }
      return [appData, ...prev]
    })

    setDeveloperApps((prev) => {
      const exists = prev.some((a) => a.id === appData.id || a.slug === appData.slug)
      if (exists) {
        return prev.map((a) => {
          if (a.id === appData.id || a.slug === appData.slug) {
            const mergedAccounts = Array.from(
              new Set([...(Array.isArray(a.accounts) ? a.accounts : []), ...(Array.isArray(appData.accounts) ? appData.accounts : [])])
            )
            return {
              ...a,
              ...appData,
              iframeUrl: isEdit ? a.iframeUrl || appData.iframeUrl : appData.iframeUrl,
              accounts: mergedAccounts,
            }
          }
          return a
        })
      }
      return [appData, ...prev]
    })

    // Se estiver editando um app já instalado/aberto, sincroniza metadados (nome, ícone, descrição) nos estados locais
    if (isEdit) {
      setInstalledApps((prev) =>
        prev.map((ia) =>
          ia.app.id === appData.id || ia.app.slug === appData.slug
            ? {
                ...ia,
                app: {
                  ...ia.app,
                  name: appData.name,
                  iconUrl: appData.iconUrl,
                  description: appData.description,
                },
              }
            : ia
        )
      )

      setCurrentAccount((prev: any) => {
        if (!prev) return prev
        return {
          ...prev,
          workspaces: prev.workspaces?.map((ws: any) => ({
            ...ws,
            installedApps: ws.installedApps?.map((ia: any) =>
              ia.app?.id === appData.id || ia.app?.slug === appData.slug
                ? {
                    ...ia,
                    app: {
                      ...ia.app,
                      name: appData.name,
                      iconUrl: appData.iconUrl,
                      description: appData.description,
                    },
                  }
                : ia
            ),
          })),
        }
      })

      setOpenApps((prev) =>
        prev.map((tab) =>
          tab.app.id === appData.id || tab.app.slug === appData.slug
            ? {
                ...tab,
                app: {
                  ...tab.app,
                  name: appData.name,
                  iconUrl: appData.iconUrl,
                  description: appData.description,
                },
              }
            : tab
        )
      )
    }

    // 2. Persiste no localStorage para a conta atual
    if (typeof localStorage !== "undefined" && currentAccount?.id) {
      try {
        const storageKey = `customMarketplaceApps:${currentAccount.id}`
        const existing = JSON.parse(localStorage.getItem(storageKey) || "[]")
        const prevMatch = existing.find((a: any) => a.id === appData.id || a.slug === appData.slug)
        const mergedItem = prevMatch
          ? {
              ...prevMatch,
              ...appData,
              iframeUrl: isEdit ? prevMatch.iframeUrl || appData.iframeUrl : appData.iframeUrl,
              accounts: Array.from(
                new Set([
                  ...(Array.isArray(prevMatch.accounts) ? prevMatch.accounts : []),
                  ...(Array.isArray(appData.accounts) ? appData.accounts : []),
                ])
              ),
            }
          : appData
        const updated = [mergedItem, ...existing.filter((a: any) => a.id !== appData.id && a.slug !== appData.slug)]
        localStorage.setItem(storageKey, JSON.stringify(updated))
      } catch (e) { }
    }

    // 3. Persiste no backend via GraphQL mutation (UpdateDeveloperApp ou CreateApp)
    try {
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("token="))
        ?.split("=")[1]

      if (token) {
        if (isEdit) {
          const res = await fetch("/api/graphql", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              query: `
                mutation UpdateCustomDeveloperApp(
                  $id: ID!
                  $name: String!
                  $slug: String
                  $description: String
                  $iconUrl: String
                  $accounts: [String!]
                  $version: String
                  $environment: String
                ) {
                  updateDeveloperApp(
                    id: $id
                    name: $name
                    slug: $slug
                    description: $description
                    iconUrl: $iconUrl
                    accounts: $accounts
                    version: $version
                    environment: $environment
                  ) {
                    id
                    name
                    slug
                    description
                    iconUrl
                    iframeUrl
                    clientId
                    developerId
                    type
                    status
                    accounts
                    version
                    environment
                  }
                }
              `,
              variables: {
                id: appData.id,
                name: appData.name,
                slug: appData.slug,
                description: appData.description || null,
                iconUrl: appData.iconUrl || null,
                accounts: appData.accounts || [],
                version: appData.version || null,
                environment: appData.environment || null,
              },
            }),
          })
          const data = await res.json()
          if (data?.data?.updateDeveloperApp) {
            const updated = data.data.updateDeveloperApp
            setMarketplaceApps((prev) =>
              prev.map((a) => (a.id === appData.id || a.slug === appData.slug ? { ...a, ...updated } : a))
            )
            setDeveloperApps((prev) =>
              prev.map((a) => (a.id === appData.id || a.slug === appData.slug ? { ...a, ...updated } : a))
            )
          }
        } else {
          const res = await fetch("/api/graphql", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              query: `
                mutation CreateCustomApp(
                  $name: String!
                  $slug: String!
                  $description: String
                  $iframeUrl: String!
                  $iconUrl: String
                  $type: String
                  $status: String
                  $clientId: String
                  $clientSecret: String
                  $accounts: [String!]
                  $version: String
                  $environment: String
                ) {
                  createApp(
                    name: $name
                    slug: $slug
                    description: $description
                    iframeUrl: $iframeUrl
                    iconUrl: $iconUrl
                    type: $type
                    status: $status
                    clientId: $clientId
                    clientSecret: $clientSecret
                    accounts: $accounts
                    scopes: []
                    dependencies: []
                    version: $version
                    environment: $environment
                  ) {
                    id
                    name
                    slug
                    clientId
                    clientSecret
                    status
                    type
                    accounts
                    version
                    environment
                  }
                }
              `,
              variables: {
                name: appData.name,
                slug: appData.slug,
                description: appData.description || null,
                iframeUrl: appData.iframeUrl,
                iconUrl: appData.iconUrl || null,
                type: appData.type || "private",
                status: appData.status || "active",
                clientId: appData.clientId || (appData as any).client_id,
                clientSecret: appData.clientSecret || (appData as any).client_secret,
                accounts: appData.accounts || [],
                version: appData.version || null,
                environment: appData.environment || null,
              },
            }),
          })
          const data = await res.json()
          if (data?.data?.createApp) {
            const created = data.data.createApp
            const finalCreatedApp = { ...appData, id: created.id, ...created }
            setMarketplaceApps((prev) =>
              prev.map((a) => (a.slug === appData.slug ? finalCreatedApp : a))
            )
            setDeveloperApps((prev) =>
              prev.map((a) => (a.slug === appData.slug ? finalCreatedApp : a))
            )
          }
        }
      }
    } catch (err) {
      console.warn("Aviso ao persistir aplicativo personalizado no backend:", err)
    }
  }, [currentAccount?.id])

  const handleUninstallApp = async (appId: string) => {
    const appItem = installedApps.find((item) => item.app.id === appId || item.app.slug === appId)?.app ||
      marketplaceApps.find((a) => a.id === appId || a.slug === appId) ||
      developerApps.find((a) => a.id === appId || a.slug === appId)
    const allIds = [appId, appItem?.id, appItem?.slug, `installed-${appId}`, appItem?.id ? `installed-${appItem.id}` : ""].filter(Boolean) as string[]

    setInstalledApps((prev) => {
      const next = prev.filter((item) => !allIds.includes(item.id) && !allIds.includes(item.app.id) && !allIds.includes(item.app.slug))
      if (typeof window !== "undefined") {
        try {
          const statuses = next.map((ia) => ({ id: ia.app.id, status: ia.status }))
          localStorage.setItem("bold_installed_apps_status", JSON.stringify(statuses))
        } catch (e) { }
      }
      return next
    })

    setGloballyInstalledApps((prev) => {
      const next = prev.filter((id) => !allIds.includes(id))
      if (typeof window !== "undefined" && currentAccount) {
        try {
          localStorage.setItem(`globallyInstalledApps:${currentAccount.id}`, JSON.stringify(next))
        } catch (e) { }
      }
      return next
    })

    setCurrentAccount((prev: any) => {
      if (!prev) return prev
      const prevInstalled = Array.isArray(prev.installedAppIds) ? prev.installedAppIds : []
      return {
        ...prev,
        installedAppIds: prevInstalled.filter((id: string) => !allIds.includes(id)),
        workspaces: prev.workspaces?.map((ws: any) => ({
          ...ws,
          installedApps: ws.installedApps?.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
        }))
      }
    })

    setCurrentWorkspace((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        installedApps: prev.installedApps?.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
      }
    })

    setAccounts((prev) =>
      prev.map((acc) => {
        if (acc.id !== currentAccount?.id) return acc
        return {
          ...acc,
          workspaces: acc.workspaces?.map((ws) => ({
            ...ws,
            installedApps: ws.installedApps?.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
          }))
        }
      })
    )

    setDeveloperApps((prev) => prev.filter((a) => !allIds.includes(a.id)))
    setHiddenAppIds((prev) => [...prev, appId])
    setDockAppIds((prev) => prev.filter((id) => !allIds.includes(id)))
    setOpenApps((prev) => prev.filter((tab) => !allIds.includes(tab.app.id) && !allIds.includes(tab.app.slug)))

    try {
      const token = getAuthToken()
      const targetWorkspace = currentWorkspace || currentAccount?.workspaces?.[0] || accounts?.[0]?.workspaces?.[0]
      if (token) {
        const realAppId = appItem?.id || appId
        if (currentAccount?.workspaces && currentAccount.workspaces.length > 0) {
          for (const ws of currentAccount.workspaces) {
            try {
              await fetch("/api/graphql", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  query: `mutation UninstallApp($workspaceId: ID!, $appId: ID!) { uninstallApp(workspaceId: $workspaceId, appId: $appId) }`,
                  variables: { workspaceId: ws.id, appId: realAppId }
                })
              })
            } catch (e) { }
          }
        } else if (targetWorkspace) {
          try {
            await fetch("/api/graphql", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                query: `mutation UninstallApp($workspaceId: ID!, $appId: ID!) { uninstallApp(workspaceId: $workspaceId, appId: $appId) }`,
                variables: { workspaceId: targetWorkspace.id, appId: realAppId }
              })
            })
          } catch (e) { }
        }
      }
    } catch (e) {
      console.error("Erro ao excluir app do BD:", e)
    }

    toast.success("Aplicativo desinstalado com sucesso!")
  }
  const handleToggleWidgetPin = () => { }

  const handleConfigureAppWorkspaces = async (appId: string, workspaceIds: string[]) => {
    const token = getAuthToken()
    const targetWorkspace = currentWorkspace || currentAccount?.workspaces?.[0] || accounts?.[0]?.workspaces?.[0]

    const appMeta = marketplaceApps.find((a) => a.id === appId || a.slug === appId) ||
      developerApps.find((a) => a.id === appId || a.slug === appId)
    const realAppId = appMeta?.id || appId
    const allIds = [appId, appMeta?.id, appMeta?.slug, `installed-${appId}`, appMeta?.id ? `installed-${appMeta.id}` : ""].filter(Boolean) as string[]

    const willBeInstalled = workspaceIds.length > 0

    // 1. Atualizar globallyInstalledApps
    setGloballyInstalledApps((prev) => {
      let next: string[]
      if (willBeInstalled) {
        next = Array.from(new Set([...prev, realAppId, appId]))
      } else {
        next = prev.filter((id) => !allIds.includes(id))
      }
      if (typeof window !== "undefined" && currentAccount) {
        try {
          localStorage.setItem(`globallyInstalledApps:${currentAccount.id}`, JSON.stringify(next))
        } catch (e) { }
      }
      return next
    })

    // 2. Atualizar workspaces de currentAccount e accounts
    const newInstalledAppObj = appMeta ? {
      id: `installed-${realAppId}`,
      app: appMeta,
      status: "active" as const,
      configuration: "{}",
      sortOrder: 0
    } : null

    setCurrentAccount((prev: any) => {
      if (!prev) return prev
      const prevInstalled = Array.isArray(prev.installedAppIds) ? prev.installedAppIds : []
      const nextInstalledIds = willBeInstalled
        ? Array.from(new Set([...prevInstalled, realAppId, appId]))
        : prevInstalled.filter((id: string) => !allIds.includes(id))
      return {
        ...prev,
        installedAppIds: nextInstalledIds,
        workspaces: prev.workspaces?.map((ws: any) => {
          const isSelected = workspaceIds.includes(ws.id)
          const currentList = ws.installedApps || []
          if (isSelected) {
            const exists = currentList.some((ia: any) => allIds.includes(ia.id) || allIds.includes(ia.app?.id) || allIds.includes(ia.app?.slug))
            if (exists) {
              return {
                ...ws,
                installedApps: currentList.map((ia: any) =>
                  allIds.includes(ia.id) || allIds.includes(ia.app?.id) || allIds.includes(ia.app?.slug)
                    ? { ...ia, status: "active" as const }
                    : ia
                )
              }
            }
            if (newInstalledAppObj) {
              return { ...ws, installedApps: [...currentList, newInstalledAppObj] }
            }
            return ws
          } else {
            return {
              ...ws,
              installedApps: currentList.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
            }
          }
        })
      }
    })

    setAccounts((prev) =>
      prev.map((acc) => {
        if (acc.id !== currentAccount?.id) return acc
        return {
          ...acc,
          workspaces: acc.workspaces?.map((ws) => {
            const isSelected = workspaceIds.includes(ws.id)
            const currentList = ws.installedApps || []
            if (isSelected) {
              const exists = currentList.some((ia: any) => allIds.includes(ia.id) || allIds.includes(ia.app?.id) || allIds.includes(ia.app?.slug))
              if (exists) {
                return {
                  ...ws,
                  installedApps: currentList.map((ia: any) =>
                    allIds.includes(ia.id) || allIds.includes(ia.app?.id) || allIds.includes(ia.app?.slug)
                      ? { ...ia, status: "active" as const }
                      : ia
                  )
                }
              }
              if (newInstalledAppObj) {
                return { ...ws, installedApps: [...currentList, newInstalledAppObj] }
              }
              return ws
            } else {
              return {
                ...ws,
                installedApps: currentList.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
              }
            }
          })
        }
      })
    )

    // 3. Atualizar estado do workspace atual
    if (currentWorkspace) {
      const isSelectedForCurrent = workspaceIds.includes(currentWorkspace.id)
      if (isSelectedForCurrent) {
        if (appMeta) {
          setInstalledApps((prev) => {
            const exists = prev.some((ia) => allIds.includes(ia.id) || allIds.includes(ia.app.id) || allIds.includes(ia.app.slug))
            if (exists) {
              return prev.map((ia) =>
                allIds.includes(ia.id) || allIds.includes(ia.app.id) || allIds.includes(ia.app.slug)
                  ? { ...ia, status: "active" as const }
                  : ia
              )
            }
            return [...prev, { id: `installed-${realAppId}`, app: appMeta, status: "active" as const, configuration: "{}", sortOrder: prev.length }]
          })
          setCurrentWorkspace((prev) => {
            if (!prev) return prev
            const curList = prev.installedApps || []
            const exists = curList.some((ia: any) => allIds.includes(ia.id) || allIds.includes(ia.app?.id) || allIds.includes(ia.app?.slug))
            if (exists) {
              return {
                ...prev,
                installedApps: curList.map((ia: any) =>
                  allIds.includes(ia.id) || allIds.includes(ia.app?.id) || allIds.includes(ia.app?.slug)
                    ? { ...ia, status: "active" as const }
                    : ia
                )
              }
            }
            return {
              ...prev,
              installedApps: [...curList, { id: `installed-${realAppId}`, app: appMeta, status: "active" as const, configuration: "{}", sortOrder: curList.length }]
            }
          })
        }
        // Ao instalar nos workspaces selecionados, já adiciona automaticamente ativo e visível (remover de hiddenAppIds)
        setHiddenAppIds((prev) => prev.filter((id) => !allIds.includes(id)))
      } else {
        setInstalledApps((prev) => prev.filter((ia) => !allIds.includes(ia.id) && !allIds.includes(ia.app.id) && !allIds.includes(ia.app.slug)))
        setCurrentWorkspace((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            installedApps: prev.installedApps?.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
          }
        })
        setOpenApps((prev) => prev.filter((tab) => !allIds.includes(tab.app.id) && !allIds.includes(tab.app.slug)))
        setDockAppIds((prev) => prev.filter((id) => !allIds.includes(id)))
      }
    }

    // Limpar o app do hiddenAppIds salvo em localStorage para todos os workspaces onde foi instalado
    if (typeof window !== "undefined") {
      workspaceIds.forEach((wsId) => {
        try {
          const saved = localStorage.getItem(`bold_hidden_app_ids:${wsId}`)
          if (saved) {
            const arr: string[] = JSON.parse(saved)
            const next = arr.filter((id) => !allIds.includes(id))
            localStorage.setItem(`bold_hidden_app_ids:${wsId}`, JSON.stringify(next))
          }
        } catch (e) { }
      })
    }

    // 4. Se for bold-crm, aplicar para contatos também
    if (appMeta?.slug === "bold-crm") {
      const contactsApp = marketplaceApps.find(a => a.slug === "bold-contacts" || a.slug === "contacts" || a.name.toLowerCase().includes("contato"))
      if (contactsApp && contactsApp.id !== realAppId && token) {
        try {
          await fetch("/api/graphql", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              query: `mutation ConfigureAppWorkspaces($appId: ID!, $workspaceIds: [ID!]!) { configureAppWorkspaces(appId: $appId, workspaceIds: $workspaceIds) }`,
              variables: { appId: contactsApp.id, workspaceIds }
            })
          })
        } catch (e) { }
      }
    }

    // 5. Chamar a mutação GraphQL configureAppWorkspaces
    if (token) {
      try {
        const res = await fetch("/api/graphql", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              mutation ConfigureAppWorkspaces($appId: ID!, $workspaceIds: [ID!]!) {
                configureAppWorkspaces(appId: $appId, workspaceIds: $workspaceIds)
              }
            `,
            variables: { appId: realAppId, workspaceIds }
          })
        })
        const resData = await res.json()
        if (resData.errors && resData.errors.length > 0) {
          console.error("Erro ao configurar workspaces do app no backend:", resData.errors[0]?.message)
        }

        if (targetWorkspace) {
          await loadInstalledApps(token, targetWorkspace.id)
        }
      } catch (e) {
        console.error("Erro na mutação configureAppWorkspaces:", e)
      }
    }
  }

  const handleToggleGlobalApp = async (appId: string, action?: "install" | "uninstall", workspaceIds?: string[]) => {
    if (action === "install" && workspaceIds) {
      return await handleConfigureAppWorkspaces(appId, workspaceIds)
    }
    const isCurrentlyInstalled = globallyInstalledApps.includes(appId) || installedApps.some((ia) => (ia.app.id === appId || ia.app.slug === appId))
    const actionToPerform = action || (isCurrentlyInstalled ? "uninstall" : "install")
    const token = getAuthToken()
    const targetWorkspace = currentWorkspace || currentAccount?.workspaces?.[0] || accounts?.[0]?.workspaces?.[0]

    const appMeta = marketplaceApps.find((a) => a.id === appId || a.slug === appId) ||
      developerApps.find((a) => a.id === appId || a.slug === appId)
    const realAppId = appMeta?.id || appId
    const allIds = [appId, appMeta?.id, appMeta?.slug, `installed-${appId}`, appMeta?.id ? `installed-${appMeta.id}` : ""].filter(Boolean) as string[]

    if (actionToPerform === "uninstall") {
      // Desinstalar aplicativo de todos os workspaces da conta
      setGloballyInstalledApps((prev) => {
        const next = prev.filter((id) => !allIds.includes(id))
        if (typeof window !== "undefined" && currentAccount) {
          try {
            localStorage.setItem(`globallyInstalledApps:${currentAccount.id}`, JSON.stringify(next))
          } catch (e) { }
        }
        return next
      })

      setInstalledApps((prev) => prev.filter((ia) => !allIds.includes(ia.id) && !allIds.includes(ia.app.id) && !allIds.includes(ia.app.slug)))
      setOpenApps((prev) => prev.filter((tab) => !allIds.includes(tab.app.id) && !allIds.includes(tab.app.slug)))
      setDockAppIds((prev) => prev.filter((id) => !allIds.includes(id)))
      setHiddenAppIds((prev) => Array.from(new Set([...prev, appId, realAppId])))

      setCurrentAccount((prev: any) => {
        if (!prev) return prev
        const prevInstalled = Array.isArray(prev.installedAppIds) ? prev.installedAppIds : []
        return {
          ...prev,
          installedAppIds: prevInstalled.filter((id: string) => !allIds.includes(id)),
          workspaces: prev.workspaces?.map((ws: any) => ({
            ...ws,
            installedApps: ws.installedApps?.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
          }))
        }
      })

      setCurrentWorkspace((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          installedApps: prev.installedApps?.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
        }
      })

      setAccounts((prev) =>
        prev.map((acc) => {
          if (acc.id !== currentAccount?.id) return acc
          return {
            ...acc,
            workspaces: acc.workspaces?.map((ws) => ({
              ...ws,
              installedApps: ws.installedApps?.filter((ia: any) => !allIds.includes(ia.id) && !allIds.includes(ia.app?.id) && !allIds.includes(ia.app?.slug))
            }))
          }
        })
      )

      if (token && targetWorkspace) {
        try {
          await fetch("/api/graphql", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              query: `
                mutation UninstallApp($workspaceId: ID!, $appId: ID!) {
                  uninstallApp(workspaceId: $workspaceId, appId: $appId)
                }
              `,
              variables: { workspaceId: targetWorkspace.id, appId: realAppId }
            })
          })
        } catch (e) {
          console.error("Erro ao desinstalar app do backend:", e)
        }
      }

      toast.success(appMeta ? `Aplicativo "${appMeta.name}" desinstalado de todos os workspaces da conta.` : "Aplicativo desinstalado com sucesso!")
    } else {
      // Instalar aplicativo globalmente na conta com status inicial "active"
      setGloballyInstalledApps((prev) => {
        const next = Array.from(new Set([...prev, realAppId, appId]))
        if (typeof window !== "undefined" && currentAccount) {
          try {
            localStorage.setItem(`globallyInstalledApps:${currentAccount.id}`, JSON.stringify(next))
          } catch (e) { }
        }
        return next
      })

      setCurrentAccount((prev: any) => {
        if (!prev) return prev
        const prevInstalled = Array.isArray(prev.installedAppIds) ? prev.installedAppIds : []
        return {
          ...prev,
          installedAppIds: Array.from(new Set([...prevInstalled, realAppId, appId]))
        }
      })

      if (appMeta) {
        setInstalledApps((prev) => {
          const exists = prev.some((ia) => ia.app.id === appMeta.id || ia.app.slug === appMeta.slug)
          if (exists) {
            return prev.map((ia) => (ia.app.id === appMeta.id || ia.app.slug === appMeta.slug ? { ...ia, status: "active" as const } : ia))
          }
          return [...prev, { id: `installed-${appMeta.id}`, app: appMeta, status: "active" as const, configuration: "{}", sortOrder: prev.length }]
        })
      }

      // Adiciona visível e ativo na área de trabalho
      setHiddenAppIds((prev) => prev.filter((id) => !allIds.includes(id)))

      if (token && targetWorkspace) {
        try {
          const res = await fetch("/api/graphql", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              query: `
                mutation InstallApp($input: InstallAppInput!) {
                  installApp(input: $input) {
                    id
                    status
                    app {
                      id
                      name
                      slug
                      iconUrl
                      iframeUrl
                    }
                  }
                }
              `,
              variables: {
                input: {
                  workspaceId: targetWorkspace.id,
                  appId: realAppId,
                  configuration: "{}"
                }
              }
            })
          })
          const resData = await res.json()
          if (resData.errors && resData.errors.length > 0) {
            console.error("Erro GraphQL ao instalar app:", resData.errors[0]?.message)
          }

          await loadInstalledApps(token, targetWorkspace.id)
        } catch (e) {
          console.error("Erro ao instalar app no workspace:", e)
        }
      }

      toast.success(
        appMeta
          ? `Aplicativo "${appMeta.name}" instalado na conta! Disponível inicialmente como inativo nos seus workspaces.`
          : "Aplicativo instalado na conta!"
      )
    }
  }

  const handleToggleGlobalWidget = async (widgetId: string, action?: "install" | "uninstall") => {
    const isCurrentlyInstalled = widgetsConfig.some((w) => (w.id === widgetId || w.slug === widgetId) && w.enabled !== false)
    const actionToPerform = action || (isCurrentlyInstalled ? "uninstall" : "install")
    const token = getAuthToken()
    const targetWorkspace = currentWorkspace || currentAccount?.workspaces?.[0] || accounts?.[0]?.workspaces?.[0]

    const widgetMeta = widgetsConfig.find((w) => w.id === widgetId || w.slug === widgetId)
    const realWidgetId = widgetMeta?.id || widgetId

    if (actionToPerform === "uninstall") {
      setWidgetsConfig((prev) => prev.filter((w) => w.id !== realWidgetId && w.id !== widgetId))
      setActiveFloatingWidgets((prev) => prev.filter((id) => id !== realWidgetId && id !== widgetId))
      setPinnedWidgets((prev) => prev.filter((id) => id !== realWidgetId && id !== widgetId))

      if (token && targetWorkspace) {
        try {
          await fetch("/api/graphql", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              query: `
                mutation UninstallWidget($workspaceId: ID!, $widgetId: ID!) {
                  uninstallWidget(workspaceId: $workspaceId, widgetId: $widgetId)
                }
              `,
              variables: { workspaceId: targetWorkspace.id, widgetId: realWidgetId }
            })
          })
        } catch (e) {
          console.error("Erro ao desinstalar widget no backend:", e)
        }
      }

      toast.success(widgetMeta ? `Widget "${widgetMeta.name}" desinstalado de todos os workspaces da conta.` : "Widget desinstalado com sucesso!")
    } else {
      if (widgetMeta) {
        setWidgetsConfig((prev) => {
          const exists = prev.some((w) => w.id === widgetMeta.id)
          if (exists) {
            return prev.map((w) => (w.id === widgetMeta.id ? { ...w, enabled: false, visible: false, status: "inactive" } : w))
          }
          return [...prev, { ...widgetMeta, enabled: false, visible: false, status: "inactive" }]
        })
      }

      if (token && targetWorkspace) {
        try {
          const res = await fetch("/api/graphql", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              query: `
                mutation InstallWidget($input: InstallWidgetInput!) {
                  installWidget(input: $input) {
                    id
                    status
                    enabled
                    widget {
                      id
                      name
                      slug
                      icon
                      type
                      dimension
                    }
                  }
                }
              `,
              variables: {
                input: {
                  workspaceId: targetWorkspace.id,
                  widgetId: realWidgetId,
                  configuration: "{}"
                }
              }
            })
          })
          const resData = await res.json()
          if (resData.errors && resData.errors.length > 0) {
            console.error("Erro GraphQL ao instalar widget:", resData.errors[0]?.message)
          }
        } catch (e) {
          console.error("Erro ao instalar widget no backend:", e)
        }
      }

      toast.success(
        widgetMeta
          ? `Widget "${widgetMeta.name}" instalado na conta! Disponível para ativação nos Ajustes dos seus workspaces.`
          : "Widget instalado na conta!"
      )
    }
  }
  const handleSwitchWorkspace = React.useCallback(
    async (wsOrId: any) => {
      let ws = wsOrId
      if (typeof wsOrId === "string") {
        ws = (currentAccount?.workspaces || []).find((w: any) => w.id === wsOrId || w.slug === wsOrId) || { id: wsOrId, name: "Workspace", slug: wsOrId }
      }
      if (!ws || ws.id === currentWorkspace?.id) return
      setLoading(true)
      try {
        // Salvar abas do workspace atual antes de alternar
        if (currentWorkspace?.id && isPreferencesLoadedRef.current) {
          saveWorkspacePreferences({
            extraSettings: {
              openApps
            }
          }, currentWorkspace.id)
        }

        isPreferencesLoadedRef.current = false
        setActiveAppId(null)
        setSplitActiveAppId(null)
        setOpenApps([])
        setCurrentWorkspace(ws)

        const token = getAuthToken()
        if (token) {
          await loadWorkspacePreferences(ws.id, token)
          await loadInstalledApps(token, ws.id)
        }

        if (typeof window !== "undefined") {
          const desktopPath = buildWorkspaceDesktopPath(currentAccount, ws)
          window.history.pushState(null, "", desktopPath)
        }

        await new Promise((resolve) => setTimeout(resolve, 650))
        toast.success(`Workspace alterado para "${ws.name || 'Workspace'}"`)
      } catch (err) {
        console.error("Erro ao alternar workspace:", err)
        toast.error("Erro ao alternar workspace")
      } finally {
        setLoading(false)
      }
    },
    [
      currentWorkspace?.id,
      currentAccount?.workspaces,
      loadInstalledApps,
      loadWorkspacePreferences,
      saveWorkspacePreferences,
      openApps,
      activeAppId,
      user,
      setAppPositions,
      setDesktopWidgets,
      setDesktopWidgetPositions,
      setActiveAppId,
      setSplitActiveAppId,
      setLoading
    ]
  )

  const handleGenerateApiKey = () => {
    const newKey = `bold_sk_live_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`
    setApiKey(newKey)
    if (typeof window !== "undefined") {
      localStorage.setItem("bold_api_key", newKey)
    }
    toast.success("Nova chave de API gerada com sucesso!")
  }
  const handleSaveWebhook = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem("bold_webhook_url", webhookUrl)
    }
    toast.success("Endpoint de Webhook salvo com sucesso!")
  }
  const handleSaveProfile = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!profileName.trim()) {
      toast.error("O nome completo não pode ficar em branco.")
      return
    }
    const token = getAuthToken()
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation UpdateUserProfile(
              $name: String!
              $avatarUrl: String
              $phone: String
              $loginMusicUrl: String
              $loginMusicEnabled: Boolean
              $lockscreenPin: String
              $fullSizeScreen: Boolean
            ) {
              updateUserProfile(
                name: $name
                avatarUrl: $avatarUrl
                phone: $phone
                loginMusicUrl: $loginMusicUrl
                loginMusicEnabled: $loginMusicEnabled
                lockscreenPin: $lockscreenPin
                fullSizeScreen: $fullSizeScreen
              ) {
                id
                name
                email
                avatarUrl
                phone
                phoneVerifiedAt
                twoFactorEnabled
                loginMusicUrl
                loginMusicEnabled
                fullSizeScreen
                lockscreenPin
              }
            }
          `,
          variables: {
            name: profileName.trim(),
            avatarUrl: profileAvatarUrl,
            phone: profilePhone,
            loginMusicUrl,
            loginMusicEnabled: isLoginMusicEnabled,
            lockscreenPin,
            fullSizeScreen
          }
        })
      })

      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        toast.error(result.errors[0]?.message || "Erro ao atualizar perfil")
        return
      }

      if (result.data?.updateUserProfile) {
        const updatedUser = result.data.updateUserProfile
        setUser(updatedUser)
        if (typeof window !== "undefined") {
          localStorage.setItem("user", JSON.stringify(updatedUser))
          if (updatedUser.id) {
            if (lockscreenPin) {
              localStorage.setItem(`lockscreenPin:${updatedUser.id}`, lockscreenPin)
            } else {
              localStorage.removeItem(`lockscreenPin:${updatedUser.id}`)
            }
            localStorage.setItem(`loginMusicUrl:${updatedUser.id}`, loginMusicUrl)
            localStorage.setItem(`loginMusicEnabled:${updatedUser.id}`, String(isLoginMusicEnabled))
            if (updatedUser.fullSizeScreen !== undefined && updatedUser.fullSizeScreen !== null) {
              localStorage.setItem(`fullSizeScreen:${updatedUser.id}`, String(updatedUser.fullSizeScreen))
              localStorage.setItem("bold_fullscreen_dismissed_permanent", String(updatedUser.fullSizeScreen))
            }
          }
        }
        toast.success("Perfil atualizado com sucesso!")
      }
    } catch (err) {
      console.error("Erro ao salvar perfil:", err)
      toast.error("Erro de conexão ao salvar perfil.")
    }
  }

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentPassword) {
      toast.error("Informe a senha atual.")
      return
    }
    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter no mínimo 6 caracteres.")
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error("A confirmação de senha não confere.")
      return
    }
    const token = getAuthToken()
    if (!token) return

    setIsChangingPassword(true)
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
              changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
            }
          `,
          variables: { currentPassword, newPassword }
        })
      })

      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        toast.error(result.errors[0]?.message || "Erro ao alterar a senha.")
        return
      }

      if (result.data?.changePassword) {
        toast.success("Senha alterada com sucesso!")
        setCurrentPassword("")
        setNewPassword("")
        setConfirmPassword("")
      } else {
        toast.error("Não foi possível alterar a senha. Verifique a senha atual.")
      }
    } catch (err) {
      console.error("Erro ao alterar senha:", err)
      toast.error("Erro de conexão ao alterar a senha.")
    } finally {
      setIsChangingPassword(false)
    }
  }
  const handleGenerateRealNotification = () => { }
  const handleRevokeApiKey = async (id: string) => {
    const token = getAuthToken()
    if (!token) return
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation RevokeApiKey($id: ID!) {
              revokeApiKey(id: $id)
            }
          `,
          variables: { id }
        })
      })
      const result = await res.json()
      if (result.errors) {
        toast.error(result.errors[0]?.message || "Erro ao revogar chave de API")
        return
      }
      toast.success("Chave de API revogada com sucesso!")
      setApiKeys((prev) => prev.filter((k) => k.id !== id))
    } catch (e) {
      console.error("Erro ao revogar chave de API:", e)
    }
  }

  const handleAcceptTerms = async () => {
    if (!hasCheckedTermsCheckbox) {
      toast.error("Você precisa marcar a caixa de concordância com os termos.")
      return
    }
    const token = getAuthToken()
    if (!token) return

    setIsAcceptingTerms(true)
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation AcceptTerms {
              acceptTerms {
                id
                name
                email
                avatarUrl
                phone
                twoFactorEnabled
                termsAcceptedAt
              }
            }
          `
        })
      })
      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        toast.error(result.errors[0]?.message || "Erro ao aceitar termos")
        return
      }

      if (result.data?.acceptTerms) {
        const acceptedAt = result.data.acceptTerms.termsAcceptedAt || new Date().toISOString()
        const updatedUser = {
          ...(user || {}),
          ...result.data.acceptTerms,
          termsAcceptedAt: acceptedAt
        }
        setUser(updatedUser)
        if (typeof window !== "undefined") {
          localStorage.setItem("user", JSON.stringify(updatedUser))
        }
        toast.success("Termos e Políticas aceitos com sucesso!")
      }
    } catch (err) {
      console.error("Erro ao aceitar termos:", err)
      toast.error("Erro de conexão ao aceitar os termos.")
    } finally {
      setIsAcceptingTerms(false)
    }
  }

  const handleRevokeApp = (id: string) => handleUninstallApp(id)
  const handleLogout = async () => {
    document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;"
    try {
      await fetch("/api/auth/logout", { method: "POST" })
    } catch (e) { }
    localStorage.removeItem("user")
    localStorage.removeItem("currentAccount")
    router.push("/login")
  }

  const handleSwitchAccount = async (accountId: string, targetWorkspaceSlug?: string) => {
    const token = getAuthToken()
    if (!token) return

    setLoading(true)
    setOpenApps([])
    setActiveAppId(null)
    setSplitActiveAppId(null)

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation SwitchAccount($accountId: ID!) {
              switchAccount(accountId: $accountId) {
                token
                currentAccount {
                  id
                  name
                  slug
                  logoUrl
                  workspaces {
                    id
                    name
                    slug
                    status
                    sortOrder
                    createdAt
                  }
                }
              }
            }
          `,
          variables: { accountId }
        })
      })

      const result = await res.json()

      if (result.data && result.data.switchAccount) {
        const { token: newToken, currentAccount: switchedAcc } = result.data.switchAccount
        document.cookie = `token=${newToken}; path=/; max-age=604800; SameSite=Lax`
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("token", newToken)
          localStorage.setItem("bold_token", newToken)
          localStorage.setItem("currentAccount", JSON.stringify(switchedAcc))
        }

        const accessibleWorkspaces = (switchedAcc.workspaces || []).filter((w: any) => w.status !== false)
        const targetWs = targetWorkspaceSlug
          ? accessibleWorkspaces.find((w: any) => w.slug?.toLowerCase() === targetWorkspaceSlug.toLowerCase() || w.id === targetWorkspaceSlug)
          : null
        const chosenWs = targetWs || getOldestActiveWorkspace(accessibleWorkspaces)

        if (!chosenWs) {
          toast.error("Você não possui acesso a nenhum workspace ativo nesta empresa.")
          router.push("/")
          return
        }

        await loadDashboardData(newToken, switchedAcc.id, chosenWs.id)

        if (typeof window !== "undefined") {
          const desktopPath = buildWorkspaceDesktopPath(switchedAcc, chosenWs)
          window.history.pushState(null, "", desktopPath)
        }

        toast.success(`Empresa alternada para ${switchedAcc.name}`)
      } else if (result.errors && result.errors[0]) {
        toast.error(result.errors[0].message || "Erro ao alternar de empresa")
      }
    } catch (err) {
      console.error("Erro ao alternar de empresa:", err)
      toast.error("Erro de conexão ao alternar de empresa")
    } finally {
      setLoading(false)
    }
  }
  const handleMobileBack = () => setActiveAppId(null)
  const handleSwitcherTouchStart = () => { }
  const handleSwitcherTouchMove = () => { }
  const handleSwitcherTouchEnd = () => { }
  const handleTogglePinApp = React.useCallback((instanceId: string) => {
    setOpenApps((prev) => prev.map((t) => (t.instanceId === instanceId ? { ...t, pinned: !t.pinned } : t)))
  }, [])

  const handleDuplicateApp = React.useCallback((tab: OpenAppTab) => {
    const newInstanceId = `${tab.app.id}-${Math.random().toString(36).substring(2, 9)}`
    const duplicatedTab: OpenAppTab = {
      instanceId: newInstanceId,
      app: tab.app,
      pinned: false,
      groupId: tab.groupId
    }
    setOpenApps((prev) => [...prev, duplicatedTab])
    setActiveAppId(newInstanceId)
  }, [])

  const [editingGroupId, setEditingGroupId] = React.useState<string | null>(null)
  const [draggedGroupId, setDraggedGroupId] = React.useState<string | null>(null)

  const handleCreateTabGroup = React.useCallback((instanceId: string) => {
    const colors = ["blue", "purple", "green", "orange", "pink"]
    const randomColor = colors[Math.floor(Math.random() * colors.length)]
    const newGroupId = `group-${Math.random().toString(36).substring(2, 9)}`
    const newGroup: TabGroup = {
      id: newGroupId,
      name: "Novo Grupo",
      color: randomColor,
      collapsed: false
    }
    setTabGroups((prev) => [...prev, newGroup])
    setOpenApps((prev) => prev.map((t) => (t.instanceId === instanceId ? { ...t, groupId: newGroupId } : t)))
    setEditingGroupId(newGroupId)
  }, [])

  const handleToggleCollapseGroup = React.useCallback((groupId: string) => {
    setTabGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, collapsed: !g.collapsed } : g)))
  }, [])

  const handleAddTabToGroup = React.useCallback((instanceId: string, groupId: string) => {
    setOpenApps((prev) => prev.map((t) => (t.instanceId === instanceId ? { ...t, groupId } : t)))
  }, [])

  const handleRemoveTabFromGroup = React.useCallback((instanceId: string) => {
    setOpenApps((prev) => prev.map((t) => (t.instanceId === instanceId ? { ...t, groupId: null } : t)))
  }, [])

  const handleRenameGroup = React.useCallback((groupId: string, newName?: string) => {
    if (newName !== undefined) {
      const trimmed = newName.slice(0, 30)
      setTabGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, name: trimmed } : g)))
    } else {
      const name = window.prompt("Nome do Grupo (máx 30 caracteres):")
      if (name && name.trim()) {
        const trimmed = name.trim().slice(0, 30)
        setTabGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, name: trimmed } : g)))
      }
    }
  }, [])

  const handleChangeGroupColor = React.useCallback((groupId: string, color: string) => {
    setTabGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, color } : g)))
  }, [])

  const handleUngroupAll = React.useCallback((groupId: string) => {
    setOpenApps((prev) => prev.map((t) => (t.groupId === groupId ? { ...t, groupId: null } : t)))
    setTabGroups((prev) => prev.filter((g) => g.id !== groupId))
    if (editingGroupId === groupId) setEditingGroupId(null)
  }, [editingGroupId])

  const handleCloseGroupTabs = React.useCallback((groupId: string) => {
    setOpenApps((prev) => prev.filter((t) => t.groupId !== groupId))
    setTabGroups((prev) => prev.filter((g) => g.id !== groupId))
    if (editingGroupId === groupId) setEditingGroupId(null)
  }, [editingGroupId])

  const [draggedTabId, setDraggedTabId] = React.useState<string | null>(null)

  const handleTabDragStart = React.useCallback((e: React.DragEvent, instanceId: string) => {
    e.dataTransfer.setData("text/plain", instanceId)
    e.dataTransfer.effectAllowed = "move"

    try {
      const emptyImg = new Image()
      emptyImg.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      if (e.dataTransfer.setDragImage) {
        e.dataTransfer.setDragImage(emptyImg, 0, 0)
      }
    } catch (err) { }

    setDraggedTabId(instanceId)
    setDraggedGroupId(null)
  }, [])

  const handleGroupDragStart = React.useCallback((e: React.DragEvent, groupId: string) => {
    e.dataTransfer.setData("text/plain", `group:${groupId}`)
    e.dataTransfer.effectAllowed = "move"

    try {
      const emptyImg = new Image()
      emptyImg.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      if (e.dataTransfer.setDragImage) {
        e.dataTransfer.setDragImage(emptyImg, 0, 0)
      }
    } catch (err) { }

    setDraggedGroupId(groupId)
    setDraggedTabId(null)
  }, [])

  const handleGroupDragOver = React.useCallback((e: React.DragEvent, targetInstanceId?: string, targetGroupId?: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    if (!draggedGroupId) return

    setOpenApps((prev) => {
      const groupTabs = prev.filter((t) => t.groupId === draggedGroupId)
      if (groupTabs.length === 0) return prev
      const remainingTabs = prev.filter((t) => t.groupId !== draggedGroupId)

      if (targetGroupId && targetGroupId !== draggedGroupId) {
        const targetGroupIndex = remainingTabs.findIndex((t) => t.groupId === targetGroupId)
        if (targetGroupIndex === -1) return prev
        const updated = [...remainingTabs]
        updated.splice(targetGroupIndex, 0, ...groupTabs)
        return updated
      }

      if (targetInstanceId) {
        const targetIndex = remainingTabs.findIndex((t) => t.instanceId === targetInstanceId)
        if (targetIndex === -1) return prev
        const updated = [...remainingTabs]
        updated.splice(targetIndex, 0, ...groupTabs)
        return updated
      }

      return prev
    })
  }, [draggedGroupId])

  const handleTabDragOver = React.useCallback((e: React.DragEvent, targetInstanceId?: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"

    if (!draggedTabId || !targetInstanceId || draggedTabId === targetInstanceId) return

    setOpenApps((prev) => {
      const sourceIndex = prev.findIndex((t) => t.instanceId === draggedTabId)
      const targetIndex = prev.findIndex((t) => t.instanceId === targetInstanceId)
      if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return prev

      const sourceTab = prev[sourceIndex]
      const targetTab = prev[targetIndex]

      // 1. Se a aba arrastada pertence a um grupo:
      // SOMENTE é possível mover/reordenar se a aba de destino pertencer ao MESMO grupo.
      if (sourceTab.groupId) {
        if (sourceTab.groupId !== targetTab.groupId) {
          return prev
        }
        const updated = [...prev]
        const [movedTab] = updated.splice(sourceIndex, 1)
        updated.splice(targetIndex, 0, movedTab)
        return updated
      }

      // 2. Se a aba arrastada NÃO pertence a um grupo, ela não pode entrar no meio de nenhum grupo
      if (targetTab.groupId) {
        return prev
      }

      // 3. Reordenação normal entre abas soltas (sem grupo)
      const updated = [...prev]
      const [movedTab] = updated.splice(sourceIndex, 1)
      updated.splice(targetIndex, 0, movedTab)
      return updated
    })
  }, [draggedTabId])

  const handleTabDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDraggedTabId(null)
    setDraggedGroupId(null)
  }, [])
  const activeTab = openApps.find((tab) => tab.instanceId === activeAppId || tab.app?.id === activeAppId)
  const activeAppDbId = activeTab?.app?.id

  const applicableWidgets = React.useMemo(() => {
    return widgetsConfig.filter((w) => {
      if (!w.enabled) return false
      if (!activeAppId || activeAppId === "desk") return true
      if (w.allAppsVisible === false && w.visibleInApps) {
        return !!activeAppDbId && w.visibleInApps.includes(activeAppDbId)
      }
      return true
    })
  }, [widgetsConfig, activeAppId, activeAppDbId])

  if (loading) {
    const loadingText = lang === "en" ? "Loading" : lang === "es" ? "Cargando" : "Carregando"
    const words = lang === "en"
      ? ["applications", "widgets", "settings", "workspace", "applications"]
      : lang === "es"
        ? ["aplicaciones", "widgets", "configuraciones", "workspace", "aplicaciones"]
        : ["aplicativos", "widgets", "configurações", "workspace", "aplicativos"]

    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950 transition-colors duration-300">
        <div className="card-loader select-none">
          <div className="custom-loader">
            <p className="text-[#004EB1] font-sans font-semibold">{loadingText}</p>
            <div className="words select-none pointer-events-none">
              {words.map((word, idx) => (
                <span key={idx} className="word font-sans">
                  {word}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen w-full flex-col bg-zinc-50 dark:bg-zinc-950 overflow-hidden font-sans">
      {/* 1. TOP BAR */}
      <DesktopTopBar
        widgetsConfig={widgetsConfig}
        activeAppId={activeAppId}
        theme={activeTheme}
        setTheme={setTheme}
        topBarConfig={topBarConfig}
        currentTime={currentTime}
        currentDate={currentDate}
        weatherInfo={weatherInfo}
        lang={lang}
        setLang={(l) => setLang(l as Language)}
        t={t}
        user={user}
        currentAccount={currentAccount}
        currentWorkspace={currentWorkspace}
        setCurrentWorkspace={handleSwitchWorkspace}
        accounts={accounts}
        notifications={notifications}
        showNotificationCenter={showNotificationCenter}
        setShowNotificationCenter={setShowNotificationCenter}
        clockTab={clockTab}
        setClockTab={setClockTab}
        alarms={alarms}
        setAlarms={setAlarms}
        newAlarmTime={newAlarmTime}
        setNewAlarmTime={setNewAlarmTime}
        newAlarmLabel={newAlarmLabel}
        setNewAlarmLabel={setNewAlarmLabel}
        timerState={timerState}
        setTimerState={setTimerState}
        timerMinutes={timerMinutes}
        setTimerMinutes={setTimerMinutes}
        calendarDate={calendarDate}
        setCalendarDate={setCalendarDate}
        setIsSwitchAccountOpen={setIsSwitchAccountOpen}
        setAccountSearchQuery={setAccountSearchQuery}
        handleOpenProfileTab={handleOpenProfileTab}
        setProfileTab={setProfileTab}
        handleOpenMarketplaceTab={handleOpenMarketplaceTab}
        handleOpenSettingsTab={handleOpenSettingsTab}
        setSettingsTab={setSettingsTab}
        handleLogout={handleLogout}
        setActiveAppId={setActiveAppId}
        profileRole={profileRole}
        workspaceRole={workspaceRole}
        isWorkspaceAdmin={isCurrentWorkspaceAdmin}
        setSplitActiveAppId={setSplitActiveAppId}
      />

      {/* 2. CHROME TAB BAR */}
      <DesktopChromeTabBar
        openApps={openApps}
        activeAppId={activeAppId}
        setActiveAppId={setActiveAppId}
        splitActiveAppId={splitActiveAppId}
        setSplitActiveAppId={setSplitActiveAppId}
        focusedPane={focusedPane}
        setFocusedPane={setFocusedPane}
        tabGroups={tabGroups}
        setTabGroups={setTabGroups}
        installedApps={installedApps}
        applicableWidgets={applicableWidgets}
        activeFloatingWidgets={activeFloatingWidgets}
        handleToggleFloatingWidget={handleToggleFloatingWidget}
        handleOpenApp={handleOpenApp}
        handleAddNewTab={handleAddNewTab}
        handleCloseApp={handleCloseApp}
        handleTogglePinApp={handleTogglePinApp}
        handleDuplicateApp={handleDuplicateApp}
        handleCreateTabGroup={handleCreateTabGroup}
        handleToggleCollapseGroup={handleToggleCollapseGroup}
        handleAddTabToGroup={handleAddTabToGroup}
        handleRemoveTabFromGroup={handleRemoveTabFromGroup}
        handleRenameGroup={handleRenameGroup}
        handleChangeGroupColor={handleChangeGroupColor}
        handleUngroupAll={handleUngroupAll}
        handleCloseGroupTabs={handleCloseGroupTabs}
        handleTabDragStart={handleTabDragStart}
        handleTabDragOver={handleTabDragOver}
        handleTabDrop={handleTabDrop}
        handleGroupDragStart={handleGroupDragStart}
        handleGroupDragOver={handleGroupDragOver}
        draggedTabId={draggedTabId}
        draggedGroupId={draggedGroupId}
        editingGroupId={editingGroupId}
        setEditingGroupId={setEditingGroupId}
        isMac={isMac}
        COLOR_MAP={COLOR_MAP}
        setIframeRefreshKeys={setIframeRefreshKeys}
        currentAccount={currentAccount}
        currentWorkspace={currentWorkspace}
        setCurrentWorkspace={handleSwitchWorkspace}
        handleOpenSettingsTab={handleOpenSettingsTab}
        setSettingsTab={setSettingsTab}
      />

      {/* 3. APP VIEWPORT CONTAINER */}
      <AppViewportContainer
        activeAppId={activeAppId}
        setActiveAppId={setActiveAppId}
        splitActiveAppId={splitActiveAppId}
        setSplitActiveAppId={setSplitActiveAppId}
        focusedPane={focusedPane}
        setFocusedPane={setFocusedPane}
        splitOrientation={splitOrientation}
        setSplitOrientation={setSplitOrientation}
        splitProportion={splitProportion}
        setSplitProportion={setSplitProportion}
        openApps={openApps}
        setOpenApps={setOpenApps}
        installedApps={installedApps}
        iframeRefreshKeys={iframeRefreshKeys}
        activeFloatingWidgets={activeFloatingWidgets}
        pinnedWidgets={pinnedWidgets}
        setPinnedWidgets={setPinnedWidgets}
        widgetsConfig={widgetsConfig}
        minimizedWidgets={minimizedWidgets}
        calcFocusedWidgetId={calcFocusedWidgetId}
        setCalcFocusedWidgetId={setCalcFocusedWidgetId}
        handleMinimizeWidget={handleMinimizeWidget}
        handlePinWidget={handlePinWidget}
        handleToggleFloatingWidget={handleToggleFloatingWidget}
        calcFormula={calcFormula}
        setCalcFormula={setCalcFormula}
        calcDisplay={calcDisplay}
        setCalcDisplay={setCalcDisplay}
        showCalcHistory={showCalcHistory}
        setShowCalcHistory={setShowCalcHistory}
        calcHistory={calcHistory}
        setCalcHistory={setCalcHistory}
        setCalcResetOnNext={setCalcResetOnNext}
        handleCalcPress={handleCalcPress}
        calendarDate={calendarDate}
        setCalendarDate={setCalendarDate}
        notesContent={notesContent}
        setNotesContent={setNotesContent}
        handleCloseApp={handleCloseApp}
        isMobile={isMobile}
        settingsTab={settingsTab}
        setSettingsTab={setSettingsTab}
        t={t}
        profileRole={profileRole}
        workspaceRole={workspaceRole}
        userWorkspaceMemberships={userWorkspaceMemberships}
        adminWorkspaces={adminWorkspaces}
        currentAccount={currentAccount}
        handleSaveCompany={handleSaveCompany}
        companyName={companyName}
        setCompanyName={setCompanyName}
        companyDocument={companyDocument}
        setCompanyDocument={setCompanyDocument}
        companyLogoUrl={companyLogoUrl || ""}
        setCompanyLogoUrl={setCompanyLogoUrl}
        companyBannerUrl={companyBannerUrl || ""}
        setCompanyBannerUrl={setCompanyBannerUrl}
        companyPrimaryColor={companyPrimaryColor}
        setCompanyPrimaryColor={setCompanyPrimaryColor}
        companyCustomDomain={companyCustomDomain}
        setCompanyCustomDomain={setCompanyCustomDomain}
        companySamlEnabled={companySamlEnabled}
        setCompanySamlEnabled={setCompanySamlEnabled}
        companySamlIdpEntityId={companySamlIdpEntityId}
        setCompanySamlIdpEntityId={setCompanySamlIdpEntityId}
        companySamlSsoUrl={companySamlSsoUrl}
        setCompanySamlSsoUrl={setCompanySamlSsoUrl}
        companySamlCertificate={companySamlCertificate}
        setCompanySamlCertificate={setCompanySamlCertificate}
        accounts={accounts}
        handleSwitchWorkspace={handleSwitchWorkspace}
        handleCreateWorkspace={handleCreateWorkspace}
        handleUpdateWorkspace={handleUpdateWorkspace}
        handleArchiveWorkspace={handleArchiveWorkspace}
        handleReactivateWorkspace={handleReactivateWorkspace}
        handleDeleteWorkspace={handleDeleteWorkspace}
        usersList={getAccountUsersList()}
        handleInviteUser={handleInviteUser}
        handleUpdateAccountMember={handleUpdateAccountMember}
        handleRemoveUser={handleRemoveUser}
        wallpaperTab={wallpaperTab}
        setWallpaperTab={setWallpaperTab}
        mobileWallpaper={mobileWallpaper}
        setMobileWallpaper={setMobileWallpaper}
        desktopWallpaper={desktopWallpaper}
        setDesktopWallpaper={setDesktopWallpaper}
        customWallpapers={customWallpapers}
        setCustomWallpapers={setCustomWallpapers}
        useSeparateMobileWallpaper={useSeparateMobileWallpaper}
        setUseSeparateMobileWallpaper={setUseSeparateMobileWallpaper}
        handleSaveWallpaperToDb={handleSaveWallpaperToDb}
        desktopGridCols={desktopGridCols}
        setDesktopGridCols={setDesktopGridCols}
        desktopIconSize={desktopIconSize}
        setDesktopIconSize={setDesktopIconSize}
        desktopShowLabels={desktopShowLabels}
        setDesktopShowLabels={setDesktopShowLabels}
        dockPosition={dockPosition}
        setDockPosition={setDockPosition}
        dockSize={dockSize}
        setDockSize={setDockSize}
        dockAutoHide={dockAutoHide}
        setDockAutoHide={setDockAutoHide}
        topBarConfig={topBarConfig}
        setTopBarConfig={setTopBarConfig}
        handleToggleAppStatus={handleToggleAppStatus}
        handleUninstallApp={handleUninstallApp}
        handleRenameApp={handleRenameApp}
        availableWidgets={widgetsConfig}
        setWidgetsConfig={setWidgetsConfig}
        handleToggleWidgetPin={handleToggleWidgetPin}
        marketplaceApps={marketplaceApps}
        globallyInstalledApps={globallyInstalledApps}
        developerApps={developerApps}
        onAddCustomApp={handleAddCustomApp}
        setAuthAppToShow={setAuthAppToShow}
        handleToggleGlobalApp={handleToggleGlobalApp}
        handleConfigureAppWorkspaces={handleConfigureAppWorkspaces}
        currentWorkspace={currentWorkspace}
        handleToggleGlobalWidget={handleToggleGlobalWidget}
        apiKey={apiKey}
        handleGenerateApiKey={handleGenerateApiKey}
        webhookUrl={webhookUrl}
        setWebhookUrl={setWebhookUrl}
        handleSaveWebhook={handleSaveWebhook}
        handleOpenMarketplaceTab={handleOpenMarketplaceTab}
        handleOpenApp={handleOpenApp}
        handleOpenExternalUrl={handleOpenExternalUrl}
        dockAppIds={dockAppIds}
        setDockAppIds={setDockAppIds}
        hiddenAppIds={hiddenAppIds}
        setHiddenAppIds={setHiddenAppIds}
        devSubTab={devSubTab}
        setDevSubTab={setDevSubTab}
        lang={lang}
        theme={activeTheme}
        user={user}
        profileTab={profileTab}
        setProfileTab={setProfileTab}
        profileName={profileName}
        setProfileName={setProfileName}
        profilePhone={profilePhone}
        setProfilePhone={setProfilePhone}
        profileAvatarUrl={profileAvatarUrl}
        setProfileAvatarUrl={setProfileAvatarUrl}
        handleSaveProfile={handleSaveProfile}
        isLoginMusicEnabled={isLoginMusicEnabled}
        setIsLoginMusicEnabled={setIsLoginMusicEnabled}
        loginMusicUrl={loginMusicUrl}
        setLoginMusicUrl={setLoginMusicUrl}
        fullSizeScreen={fullSizeScreen}
        setFullSizeScreen={setFullSizeScreen}
        currentPassword={currentPassword}
        setCurrentPassword={setCurrentPassword}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        isChangingPassword={isChangingPassword}
        handleSavePassword={handleSavePassword}
        totpDisableCode={totpDisableCode}
        setTotpDisableCode={setTotpDisableCode}
        handleDisable2FA={handleDisable2FA}
        handleEnable2FA={handleEnable2FA}
        setupData={setupData}
        totpError={totpError}
        totpCode={totpCode}
        setTotpCode={setTotpCode}
        lockscreenPin={lockscreenPin}
        setLockscreenPin={setLockscreenPin}
        weatherInfo={weatherInfo}
        pinnedWidgetSides={pinnedWidgetSides}
        setPinnedWidgetSides={setPinnedWidgetSides}
        collapsedSides={collapsedSides}
        setCollapsedSides={setCollapsedSides}
        sideWidths={sideWidths}
        setSideWidths={setSideWidths}
        topPanelHeight={topPanelHeight}
        setTopPanelHeight={setTopPanelHeight}
        showToast={showToast}
        updateAppSubpath={updateAppSubpath}
      />

      {/* 4. HOME GRID */}
      {activeAppId === null && (
        <DesktopAppGrid
          isMobile={isMobile}
          mobileWallpaperStyle={mobileWallpaperStyle}
          desktopWallpaperStyle={desktopWallpaperStyle}
          openApps={openApps}
          notifications={notifications}
          currentTime={currentTime}
          weatherInfo={weatherInfo}
          mobileSearch={mobileSearch}
          setMobileSearch={setMobileSearch}
          editMode={editMode}
          setEditMode={setEditMode}
          showToast={showToast}
          setShowWidgetLibraryModal={setShowWidgetLibraryModal}
          installedApps={installedApps}
          finalMobileHomeOrder={finalMobileHomeOrder}
          handleHomeDragStart={handleHomeDragStart}
          handleHomeDragOver={handleHomeDragOver}
          handleHomeDrop={handleHomeDrop}
          handleOpenWidgetAsApp={handleOpenWidgetAsApp}
          widgetsConfig={widgetsConfig}
          setWidgetsConfig={setWidgetsConfig}
          moveHomeItem={moveHomeItem}
          notesContent={notesContent}
          setNotesContent={setNotesContent}
          calcFormula={calcFormula}
          calcDisplay={calcDisplay}
          handleCalcPress={handleCalcPress}
          handleOpenApp={handleOpenApp}
          setHiddenAppIds={setHiddenAppIds}
          hiddenAppIds={hiddenAppIds}
          renamedAppNames={renamedAppNames}
          theme={activeTheme}
          setShowMobileNotifications={setShowMobileNotifications}
          setShowMobileControlCenter={setShowMobileControlCenter}
          setShowMobileAppSwitcher={setShowMobileAppSwitcher}
          handleDesktopContextMenu={handleDesktopContextMenu}
          handleDesktopDrop={handleDesktopDrop}
          setContextMenu={setContextMenu}
          windowSize={windowSize}
          getCellSize={getCellSize}
          getDesktopIconConfig={getDesktopIconConfig}
          appPositions={appPositions}
          draggedAppId={draggedAppId}
          handleAppIconDragStart={handleAppIconDragStart}
          setOpenApps={setOpenApps}
          setSplitActiveAppId={setSplitActiveAppId}
          setFocusedPane={setFocusedPane}
          handleRenameAppClick={(appId, currentName) => {
            setRenamingAppId(appId)
            setRenamingAppInput(currentName || "")
          }}
          calendarDate={calendarDate}
          setCalendarDate={setCalendarDate}
          pinnedWidgets={pinnedWidgets}
          handlePinWidget={handlePinWidget}
          pinnedWidgetSides={pinnedWidgetSides}
          setPinnedWidgetSides={setPinnedWidgetSides}
          collapsedSides={collapsedSides}
          setCollapsedSides={setCollapsedSides}
          sideWidths={sideWidths}
          setSideWidths={setSideWidths}
          topPanelHeight={topPanelHeight}
          setTopPanelHeight={setTopPanelHeight}
          minimizedWidgets={minimizedWidgets}
          calcFocusedWidgetId={calcFocusedWidgetId}
          setCalcFocusedWidgetId={setCalcFocusedWidgetId}
          handleMinimizeWidget={handleMinimizeWidget}
          handleToggleFloatingWidget={handleToggleFloatingWidget}
          showCalcHistory={showCalcHistory}
          setShowCalcHistory={setShowCalcHistory}
          calcHistory={calcHistory}
          setCalcHistory={setCalcHistory}
          setCalcResetOnNext={setCalcResetOnNext}
          setCalcDisplay={setCalcDisplay}
          setCalcFormula={setCalcFormula}
          setPinnedWidgets={setPinnedWidgets}
          mobileDockAppIds={mobileDockAppIds}
          setMobileDockAppIds={setMobileDockAppIds}
          desktopGridCols={desktopGridCols}
          desktopIconSize={desktopIconSize}
          desktopShowLabels={desktopShowLabels}
          topBarConfig={topBarConfig}
          currentAccount={currentAccount}
          currentWorkspace={currentWorkspace}
          handleSwitchWorkspace={handleSwitchWorkspace}
          setCurrentWorkspace={handleSwitchWorkspace}
        />
      )}

      {/* 5. WORKSPACE OVERLAYS */}
      <WorkspaceOverlays
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        showWallpaperModal={showWallpaperModal}
        setShowWallpaperModal={setShowWallpaperModal}
        showAddAppModal={showAddAppModal}
        setShowAddAppModal={setShowAddAppModal}
        showAddWidgetToDesktopModal={showAddWidgetToDesktopModal}
        setShowAddWidgetToDesktopModal={setShowAddWidgetToDesktopModal}
        desktopIconSize={desktopIconSize}
        setDesktopIconSize={setDesktopIconSize}
        user={user}
        desktopWallpaper={desktopWallpaper}
        setDesktopWallpaper={setDesktopWallpaper}
        mobileWallpaper={mobileWallpaper}
        setMobileWallpaper={setMobileWallpaper}
        useSeparateMobileWallpaper={useSeparateMobileWallpaper}
        setUseSeparateMobileWallpaper={setUseSeparateMobileWallpaper}
        wallpaperTab={wallpaperTab}
        setWallpaperTab={setWallpaperTab}
        customWallpapers={customWallpapers}
        setCustomWallpapers={setCustomWallpapers}
        handleSaveWallpaperToDb={handleSaveWallpaperToDb}
        installedApps={installedApps}
        hiddenAppIds={hiddenAppIds}
        setHiddenAppIds={setHiddenAppIds}
        showWidgetLibraryModal={showWidgetLibraryModal}
        setShowWidgetLibraryModal={setShowWidgetLibraryModal}
        widgetsConfig={widgetsConfig}
        setWidgetsConfig={setWidgetsConfig}
        renamingAppId={renamingAppId}
        setRenamingAppId={setRenamingAppId}
        renamingAppInput={renamingAppInput}
        setRenamingAppInput={setRenamingAppInput}
        handleSaveRenameApp={handleSaveRenameApp}
        showMobileControlCenter={showMobileControlCenter}
        setShowMobileControlCenter={setShowMobileControlCenter}
        accounts={accounts}
        currentAccount={currentAccount}
        lang={lang}
        setLang={setLang}
        theme={activeTheme}
        setTheme={setTheme}
        notifications={notifications}
        setNotifications={setNotifications}
        handleLogout={handleLogout}
        handleSwitchAccount={handleSwitchAccount}
        handleOpenProfileTab={handleOpenProfileTab}
        setProfileTab={setProfileTab}
        handleOpenSettingsTab={handleOpenSettingsTab}
        setShowMobileNotifications={setShowMobileNotifications}
        showMobileNotifications={showMobileNotifications}
        showMobileAppSwitcher={showMobileAppSwitcher}
        setShowMobileAppSwitcher={setShowMobileAppSwitcher}
        openApps={openApps}
        setActiveAppId={setActiveAppId}
        handleCloseApp={handleCloseApp}
        handleCloseAllApps={handleCloseAllApps}
        switcherOffsets={switcherOffsets}
        handleSwitcherTouchStart={handleSwitcherTouchStart}
        handleSwitcherTouchMove={handleSwitcherTouchMove}
        handleSwitcherTouchEnd={handleSwitcherTouchEnd}
        revokingApiKeyId={revokingApiKeyId}
        setRevokingApiKeyId={setRevokingApiKeyId}
        handleRevokeApiKey={handleRevokeApiKey}
        revokingAppId={revokingAppId}
        setRevokingAppId={setRevokingAppId}
        handleRevokeApp={handleRevokeApp}
        showNotificationCenter={showNotificationCenter}
        setShowNotificationCenter={setShowNotificationCenter}
        handleGenerateRealNotification={handleGenerateRealNotification}
        activeFloatingWidgets={activeFloatingWidgets}
        floatingPositions={floatingPositions}
        floatingSizes={floatingSizes}
        setFloatingPositions={setFloatingPositions}
        setFloatingSizes={setFloatingSizes}
        minimizedWidgets={minimizedWidgets}
        handleFocusFloatingWidget={handleFocusFloatingWidget}
        handleDragWidget={handleDragWidget}
        handleMinimizeWidget={handleMinimizeWidget}
        handlePinWidget={handlePinWidget}
        handleToggleFloatingWidget={handleToggleFloatingWidget}
        calcFocusedWidgetId={calcFocusedWidgetId}
        setCalcFocusedWidgetId={setCalcFocusedWidgetId}
        calcFormula={calcFormula}
        setCalcFormula={setCalcFormula}
        calcDisplay={calcDisplay}
        setCalcDisplay={setCalcDisplay}
        showCalcHistory={showCalcHistory}
        setShowCalcHistory={setShowCalcHistory}
        calcHistory={calcHistory}
        setCalcHistory={setCalcHistory}
        setCalcResetOnNext={setCalcResetOnNext}
        handleCalcPress={handleCalcPress}
        calendarDate={calendarDate}
        setCalendarDate={setCalendarDate}
        notesContent={notesContent}
        setNotesContent={setNotesContent}
        activeAppId={activeAppId}
        isDockVisible={isDockVisible}
        setIsDockVisible={setIsDockVisible}
        dockPosition={dockPosition}
        dockOrientation={dockOrientation}
        dockAlignment={dockAlignment}
        dockNoMargin={dockNoMargin}
        dockSize={dockSize}
        setDockSize={setDockSize}
        dockAppIds={dockAppIds}
        setDockAppIds={setDockAppIds}
        dockWidgetIds={dockWidgetIds}
        handleDockAppClick={handleDockAppClick}
        handleOpenMarketplaceTab={handleOpenMarketplaceTab}
        setSettingsTab={setSettingsTab}
        setSplitActiveAppId={setSplitActiveAppId}
        handleDockDragStart={handleDockDragStart}
        isSwitchAccountOpen={isSwitchAccountOpen}
        setIsSwitchAccountOpen={setIsSwitchAccountOpen}
        t={t}
        handleMobileBack={handleMobileBack}
        authAppToShow={authAppToShow}
        setAuthAppToShow={setAuthAppToShow}
        handleToggleGlobalApp={handleToggleGlobalApp}
        marketplaceApps={marketplaceApps}
        globallyInstalledApps={globallyInstalledApps}
        setGloballyInstalledApps={setGloballyInstalledApps}
        hasCheckedTermsCheckbox={hasCheckedTermsCheckbox}
        setHasCheckedTermsCheckbox={setHasCheckedTermsCheckbox}
        isAcceptingTerms={isAcceptingTerms}
        handleAcceptTerms={handleAcceptTerms}
        pinnedWidgets={pinnedWidgets}
        pinnedWidgetSides={pinnedWidgetSides}
        setPinnedWidgetSides={setPinnedWidgetSides}
        collapsedSides={collapsedSides}
        setCollapsedSides={setCollapsedSides}
        sideWidths={sideWidths}
        setSideWidths={setSideWidths}
        topPanelHeight={topPanelHeight}
        setTopPanelHeight={setTopPanelHeight}
        weatherInfo={weatherInfo}
        showToast={showToast}
      />

      {/* MENU DE CONTEXTO DE LINKS (BOTÃO DIREITO) */}
      {linkContextMenu && (
        <div
          id="bold-link-context-menu"
          className="fixed z-[99999] w-64 rounded-xl border border-zinc-200/80 dark:border-zinc-800 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl shadow-2xl p-1.5 text-xs select-none animate-in fade-in zoom-in-95 duration-100"
          style={{
            left: typeof window !== "undefined" ? Math.min(Math.max(8, linkContextMenu.x), window.innerWidth - 268) : linkContextMenu.x,
            top: typeof window !== "undefined" ? Math.min(Math.max(8, linkContextMenu.y), window.innerHeight - 190) : linkContextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          {linkContextMenu.text && (
            <div className="px-2.5 py-1.5 border-b border-zinc-100 dark:border-zinc-800/80 mb-1">
              <p className="text-[11px] font-semibold text-zinc-800 dark:text-zinc-200 truncate">
                {linkContextMenu.text}
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              const { url, sourceBaseUrl } = linkContextMenu
              setLinkContextMenu(null)
              handleOpenExternalUrl(url, { forceNewTab: true, sourceBaseUrl })
            }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left font-medium text-zinc-700 dark:text-zinc-200 hover:bg-[#004EB1]/10 hover:text-[#004EB1] dark:hover:bg-blue-500/15 dark:hover:text-blue-400 transition-colors cursor-pointer"
          >
            <span className="size-4 flex items-center justify-center text-sm leading-none">＋</span>
            <span>Abrir link em uma nova guia</span>
          </button>
          <button
            type="button"
            onClick={() => {
              const { url, sourceBaseUrl } = linkContextMenu
              setLinkContextMenu(null)
              handleOpenExternalUrl(url, { forceNewTab: true, inBackground: true, sourceBaseUrl })
            }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <span className="size-4 flex items-center justify-center text-xs leading-none">⧉</span>
            <span>Abrir link em guia em 2º plano</span>
          </button>
          <button
            type="button"
            onClick={() => {
              const { url, sourceBaseUrl } = linkContextMenu
              setLinkContextMenu(null)
              handleOpenExternalUrl(url, { forceNewTab: true, openInSplit: true, sourceBaseUrl })
            }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <span className="size-4 flex items-center justify-center text-xs leading-none">◫</span>
            <span>Abrir link em tela dividida</span>
          </button>
          <div className="my-1 border-t border-zinc-100 dark:border-zinc-800/80" />
          <button
            type="button"
            onClick={() => {
              const { url } = linkContextMenu
              setLinkContextMenu(null)
              try {
                navigator.clipboard.writeText(url)
                showToast("Endereço do link copiado!", "success")
              } catch {}
            }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <span className="size-4 flex items-center justify-center text-xs leading-none">📋</span>
            <span>Copiar endereço do link</span>
          </button>
        </div>
      )}

      {/* MODAL DE SUGESTÃO DE TELA CHEIA */}
      <FullscreenPromptModal
        isOpen={showFullscreenPrompt}
        onClose={() => setShowFullscreenPrompt(false)}
        onEnableFullscreen={handleEnableFullscreen}
        user={user}
        onUpdateFullSizeScreen={setFullSizeScreen}
      />
    </div>
  )
}
